import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { RLP_ATTRIBUTION } from "./lib/attribution.js";
import { branding } from "@nrl/shared/branding";
import { guidelines, BADGE_DEFS, foreverProgress } from "@nrl/shared";
import { supabase } from "./lib/supabase";
import { signIn, signUp, signOut, fetchProfile, defaultGameState, requestPasswordReset, updatePassword, friendlyAuthError } from "./lib/auth";
import { fetchMyBadges, EMPTY_BADGES } from "./lib/badges";
import { fetchLeaderboard, setSupportedTeam, fetchMyProgress, leaderboardErrorCopy, adminSetMemberTeam } from "./lib/leaderboard";
import { fetchXpLedgerSummary } from "./lib/xpLedger";
import { fetchMyXp, deriveXpSections } from "./lib/xpLedger";
import { fetchMyStats, EMPTY_MY_STATS } from "./lib/stats";
import {
  fetchConfirmationQueue,
  prefillMatch,
  prefillToDraft,
  draftToConfirmation,
  saveMatchConfirmation,
  releaseRound,
  adminAppClock,
  adminLocationStandins,
  adminSetLocationStandin,
  searchOriginPlayers,
  fetchOriginConfirmation,
} from "./lib/adminApi";
import { TeamMatchup, TeamName } from "./lib/teamMarks";
import { originLabel, originTag } from "./lib/originLabel";
import {
  fetchMyCheckIns,
  fetchUpcomingFixtures,
  fixtureWindowState,
  kickoffVenueLabel,
  kickoffViewerLabel,
  matchDateLabel,
} from "./lib/fixtures";
import { performCheckIn } from "./lib/checkinApi";
import { syncAppClock, appNow, appNowMs, getAppClock, onAppClock, resetAppClockForAccount, getClockGeneration, isAppClockReady } from "./lib/appClock";
import { claimStandardPack, openGrantedPack } from "./lib/packApi";
import { fetchMyCollection, fetchMyGrants, fetchNewestStandardGrant, importBridge, mergePackDraws, nextStandardPackAt, packDuplicateLabel } from "./lib/collection";
import { fetchTradeBoard, postListing, cancelListing, postCounter, withdrawCounter, declineCounter, acceptCounter, itemsFromCompositeIds, compositeIdsFromItems, offerValue, tradeErrorCopy } from "./lib/tradesApi";
import { getMyForever, submitForeverSet, fetchCardTeams, foreverErrorCopy, newForeverRequestId } from "./lib/forever";
import { playChime, primeAudio } from "./lib/sounds";
import { fetchRegistrationStatus, registerInterest, adminSetRegistration, adminInterestRegistrations } from "./lib/registrationApi";

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const T = {
  bg: "#f7f9f6",
  surface: "#ffffff",
  surfaceHigh: "#eef2ec",
  surfaceMid: "#f2f5f0",
  border: "rgba(0,0,0,0.08)",
  borderGreen: "rgba(65,171,128,0.35)",
  text: "#0c130c",
  textMuted: "rgba(12,19,12,0.55)",
  textDim: "rgba(12,19,12,0.32)",
  textSubtle: "rgba(12,19,12,0.66)",
  accent: "#41ab80",
  accentDark: "#348966",
  accentDim: "rgba(65,171,128,0.12)",
  purple: "#7c5cfc",
  purpleLight: "#7c5cfc",
  purpleDim: "rgba(124,92,252,0.12)",
  green: "#41ab80",
  greenDim: "rgba(65,171,128,0.12)",
  greenBright: "#348966",
  blue: "#2f7fd8",
  blueDim: "rgba(47,127,216,0.12)",
  gold: "#b8860b",
  goldDim: "rgba(184,134,11,0.12)",
  red: "#dc3030",
  redDim: "rgba(220,48,48,0.1)",
  radius: 16,
  radiusSm: 10,
  radiusLg: 22,
};

const RARITY = {
  common:   { label: "Common",   color: "#9ca3af", glow: "rgba(156,163,175,0.3)", pts: 2,   emoji: "⚪", gradient: "linear-gradient(135deg,#374151,#1f2937)" },
  silver:   { label: "Silver",   color: "#d1d5db", glow: "rgba(209,213,219,0.4)", pts: 16,  emoji: "🥈", gradient: "linear-gradient(135deg,#6b7280,#374151)" },
  gold:     { label: "Gold",     color: "#f5b731", glow: "rgba(245,183,49,0.5)",  pts: 32,  emoji: "🥇", gradient: "linear-gradient(135deg,#d97706,#92400e)" },
  platinum: { label: "Platinum", color: "#a5f3fc", glow: "rgba(165,243,252,0.5)", pts: 64,  emoji: "💎", gradient: "linear-gradient(135deg,#0891b2,#1e3a5f)" },
  ultimate: { label: "Ultimate", color: "#f75f5f", glow: "rgba(247,95,95,0.6)",   pts: 128, emoji: "👑", gradient: "linear-gradient(135deg,#dc2626,#7c3aed)" },
};

// ─── NRL DATA ─────────────────────────────────────────────────────────────────
// ─── VENUE COORDINATES (for location-verified check-in) ───────────────────────
// Centroid coordinates verified via Google Places. Used with a 200m radius check.
const VENUE_COORDS = {
  "AAMI Park":                          { lat:-37.824428,  lng:144.984410 },
  "Accor Stadium":                      { lat:-33.847116,  lng:151.063414 },
  "Allegiant Stadium, Las Vegas":       { lat:36.090867,   lng:-115.183322 },
  "Allianz Stadium":                    { lat:-33.889067,  lng:151.225364 },
  "BlueBet Stadium":                    { lat:-33.758944,  lng:150.686867 },
  "Campbelltown Sports Stadium":        { lat:-34.050390,  lng:150.833767 },
  "Cbus Super Stadium":                 { lat:-28.066797,  lng:153.378796 },
  "CommBank Stadium":                   { lat:-33.808669,  lng:151.000132 },
  "GIO Stadium":                        { lat:-35.250019,  lng:149.102676 },
  "Glen Willow, Mudgee":                { lat:-32.582122,  lng:149.584217 },
  "Go Media Stadium":                   { lat:-36.918283,  lng:174.812396 },
  "HBF Park, Perth":                    { lat:-31.945777,  lng:115.869861 },
  "Kayo Stadium":                       { lat:-27.218829,  lng:153.097230 },
  "Leichhardt Oval":                    { lat:-33.869253,  lng:151.154250 },
  "McDonald Jones Stadium":             { lat:-32.918605,  lng:151.726126 },
  "Melbourne Cricket Ground":           { lat:-37.819967,  lng:144.983449 },
  "Netstrata Jubilee Stadium":          { lat:-33.971726,  lng:151.128499 },
  "One NZ Stadium, Christchurch":       { lat:-43.533776,  lng:172.643953 },
  "Optus Stadium, Perth":               { lat:-31.950749,  lng:115.888799 },
  "PointsBet Stadium":                  { lat:-34.038472,  lng:151.140742 },
  "Queensland Country Bank Stadium":    { lat:-19.266081,  lng:146.816540 },
  "SKY Stadium, Wellington":            { lat:-41.273049,  lng:174.785898 },
  "Suncorp Stadium":                    { lat:-27.464799,  lng:153.009611 },
  "TIO Stadium, Darwin":                { lat:-12.399194,  lng:130.887312 },
  "WIN Stadium":                        { lat:-34.428529,  lng:150.902547 },
  "4 Pines Park":                       { lat:-33.760875,  lng:151.273463 },
};

const CHECKIN_RADIUS_METERS = 200;

// RUL-1: check-in window derives from the canonical rules fixture
// (packages/shared/src/guidelines-values.json, checkinWindow), not prototype
// copy. Display only — server enforcement is a sibling task.
// FULLTIME_OFFSET_MIN = estimated kickoff-to-fulltime minutes (client const).
const CHECKIN_PRE_MIN = guidelines.checkinWindow.preKickoffMinutes;
const CHECKIN_POST_MIN = guidelines.checkinWindow.postFullTimeMinutes;
const FULLTIME_OFFSET_MIN = 100;
const CHECKIN_WINDOW_CLOSE_MIN = FULLTIME_OFFSET_MIN + CHECKIN_POST_MIN;

// 60 -> "1 hour", 90 -> "1 hour 30 minutes", 120 -> "2 hours", 30 -> "30 minutes".
function formatDurationMinutes(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  const parts = [];
  if (h > 0) parts.push(h === 1 ? "1 hour" : `${h} hours`);
  if (m > 0) parts.push(m === 1 ? "1 minute" : `${m} minutes`);
  return parts.join(" ");
}

// Haversine formula — distance in meters between two lat/lng points
function distanceMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const toRad = d => d * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat/2)**2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

const NRL_TEAMS = {
  // cardPlayers = 10 most popular players used for the card system (separate).
  // Round 12 removed the App-local `squad` arrays — match players now come from
  // the server line-up (get_my_stats) and the admin squads screen (server-side).
  "Brisbane Broncos":              { logo:"🟡", state:"QLD",
    cardPlayers:["Reece Walsh","Payne Haas","Adam Reynolds","Selwyn Cobbo","Ezra Mam","Kotoni Staggs","Pat Carrigan","Corey Oates","Jordan Riki","Herbie Farnworth"] },
  "Canberra Raiders":              { logo:"🟢", state:"ACT",
    cardPlayers:["Jack Wighton","Josh Papalii","Joseph Tapine","Elliott Whitehead","Jarrod Croker","Tom Starling","Hudson Young","Xavier Savage","Nick Cotric","Corey Horsburgh"] },
  "Canterbury-Bankstown Bulldogs": { logo:"🔵", state:"NSW",
    cardPlayers:["Stephen Crichton","Josh Addo-Carr","Matt Burton","Viliame Kikau","Reed Mahoney","Tevita Pangai Jr","Paul Vaughan","Bronson Xerri","Jake Averillo","Sitili Tupouniua"] },
  "Cronulla-Sutherland Sharks":    { logo:"🔷", state:"NSW",
    cardPlayers:["Nicho Hynes","William Kennedy","Siosaia Talakai","Cameron McInnes","Jesse Ramien","Briton Nikora","Braydon Trindall","Toby Rudolf","Royce Hunt","Blayke Brailey"] },
  "Gold Coast Titans":             { logo:"🌊", state:"QLD",
    cardPlayers:["Tino Fa'asuamaleaui","David Fifita","AJ Brimson","Jaydn Su'A","Kieran Foran","Moeaki Fotuaika","Greg Marzhew","Tanah Boyd","Kevin Proctor","Alofiana Khan-Pereira"] },
  "Manly-Warringah Sea Eagles":    { logo:"🦅", state:"NSW",
    cardPlayers:["Tom Trbojevic","Daly Cherry-Evans","Jake Trbojevic","Jason Saab","Reuben Garrick","Haumole Olakau'atu","Luke Brooks","Christian Tuipulotu","Martin Taupau","Josh Schuster"] },
  "Melbourne Storm":               { logo:"⚡", state:"VIC",
    cardPlayers:["Cameron Munster","Ryan Papenhuyzen","Harry Grant","Jahrome Hughes","Nelson Asofa-Solomona","Christian Welch","Justin Olam","Felise Kaufusi","Tui Kamikamica","Reimis Smith"] },
  "Newcastle Knights":             { logo:"⚔️", state:"NSW",
    cardPlayers:["Kalyn Ponga","Tyson Frizell","David Klemmer","Dane Gagai","Jayden Brailey","Daniel Saifiti","Bradman Best","Phoenix Crossland","Adam Elliott","Dominic Young"] },
  "North Queensland Cowboys":      { logo:"🤠", state:"QLD",
    cardPlayers:["Jason Taumalolo","Scott Drinkwater","Tom Dearden","Reuben Cotter","Jeremiah Nanai","Murray Taulagi","Hamiso Tabuai-Fidow","Kyle Feldt","Jake Clifford","Coen Hess"] },
  "Parramatta Eels":               { logo:"🟠", state:"NSW",
    cardPlayers:["Mitchell Moses","Clint Gutherson","Dylan Brown","Reagan Campbell-Gillard","Isaiah Papali'i","Junior Paulo","Ryan Matterson","Bailey Simonsson","Marata Niukore","Jake Arthur"] },
  "Penrith Panthers":              { logo:"🐾", state:"NSW",
    cardPlayers:["Nathan Cleary","Isaah Yeo","Jarome Luai","James Fisher-Harris","Brian To'o","Dylan Edwards","Liam Martin","Spencer Leniu","Scott Sorensen","Moses Leota"] },
  "South Sydney Rabbitohs":        { logo:"🐰", state:"NSW",
    cardPlayers:["Latrell Mitchell","Cody Walker","Cameron Murray","Damien Cook","Alex Johnston","Jai Arrow","Thomas Burgess","Taane Milne","Keaon Koloamatangi","Lachlan Ilias"] },
  "St. George Illawarra Dragons":  { logo:"🐉", state:"NSW",
    cardPlayers:["Ben Hunt","Zac Lomax","Tariq Sims","Tyrell Sloan","Moses Suli","Kyle Flanagan","Mikaele Ravalawa","Jack Bird","Blake Lawrie","Jayden Sullivan"] },
  "Sydney Roosters":               { logo:"🐓", state:"NSW",
    cardPlayers:["James Tedesco","Joseph Manu","Luke Keary","Jared Waerea-Hargreaves","Victor Radley","Angus Crichton","Sam Walker","Daniel Tupou","Brandon Smith","Nat Butcher"] },
  "Wests Tigers":                  { logo:"🐯", state:"NSW",
    cardPlayers:["Api Koroisau","Lachlan Galvin","Daine Laurie","Stefano Utoikamanu","Adam Doueihi","David Nofoaluma","Charlie Staines","Brent Naden","Starford To'a","John Bateman"] },
  "New Zealand Warriors":          { logo:"🗡️", state:"NZL",
    cardPlayers:["Shaun Johnson","Tohu Harris","Roger Tuivasa-Sheck","Jazz Tevaga","Dallin Watene-Zelezniak","Addin Fonoti-Fifita","Te Maire Martin","Kane Evans","Marcelo Montoya","Wayde Egan"] },
  "Dolphins":                      { logo:"🐬", state:"QLD",
    cardPlayers:["Kodi Nikorima","Jesse Bromwich","Kenny Bromwich","Tom Gilbert","Tesi Niu","Herbie Farnworth","Jake Averillo","Hamiso Tabuai-Fidow","Brenko Lee","Jamayne Taunoa-Brown"] },
};

const TEAM_NAMES = Object.keys(NRL_TEAMS);

const CARD_DEFINITIONS = [
  { num:1, name:"BRISBANE BRONCOS TEAM CARD", team:"Brisbane Broncos", isTeamCard:true },
  { num:2, name:"Xavier Willison", team:"Brisbane Broncos", isTeamCard:false },
  { num:3, name:"Ezra Mam", team:"Brisbane Broncos", isTeamCard:false },
  { num:4, name:"Ben Talty", team:"Brisbane Broncos", isTeamCard:false },
  { num:5, name:"Cory Paix", team:"Brisbane Broncos", isTeamCard:false },
  { num:6, name:"Josiah Karapani", team:"Brisbane Broncos", isTeamCard:false },
  { num:7, name:"Jordan Riki", team:"Brisbane Broncos", isTeamCard:false },
  { num:8, name:"Adam Reynolds", team:"Brisbane Broncos", isTeamCard:false },
  { num:9, name:"Kotoni Staggs", team:"Brisbane Broncos", isTeamCard:false },
  { num:10, name:"Gehamat Shibasaki", team:"Brisbane Broncos", isTeamCard:false },
  { num:11, name:"Ben Hunt", team:"Brisbane Broncos", isTeamCard:false },
  { num:12, name:"Patrick Carrigan", team:"Brisbane Broncos", isTeamCard:false },
  { num:13, name:"Reece Walsh", team:"Brisbane Broncos", isTeamCard:false },
  { num:14, name:"Jack Gosiewski", team:"Brisbane Broncos", isTeamCard:false },
  { num:15, name:"Deine Mariner", team:"Brisbane Broncos", isTeamCard:false },
  { num:16, name:"Payne Haas", team:"Brisbane Broncos", isTeamCard:false },
  { num:17, name:"Jesse Arthars", team:"Brisbane Broncos", isTeamCard:false },
  { num:18, name:"Brendan Piakura", team:"Brisbane Broncos", isTeamCard:false },
  { num:19, name:"CANBERRA RAIDERS TEAM CARD", team:"Canberra Raiders", isTeamCard:true },
  { num:20, name:"Joseph Tapine", team:"Canberra Raiders", isTeamCard:false },
  { num:21, name:"Tom Starling", team:"Canberra Raiders", isTeamCard:false },
  { num:22, name:"Corey Horsburgh", team:"Canberra Raiders", isTeamCard:false },
  { num:23, name:"Morgan Smithies", team:"Canberra Raiders", isTeamCard:false },
  { num:24, name:"Kaeo Weekes", team:"Canberra Raiders", isTeamCard:false },
  { num:25, name:"Ata Mariota", team:"Canberra Raiders", isTeamCard:false },
  { num:26, name:"Ethan Sanders", team:"Canberra Raiders", isTeamCard:false },
  { num:27, name:"Jayden Brailey", team:"Canberra Raiders", isTeamCard:false },
  { num:28, name:"Sebastian Kris", team:"Canberra Raiders", isTeamCard:false },
  { num:29, name:"Zac Hosking", team:"Canberra Raiders", isTeamCard:false },
  { num:30, name:"Savelio Tamale", team:"Canberra Raiders", isTeamCard:false },
  { num:31, name:"Ethan Strange", team:"Canberra Raiders", isTeamCard:false },
  { num:32, name:"Hudson Young", team:"Canberra Raiders", isTeamCard:false },
  { num:33, name:"Matthew Timoko", team:"Canberra Raiders", isTeamCard:false },
  { num:34, name:"Simi Sasagi", team:"Canberra Raiders", isTeamCard:false },
  { num:35, name:"Xavier Savage", team:"Canberra Raiders", isTeamCard:false },
  { num:36, name:"Josh Papalii", team:"Canberra Raiders", isTeamCard:false },
  { num:37, name:"CANTERBURY BULLDOGS TEAM CARD", team:"Canterbury-Bankstown Bulldogs", isTeamCard:true },
  { num:38, name:"Connor Tracey", team:"Canterbury-Bankstown Bulldogs", isTeamCard:false },
  { num:39, name:"Jacob Kiraz", team:"Canterbury-Bankstown Bulldogs", isTeamCard:false },
  { num:40, name:"Bronson Xerri", team:"Canterbury-Bankstown Bulldogs", isTeamCard:false },
  { num:41, name:"Stephen Crichton", team:"Canterbury-Bankstown Bulldogs", isTeamCard:false },
  { num:42, name:"Marcelo Montoya", team:"Canterbury-Bankstown Bulldogs", isTeamCard:false },
  { num:43, name:"Matt Burton", team:"Canterbury-Bankstown Bulldogs", isTeamCard:false },
  { num:44, name:"Lachlan Galvin", team:"Canterbury-Bankstown Bulldogs", isTeamCard:false },
  { num:45, name:"Max King", team:"Canterbury-Bankstown Bulldogs", isTeamCard:false },
  { num:46, name:"Bailey Hayward", team:"Canterbury-Bankstown Bulldogs", isTeamCard:false },
  { num:47, name:"Samuel Hughes", team:"Canterbury-Bankstown Bulldogs", isTeamCard:false },
  { num:48, name:"Viliame Kikau", team:"Canterbury-Bankstown Bulldogs", isTeamCard:false },
  { num:49, name:"Jacob Preston", team:"Canterbury-Bankstown Bulldogs", isTeamCard:false },
  { num:50, name:"Jaeman Salmon", team:"Canterbury-Bankstown Bulldogs", isTeamCard:false },
  { num:51, name:"Kurt Mann", team:"Canterbury-Bankstown Bulldogs", isTeamCard:false },
  { num:52, name:"Sitili Tupouniua", team:"Canterbury-Bankstown Bulldogs", isTeamCard:false },
  { num:53, name:"Harry Hayes", team:"Canterbury-Bankstown Bulldogs", isTeamCard:false },
  { num:54, name:"Josh Curran", team:"Canterbury-Bankstown Bulldogs", isTeamCard:false },
  { num:55, name:"CRONULLA SHARKS TEAM CARD", team:"Cronulla-Sutherland Sharks", isTeamCard:true },
  { num:56, name:"William Kennedy", team:"Cronulla-Sutherland Sharks", isTeamCard:false },
  { num:57, name:"Billy Burns", team:"Cronulla-Sutherland Sharks", isTeamCard:false },
  { num:58, name:"Toby Rudolf", team:"Cronulla-Sutherland Sharks", isTeamCard:false },
  { num:59, name:"Braydon Trindall", team:"Cronulla-Sutherland Sharks", isTeamCard:false },
  { num:60, name:"Thomas Hazelton", team:"Cronulla-Sutherland Sharks", isTeamCard:false },
  { num:61, name:"Jesse Colquhoun", team:"Cronulla-Sutherland Sharks", isTeamCard:false },
  { num:62, name:"Addin Fonua-Blake", team:"Cronulla-Sutherland Sharks", isTeamCard:false },
  { num:63, name:"Siosifa Talakai", team:"Cronulla-Sutherland Sharks", isTeamCard:false },
  { num:64, name:"KL Iro", team:"Cronulla-Sutherland Sharks", isTeamCard:false },
  { num:65, name:"Samuel Stonestreet", team:"Cronulla-Sutherland Sharks", isTeamCard:false },
  { num:66, name:"Oregon Kaufusi", team:"Cronulla-Sutherland Sharks", isTeamCard:false },
  { num:67, name:"Briton Nikora", team:"Cronulla-Sutherland Sharks", isTeamCard:false },
  { num:68, name:"Blayke Brailey", team:"Cronulla-Sutherland Sharks", isTeamCard:false },
  { num:69, name:"Nicho Hynes", team:"Cronulla-Sutherland Sharks", isTeamCard:false },
  { num:70, name:"Jesse Ramien", team:"Cronulla-Sutherland Sharks", isTeamCard:false },
  { num:71, name:"Sione Katoa", team:"Cronulla-Sutherland Sharks", isTeamCard:false },
  { num:72, name:"Cameron McInnes", team:"Cronulla-Sutherland Sharks", isTeamCard:false },
  { num:73, name:"DOLPHINS TEAM CARD", team:"Dolphins", isTeamCard:true },
  { num:74, name:"Jamayne Isaako", team:"Dolphins", isTeamCard:false },
  { num:75, name:"Tom Gilbert", team:"Dolphins", isTeamCard:false },
  { num:76, name:"Connelly Lemuelu", team:"Dolphins", isTeamCard:false },
  { num:77, name:"Isaiya Katoa", team:"Dolphins", isTeamCard:false },
  { num:78, name:"Felise Kaufusi", team:"Dolphins", isTeamCard:false },
  { num:79, name:"Morgan Knowles", team:"Dolphins", isTeamCard:false },
  { num:80, name:"Herbie Farnworth", team:"Dolphins", isTeamCard:false },
  { num:81, name:"Kulikefu Finefeuiaki", team:"Dolphins", isTeamCard:false },
  { num:82, name:"Hamiso Tabuai-Fidow", team:"Dolphins", isTeamCard:false },
  { num:83, name:"Selwyn Cobbo", team:"Dolphins", isTeamCard:false },
  { num:84, name:"Kodi Nikorima", team:"Dolphins", isTeamCard:false },
  { num:85, name:"Thomas Flegler", team:"Dolphins", isTeamCard:false },
  { num:86, name:"Max Plath", team:"Dolphins", isTeamCard:false },
  { num:87, name:"Francis Molo", team:"Dolphins", isTeamCard:false },
  { num:88, name:"Jack Bostock", team:"Dolphins", isTeamCard:false },
  { num:89, name:"Jeremy Marshall-King", team:"Dolphins", isTeamCard:false },
  { num:90, name:"Trai Fuller", team:"Dolphins", isTeamCard:false },
  { num:91, name:"GOLD COAST TITANS TEAM CARD", team:"Gold Coast Titans", isTeamCard:true },
  { num:92, name:"Phillip Sami", team:"Gold Coast Titans", isTeamCard:false },
  { num:93, name:"Moeaki Fotuaika", team:"Gold Coast Titans", isTeamCard:false },
  { num:94, name:"Beau Fermor", team:"Gold Coast Titans", isTeamCard:false },
  { num:95, name:"Kurtis Morrin", team:"Gold Coast Titans", isTeamCard:false },
  { num:96, name:"Keano Kini", team:"Gold Coast Titans", isTeamCard:false },
  { num:97, name:"Arama Hau", team:"Gold Coast Titans", isTeamCard:false },
  { num:98, name:"Cooper Bai", team:"Gold Coast Titans", isTeamCard:false },
  { num:99, name:"AJ Brimson", team:"Gold Coast Titans", isTeamCard:false },
  { num:100, name:"Klese Haas", team:"Gold Coast Titans", isTeamCard:false },
  { num:101, name:"Tino Fa'asuamaleaui", team:"Gold Coast Titans", isTeamCard:false },
  { num:102, name:"Chris Randall", team:"Gold Coast Titans", isTeamCard:false },
  { num:103, name:"Jayden Campbell", team:"Gold Coast Titans", isTeamCard:false },
  { num:104, name:"Sosefo Fifita", team:"Gold Coast Titans", isTeamCard:false },
  { num:105, name:"Oliver Pascoe", team:"Gold Coast Titans", isTeamCard:false },
  { num:106, name:"Sam Verrills", team:"Gold Coast Titans", isTeamCard:false },
  { num:107, name:"Lachlan Ilias", team:"Gold Coast Titans", isTeamCard:false },
  { num:108, name:"Zane Harrison", team:"Gold Coast Titans", isTeamCard:false },
  { num:109, name:"MANLY SEA EAGLES TEAM CARD", team:"Manly-Warringah Sea Eagles", isTeamCard:true },
  { num:110, name:"Luke Brooks", team:"Manly-Warringah Sea Eagles", isTeamCard:false },
  { num:111, name:"Jake Trbojevic", team:"Manly-Warringah Sea Eagles", isTeamCard:false },
  { num:112, name:"Taniela Paseka", team:"Manly-Warringah Sea Eagles", isTeamCard:false },
  { num:113, name:"Reuben Garrick", team:"Manly-Warringah Sea Eagles", isTeamCard:false },
  { num:114, name:"Jake Simpkin", team:"Manly-Warringah Sea Eagles", isTeamCard:false },
  { num:115, name:"Ben Trbojevic", team:"Manly-Warringah Sea Eagles", isTeamCard:false },
  { num:116, name:"Jason Saab", team:"Manly-Warringah Sea Eagles", isTeamCard:false },
  { num:117, name:"Kobe Hetherington", team:"Manly-Warringah Sea Eagles", isTeamCard:false },
  { num:118, name:"Lehi Hopoate", team:"Manly-Warringah Sea Eagles", isTeamCard:false },
  { num:119, name:"Jamal Fogarty", team:"Manly-Warringah Sea Eagles", isTeamCard:false },
  { num:120, name:"Brandon Wakeham", team:"Manly-Warringah Sea Eagles", isTeamCard:false },
  { num:121, name:"Haumole Olakau'atu", team:"Manly-Warringah Sea Eagles", isTeamCard:false },
  { num:122, name:"Toluta'u Koula", team:"Manly-Warringah Sea Eagles", isTeamCard:false },
  { num:123, name:"Ethan Bullemor", team:"Manly-Warringah Sea Eagles", isTeamCard:false },
  { num:124, name:"Clayton Faulalo", team:"Manly-Warringah Sea Eagles", isTeamCard:false },
  { num:125, name:"Nathan Brown", team:"Manly-Warringah Sea Eagles", isTeamCard:false },
  { num:126, name:"Tom Trbojevic", team:"Manly-Warringah Sea Eagles", isTeamCard:false },
  { num:127, name:"MELBOURNE STORM TEAM CARD", team:"Melbourne Storm", isTeamCard:true },
  { num:128, name:"Josh King", team:"Melbourne Storm", isTeamCard:false },
  { num:129, name:"Stefano Utoikamanu", team:"Melbourne Storm", isTeamCard:false },
  { num:130, name:"Will Warbrick", team:"Melbourne Storm", isTeamCard:false },
  { num:131, name:"Sualauvi Fa'alogo", team:"Melbourne Storm", isTeamCard:false },
  { num:132, name:"Cooper Clarke", team:"Melbourne Storm", isTeamCard:false },
  { num:133, name:"Jahrome Hughes", team:"Melbourne Storm", isTeamCard:false },
  { num:134, name:"Cameron Munster", team:"Melbourne Storm", isTeamCard:false },
  { num:135, name:"Harry Grant", team:"Melbourne Storm", isTeamCard:false },
  { num:136, name:"Joe Chan", team:"Melbourne Storm", isTeamCard:false },
  { num:137, name:"Nick Meaney", team:"Melbourne Storm", isTeamCard:false },
  { num:138, name:"Jack Howarth", team:"Melbourne Storm", isTeamCard:false },
  { num:139, name:"Trent Loiero", team:"Melbourne Storm", isTeamCard:false },
  { num:140, name:"Alec MacDonald", team:"Melbourne Storm", isTeamCard:false },
  { num:141, name:"Tyran Wishart", team:"Melbourne Storm", isTeamCard:false },
  { num:142, name:"Moses Leo", team:"Melbourne Storm", isTeamCard:false },
  { num:143, name:"Ativalu Lisati", team:"Melbourne Storm", isTeamCard:false },
  { num:144, name:"Shawn Blore", team:"Melbourne Storm", isTeamCard:false },
  { num:145, name:"NEWCASTLE KNIGHTS TEAM CARD", team:"Newcastle Knights", isTeamCard:true },
  { num:146, name:"Dane Gagai", team:"Newcastle Knights", isTeamCard:false },
  { num:147, name:"Jacob Saifiti", team:"Newcastle Knights", isTeamCard:false },
  { num:148, name:"Pasami Saulo", team:"Newcastle Knights", isTeamCard:false },
  { num:149, name:"Dominic Young", team:"Newcastle Knights", isTeamCard:false },
  { num:150, name:"Phoenix Crossland", team:"Newcastle Knights", isTeamCard:false },
  { num:151, name:"Mat Croker", team:"Newcastle Knights", isTeamCard:false },
  { num:152, name:"Jermaine McEwen", team:"Newcastle Knights", isTeamCard:false },
  { num:153, name:"Greg Marzhew", team:"Newcastle Knights", isTeamCard:false },
  { num:154, name:"Trey Mooney", team:"Newcastle Knights", isTeamCard:false },
  { num:155, name:"Tyson Frizell", team:"Newcastle Knights", isTeamCard:false },
  { num:156, name:"Dylan Lucas", team:"Newcastle Knights", isTeamCard:false },
  { num:157, name:"Sandon Smith", team:"Newcastle Knights", isTeamCard:false },
  { num:158, name:"Fletcher Sharpe", team:"Newcastle Knights", isTeamCard:false },
  { num:159, name:"Fletcher Hunt", team:"Newcastle Knights", isTeamCard:false },
  { num:160, name:"Dylan Brown", team:"Newcastle Knights", isTeamCard:false },
  { num:161, name:"Kalyn Ponga", team:"Newcastle Knights", isTeamCard:false },
  { num:162, name:"Bradman Best", team:"Newcastle Knights", isTeamCard:false },
  { num:163, name:"NORTH QUEENSLAND COWBOYS TEAM CARD", team:"North Queensland Cowboys", isTeamCard:true },
  { num:164, name:"Coen Hess", team:"North Queensland Cowboys", isTeamCard:false },
  { num:165, name:"Jake Clifford", team:"North Queensland Cowboys", isTeamCard:false },
  { num:166, name:"Scott Drinkwater", team:"North Queensland Cowboys", isTeamCard:false },
  { num:167, name:"Heilum Luki", team:"North Queensland Cowboys", isTeamCard:false },
  { num:168, name:"Griffin Neame", team:"North Queensland Cowboys", isTeamCard:false },
  { num:169, name:"Jaxon Purdue", team:"North Queensland Cowboys", isTeamCard:false },
  { num:170, name:"Jason Taumalolo", team:"North Queensland Cowboys", isTeamCard:false },
  { num:171, name:"Reed Mahoney", team:"North Queensland Cowboys", isTeamCard:false },
  { num:172, name:"Tom Chester", team:"North Queensland Cowboys", isTeamCard:false },
  { num:173, name:"Matt Lodge", team:"North Queensland Cowboys", isTeamCard:false },
  { num:174, name:"Thomas Mikaele", team:"North Queensland Cowboys", isTeamCard:false },
  { num:175, name:"Sam McIntyre", team:"North Queensland Cowboys", isTeamCard:false },
  { num:176, name:"Reuben Cotter", team:"North Queensland Cowboys", isTeamCard:false },
  { num:177, name:"Braidon Burns", team:"North Queensland Cowboys", isTeamCard:false },
  { num:178, name:"Tom Dearden", team:"North Queensland Cowboys", isTeamCard:false },
  { num:179, name:"Murray Taulagi", team:"North Queensland Cowboys", isTeamCard:false },
  { num:180, name:"Jeremiah Nanai", team:"North Queensland Cowboys", isTeamCard:false },
  { num:181, name:"PARRAMATTA EELS TEAM CARD", team:"Parramatta Eels", isTeamCard:true },
  { num:182, name:"Dylan Walker", team:"Parramatta Eels", isTeamCard:false },
  { num:183, name:"Jack Williams", team:"Parramatta Eels", isTeamCard:false },
  { num:184, name:"Tallyn Da Silva", team:"Parramatta Eels", isTeamCard:false },
  { num:185, name:"Josh Addo-Carr", team:"Parramatta Eels", isTeamCard:false },
  { num:186, name:"Brian Kelly", team:"Parramatta Eels", isTeamCard:false },
  { num:187, name:"Sean Russell", team:"Parramatta Eels", isTeamCard:false },
  { num:188, name:"Joash Papalii", team:"Parramatta Eels", isTeamCard:false },
  { num:189, name:"Jack De Belin", team:"Parramatta Eels", isTeamCard:false },
  { num:190, name:"Mitchell Moses", team:"Parramatta Eels", isTeamCard:false },
  { num:191, name:"Junior Paulo", team:"Parramatta Eels", isTeamCard:false },
  { num:192, name:"Kelma Tuilagi", team:"Parramatta Eels", isTeamCard:false },
  { num:193, name:"Luca Moretti", team:"Parramatta Eels", isTeamCard:false },
  { num:194, name:"Kitione Kautoga", team:"Parramatta Eels", isTeamCard:false },
  { num:195, name:"Isaiah Iongi", team:"Parramatta Eels", isTeamCard:false },
  { num:196, name:"Bailey Simonsson", team:"Parramatta Eels", isTeamCard:false },
  { num:197, name:"Will Penisini", team:"Parramatta Eels", isTeamCard:false },
  { num:198, name:"Jonah Pezet", team:"Parramatta Eels", isTeamCard:false },
  { num:199, name:"PENRITH PANTHERS TEAM CARD", team:"Penrith Panthers", isTeamCard:true },
  { num:200, name:"Scott Sorensen", team:"Penrith Panthers", isTeamCard:false },
  { num:201, name:"Moses Leota", team:"Penrith Panthers", isTeamCard:false },
  { num:202, name:"Dylan Edwards", team:"Penrith Panthers", isTeamCard:false },
  { num:203, name:"Isaiah Papali'i", team:"Penrith Panthers", isTeamCard:false },
  { num:204, name:"Lindsay Smith", team:"Penrith Panthers", isTeamCard:false },
  { num:205, name:"Thomas Jenkins", team:"Penrith Panthers", isTeamCard:false },
  { num:206, name:"Paul Alamoti", team:"Penrith Panthers", isTeamCard:false },
  { num:207, name:"Blaize Talagi", team:"Penrith Panthers", isTeamCard:false },
  { num:208, name:"Isaah Yeo", team:"Penrith Panthers", isTeamCard:false },
  { num:209, name:"Nathan Cleary", team:"Penrith Panthers", isTeamCard:false },
  { num:210, name:"Luke Garner", team:"Penrith Panthers", isTeamCard:false },
  { num:211, name:"Brian To'o", team:"Penrith Panthers", isTeamCard:false },
  { num:212, name:"Casey McLean", team:"Penrith Panthers", isTeamCard:false },
  { num:213, name:"Izack Tago", team:"Penrith Panthers", isTeamCard:false },
  { num:214, name:"Liam Martin", team:"Penrith Panthers", isTeamCard:false },
  { num:215, name:"Mitch Kenny", team:"Penrith Panthers", isTeamCard:false },
  { num:216, name:"Liam Henry", team:"Penrith Panthers", isTeamCard:false },
  { num:217, name:"SOUTH SYDNEY RABBITOHS TEAM CARD", team:"South Sydney Rabbitohs", isTeamCard:true },
  { num:218, name:"Cody Walker", team:"South Sydney Rabbitohs", isTeamCard:false },
  { num:219, name:"Tevita Tatola", team:"South Sydney Rabbitohs", isTeamCard:false },
  { num:220, name:"Keaon Koloamatangi", team:"South Sydney Rabbitohs", isTeamCard:false },
  { num:221, name:"Tallis Duncan", team:"South Sydney Rabbitohs", isTeamCard:false },
  { num:222, name:"Alex Johnston", team:"South Sydney Rabbitohs", isTeamCard:false },
  { num:223, name:"Lachlan Hubner", team:"South Sydney Rabbitohs", isTeamCard:false },
  { num:224, name:"Cameron Murray", team:"South Sydney Rabbitohs", isTeamCard:false },
  { num:225, name:"Campbell Graham", team:"South Sydney Rabbitohs", isTeamCard:false },
  { num:226, name:"Bronson Garlick", team:"South Sydney Rabbitohs", isTeamCard:false },
  { num:227, name:"Euan Aitken", team:"South Sydney Rabbitohs", isTeamCard:false },
  { num:228, name:"Latrell Mitchell", team:"South Sydney Rabbitohs", isTeamCard:false },
  { num:229, name:"Jye Gray", team:"South Sydney Rabbitohs", isTeamCard:false },
  { num:230, name:"Jack Wighton", team:"South Sydney Rabbitohs", isTeamCard:false },
  { num:231, name:"Jamie Humphreys", team:"South Sydney Rabbitohs", isTeamCard:false },
  { num:232, name:"David Fifita", team:"South Sydney Rabbitohs", isTeamCard:false },
  { num:233, name:"Ashton Ward", team:"South Sydney Rabbitohs", isTeamCard:false },
  { num:234, name:"Brandon Smith", team:"South Sydney Rabbitohs", isTeamCard:false },
  { num:235, name:"ST GEORGE ILLAWARRA DRAGONS TEAM CARD", team:"St. George Illawarra Dragons", isTeamCard:true },
  { num:236, name:"Damien Cook", team:"St. George Illawarra Dragons", isTeamCard:false },
  { num:237, name:"Valentine Holmes", team:"St. George Illawarra Dragons", isTeamCard:false },
  { num:238, name:"Hamish Stewart", team:"St. George Illawarra Dragons", isTeamCard:false },
  { num:239, name:"Emre Guler", team:"St. George Illawarra Dragons", isTeamCard:false },
  { num:240, name:"Daniel Atkinson", team:"St. George Illawarra Dragons", isTeamCard:false },
  { num:241, name:"Toby Couchman", team:"St. George Illawarra Dragons", isTeamCard:false },
  { num:242, name:"Josh Kerr", team:"St. George Illawarra Dragons", isTeamCard:false },
  { num:243, name:"Setu Tu", team:"St. George Illawarra Dragons", isTeamCard:false },
  { num:244, name:"Clinton Gutherson", team:"St. George Illawarra Dragons", isTeamCard:false },
  { num:245, name:"Luciano Leilua", team:"St. George Illawarra Dragons", isTeamCard:false },
  { num:246, name:"Kyle Flanagan", team:"St. George Illawarra Dragons", isTeamCard:false },
  { num:247, name:"Moses Suli", team:"St. George Illawarra Dragons", isTeamCard:false },
  { num:248, name:"Christian Tuipulotu", team:"St. George Illawarra Dragons", isTeamCard:false },
  { num:249, name:"Ryan Couchman", team:"St. George Illawarra Dragons", isTeamCard:false },
  { num:250, name:"Jaydn Su'A", team:"St. George Illawarra Dragons", isTeamCard:false },
  { num:251, name:"Blake Lawrie", team:"St. George Illawarra Dragons", isTeamCard:false },
  { num:252, name:"Mathew Feagai", team:"St. George Illawarra Dragons", isTeamCard:false },
  { num:253, name:"SYDNEY ROOSTERS TEAM CARD", team:"Sydney Roosters", isTeamCard:true },
  { num:254, name:"Daly Cherry-Evans", team:"Sydney Roosters", isTeamCard:false },
  { num:255, name:"Connor Watson", team:"Sydney Roosters", isTeamCard:false },
  { num:256, name:"Angus Crichton", team:"Sydney Roosters", isTeamCard:false },
  { num:257, name:"Nat Butcher", team:"Sydney Roosters", isTeamCard:false },
  { num:258, name:"Naufahu Whyte", team:"Sydney Roosters", isTeamCard:false },
  { num:259, name:"Siua Wong", team:"Sydney Roosters", isTeamCard:false },
  { num:260, name:"James Tedesco", team:"Sydney Roosters", isTeamCard:false },
  { num:261, name:"Sam Walker", team:"Sydney Roosters", isTeamCard:false },
  { num:262, name:"Lindsay Collins", team:"Sydney Roosters", isTeamCard:false },
  { num:263, name:"Robert Toia", team:"Sydney Roosters", isTeamCard:false },
  { num:264, name:"Salesi Foketi", team:"Sydney Roosters", isTeamCard:false },
  { num:265, name:"Daniel Tupou", team:"Sydney Roosters", isTeamCard:false },
  { num:266, name:"Reece Robson", team:"Sydney Roosters", isTeamCard:false },
  { num:267, name:"Hugo Savala", team:"Sydney Roosters", isTeamCard:false },
  { num:268, name:"Spencer Leniu", team:"Sydney Roosters", isTeamCard:false },
  { num:269, name:"Mark Nawaqanitawase", team:"Sydney Roosters", isTeamCard:false },
  { num:270, name:"Victor Radley", team:"Sydney Roosters", isTeamCard:false },
  { num:271, name:"WARRIORS TEAM CARD", team:"New Zealand Warriors", isTeamCard:true },
  { num:272, name:"Dallin Watene-Zelezniak", team:"New Zealand Warriors", isTeamCard:false },
  { num:273, name:"Erin Clark", team:"New Zealand Warriors", isTeamCard:false },
  { num:274, name:"Wayde Egan", team:"New Zealand Warriors", isTeamCard:false },
  { num:275, name:"Jackson Ford", team:"New Zealand Warriors", isTeamCard:false },
  { num:276, name:"James Fisher-Harris", team:"New Zealand Warriors", isTeamCard:false },
  { num:277, name:"Jacob Laban", team:"New Zealand Warriors", isTeamCard:false },
  { num:278, name:"Selumiela Halasima", team:"New Zealand Warriors", isTeamCard:false },
  { num:279, name:"Roger Tuivasa-Sheck", team:"New Zealand Warriors", isTeamCard:false },
  { num:280, name:"Chanel Harris-Tavita", team:"New Zealand Warriors", isTeamCard:false },
  { num:281, name:"Tanah Boyd", team:"New Zealand Warriors", isTeamCard:false },
  { num:282, name:"Adam Pompey", team:"New Zealand Warriors", isTeamCard:false },
  { num:283, name:"Alofiana Khan-Pereira", team:"New Zealand Warriors", isTeamCard:false },
  { num:284, name:"Charnze Nicoll-Klokstad", team:"New Zealand Warriors", isTeamCard:false },
  { num:285, name:"Marata Niukore", team:"New Zealand Warriors", isTeamCard:false },
  { num:286, name:"Mitchell Barnett", team:"New Zealand Warriors", isTeamCard:false },
  { num:287, name:"Kurt Capewell", team:"New Zealand Warriors", isTeamCard:false },
  { num:288, name:"Te Maire Martin", team:"New Zealand Warriors", isTeamCard:false },
  { num:289, name:"WESTS TIGERS TEAM CARD", team:"Wests Tigers", isTeamCard:true },
  { num:290, name:"Sunia Turuva", team:"Wests Tigers", isTeamCard:false },
  { num:291, name:"Fonua Pole", team:"Wests Tigers", isTeamCard:false },
  { num:292, name:"Royce Hunt", team:"Wests Tigers", isTeamCard:false },
  { num:293, name:"Alex Seyfarth", team:"Wests Tigers", isTeamCard:false },
  { num:294, name:"Kai Pearce-Paul", team:"Wests Tigers", isTeamCard:false },
  { num:295, name:"Terrell May", team:"Wests Tigers", isTeamCard:false },
  { num:296, name:"Sione Fainu", team:"Wests Tigers", isTeamCard:false },
  { num:297, name:"Alex Twal", team:"Wests Tigers", isTeamCard:false },
  { num:298, name:"Jarome Luai", team:"Wests Tigers", isTeamCard:false },
  { num:299, name:"Jock Madden", team:"Wests Tigers", isTeamCard:false },
  { num:300, name:"Jahream Bula", team:"Wests Tigers", isTeamCard:false },
  { num:301, name:"Apisai Koroisau", team:"Wests Tigers", isTeamCard:false },
  { num:302, name:"Latu Fainu", team:"Wests Tigers", isTeamCard:false },
  { num:303, name:"Adam Doueihi", team:"Wests Tigers", isTeamCard:false },
  { num:304, name:"Samuela Fainu", team:"Wests Tigers", isTeamCard:false },
  { num:305, name:"Heamasi Makasini", team:"Wests Tigers", isTeamCard:false },
  { num:306, name:"Taylan May", team:"Wests Tigers", isTeamCard:false },
];

// 5 sets × 306 cards = 1530 total cards
const ALL_CARDS = [];
const RARITIES_ORDER = ["common","silver","gold","platinum","ultimate"];
RARITIES_ORDER.forEach((rarity, setIdx) => {
  CARD_DEFINITIONS.forEach(def => {
    ALL_CARDS.push({
      id: `${rarity}-card-${def.num}`,
      num: def.num,
      name: def.name,
      team: def.team,
      isTeamCard: def.isTeamCard,
      player: def.isTeamCard ? def.team : def.name,
      rarity,
      set: setIdx + 1,
    });
  });
});

// ─── FOREVER COLLECTION SET DEFINITIONS ────────────────────────────────────────
// ─── RANK / BELT SYSTEM ─────────────────────────────────────────────────────────
// Based on all-time games attended. Belt color + stripe count, martial-arts style.
// Rank thresholds are the MINIMUM all-time games attended required to hold that rank.
const RANK_TIERS = [
  { rank:1,  games:0,   belt:"White",  stripes:0 },
  { rank:2,  games:1,   belt:"White",  stripes:1 },
  { rank:3,  games:2,   belt:"White",  stripes:2 },
  { rank:4,  games:3,   belt:"White",  stripes:3 },
  { rank:5,  games:4,   belt:"White",  stripes:4 },
  { rank:6,  games:5,   belt:"Yellow", stripes:0 },
  { rank:7,  games:6,   belt:"Yellow", stripes:1 },
  { rank:8,  games:7,   belt:"Yellow", stripes:2 },
  { rank:9,  games:8,   belt:"Yellow", stripes:3 },
  { rank:10, games:9,   belt:"Yellow", stripes:4 },
  { rank:11, games:10,  belt:"Blue",   stripes:0 },
  { rank:12, games:11,  belt:"Blue",   stripes:1 },
  { rank:13, games:12,  belt:"Blue",   stripes:2 },
  { rank:14, games:13,  belt:"Blue",   stripes:3 },
  { rank:15, games:14,  belt:"Blue",   stripes:4 },
  { rank:16, games:15,  belt:"Green",  stripes:0 },
  { rank:17, games:16,  belt:"Green",  stripes:1 },
  { rank:18, games:17,  belt:"Green",  stripes:2 },
  { rank:19, games:18,  belt:"Green",  stripes:3 },
  { rank:20, games:19,  belt:"Green",  stripes:4 },
  { rank:21, games:20,  belt:"Purple", stripes:0 },
  { rank:22, games:22,  belt:"Purple", stripes:1 },
  { rank:23, games:26,  belt:"Purple", stripes:2 },
  { rank:24, games:30,  belt:"Purple", stripes:3 },
  { rank:25, games:35,  belt:"Purple", stripes:4 },
  { rank:26, games:41,  belt:"Red",    stripes:0 },
  { rank:27, games:48,  belt:"Red",    stripes:1 },
  { rank:28, games:56,  belt:"Red",    stripes:2 },
  { rank:29, games:65,  belt:"Red",    stripes:3 },
  { rank:30, games:76,  belt:"Red",    stripes:4 },
  { rank:31, games:89,  belt:"Brown",  stripes:0 },
  { rank:32, games:103, belt:"Brown",  stripes:1 },
  { rank:33, games:121, belt:"Brown",  stripes:2 },
  { rank:34, games:141, belt:"Brown",  stripes:3 },
  { rank:35, games:164, belt:"Brown",  stripes:4 },
  { rank:36, games:192, belt:"Black",  stripes:0 },
  { rank:37, games:224, belt:"Black",  stripes:1 },
  { rank:38, games:261, belt:"Black",  stripes:2 },
  { rank:39, games:305, belt:"Black",  stripes:3 },
  { rank:40, games:356, belt:"Black",  stripes:4 },
  { rank:41, games:415, belt:"Black",  stripes:5 },
  { rank:42, games:485, belt:"Black",  stripes:6 },
  { rank:43, games:566, belt:"Black",  stripes:7 },
  { rank:44, games:661, belt:"Black",  stripes:8 },
  { rank:45, games:771, belt:"Black",  stripes:9 },
  { rank:46, games:900, belt:"Black",  stripes:10 },
];

const BELT_COLORS = {
  White:  { base:"#ffffff", border:"#d1d5db", stripe:"#1a1a1a" },
  Yellow: { base:"#fbbf24", border:"#d97706", stripe:"#1a1a1a" },
  Blue:   { base:"#3b82f6", border:"#1d4ed8", stripe:"#ffffff" },
  Green:  { base:"#22c55e", border:"#15803d", stripe:"#ffffff" },
  Purple: { base:"#a855f7", border:"#7e22ce", stripe:"#ffffff" },
  Red:    { base:"#ef4444", border:"#b91c1c", stripe:"#ffffff" },
  Brown:  { base:"#92400e", border:"#5c2a08", stripe:"#ffffff" },
  Black:  { base:"#18181b", border:"#000000", stripe:"#ffffff" },
};

// Returns the current rank tier object for a given all-time games-attended count.
function getRankForGames(gamesAttended) {
  let current = RANK_TIERS[0];
  for(const tier of RANK_TIERS) {
    if(gamesAttended >= tier.games) current = tier;
    else break;
  }
  return current;
}

// Returns the next rank tier (or null if already at max rank).
function getNextRank(currentRankNum) {
  return RANK_TIERS.find(t => t.rank === currentRankNum + 1) || null;
}

const CURRENT_SEASON = 2026;
// Admin access is now a server-side role (profiles.is_admin, seeded from
// app_config.admin_email) — no hard-coded admin email in the client (spec §5).
const SEASON_START_DATE = "2026-02-28"; // first Round 1 fixture date — used to time the "confirm your team" prompt window
// MOCK_USERS (fake leaderboard padding) removed — real users only (spec §4).

// ─── STORAGE ──────────────────────────────────────────────────────────────────
const DB_KEY = "nrl_tracker_v3";
function loadDB() {
  try { return JSON.parse(localStorage.getItem(DB_KEY) || "{}"); } catch { return {}; }
}
function saveDB(data) {
  try { localStorage.setItem(DB_KEY, JSON.stringify(data)); } catch {}
}

// ─── ICONS ────────────────────────────────────────────────────────────────────
const Ico = {
  Home: ()=><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  Games: ()=><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 8v8M8 12h8"/></svg>,
  Badges: ()=><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/></svg>,
  Cards: ()=><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/></svg>,
  Board: ()=><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
  More: ()=><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>,
  Plus: ()=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  X: ()=><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  Lock: ()=><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>,
  Zap: ()=><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  Trade: ()=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M7 16V4m0 0L3 8m4-4l4 4"/><path d="M17 8v12m0 0l4-4m-4 4l-4-4"/></svg>,
  Pack: ()=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>,
};

// ─── COMPONENTS ──────────────────────────────────────────────────────────────

function TradingCard({ card, size="md", activated=false, onClick }) {
  const r = RARITY[card.rarity];
  const team = NRL_TEAMS[card.team];
  const sz = size==="sm" ? { w:90, h:126, font:8, namef:9 } : size==="lg" ? { w:160, h:224, font:12, namef:14 } : { w:120, h:168, font:10, namef:12 };
  return (
    <div onClick={onClick} style={{
      width: sz.w, height: sz.h, borderRadius: 10, cursor: onClick?"pointer":"default",
      background: r.gradient, border: `2px solid ${r.color}`,
      boxShadow: activated ? `0 0 20px ${r.glow}, 0 0 40px ${r.glow}` : `0 0 8px ${r.glow}`,
      display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"space-between",
      padding: 8, position:"relative", overflow:"hidden", transition:"transform 0.2s",
      transform: onClick?"scale(1)":"none", flexShrink:0,
    }}>
      {activated && <div style={{ position:"absolute", inset:0, background:"rgba(255,200,0,0.1)", borderRadius:8, pointerEvents:"none" }}/>}
      <div style={{ fontSize: sz.font, color: r.color, fontWeight:700, letterSpacing:1, textTransform:"uppercase", opacity:0.8 }}>{r.label}</div>
      <div style={{ width: sz.namef+16, height: sz.namef+16, borderRadius:"50%", background:"rgba(255,255,255,0.08)" }}/>
      <div style={{ textAlign:"center" }}>
        <div style={{ fontSize: sz.namef, fontWeight:700, color:"#fff", lineHeight:1.2 }}>{card.player}</div>
        <div style={{ fontSize: sz.font, color:"rgba(12,19,12,0.5)", marginTop:2 }}><TeamName team={card.team} size={sz.font}>{card.team.split(" ").slice(-1)[0]}</TeamName></div>
      </div>
      <div style={{ fontSize: sz.font-1, color: r.color, fontWeight:800 }}>{r.emoji} {r.pts} pts</div>
      {activated && <div style={{ position:"absolute", top:4, right:4, fontSize:10 }}>⚡</div>}
    </div>
  );
}



function StatPill({ label, value, color, onClick }) {
  return (
    <div onClick={onClick} style={{ background:T.surfaceHigh, borderRadius:T.radiusSm, padding:"10px 14px", textAlign:"center", flex:1, cursor:onClick?"pointer":"default", border:`1px solid ${onClick?"rgba(12,19,12,0.15)":T.border}`, transition:"all 0.15s" }}>
      <div style={{ fontSize:20, fontWeight:800, color: color||T.text }}>{value}</div>
      <div style={{ fontSize:10, color:T.textMuted, marginTop:2 }}>{label}</div>
      {onClick && <div style={{ fontSize:9, color:T.textDim, marginTop:1 }}>tap to view</div>}
    </div>
  );
}


function Badge({ badge, earned, size="md", svgCache, onVisible }) {
  const sz = size==="sm" ? 44 : size==="lg" ? 80 : 60;
  const svg = svgCache?.[badge.id];
  const ref = (el) => {
    if(el && onVisible && !svg) {
      const obs = new IntersectionObserver(([entry]) => {
        if(entry.isIntersecting) { onVisible(badge); obs.disconnect(); }
      }, { threshold: 0.1 });
      obs.observe(el);
    }
  };
  const iconContent = () => {
    // Locked badges: consistent grey circle with white lock icon (no per-badge variation)
    if(!earned) {
      return (
        <div style={{ width:sz, height:sz, borderRadius:"50%", background:"#9ca3af", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
          <svg width={sz*0.42} height={sz*0.42} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
        </div>
      );
    }
    // Earned badges: unique emoji per badge in a colored circle
    return (
      <div style={{ width:sz, height:sz, borderRadius:"50%", background:"linear-gradient(135deg,#1a2e1a,#16314f)", border:"2px solid #41ab80", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, boxShadow:"0 0 14px rgba(65,171,128,0.4)" }}>
        <span style={{ fontSize:sz*0.42 }}>{badge.icon}</span>
      </div>
    );
  };
  return (
    <div ref={ref} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:5, opacity: earned?1:0.4, filter: earned?"none":"grayscale(0.5)" }}>
      {iconContent()}
      {size!=="sm" && <div style={{ fontSize:10, color: earned?T.text:T.textDim, textAlign:"center", maxWidth:sz+10, lineHeight:1.2, fontWeight: earned?600:400 }}>{badge.name}</div>}
      {size!=="sm" && <div style={{ fontSize:10, color:"#39ff14", fontWeight:700 }}>{badge.pts}XP</div>}
    </div>
  );
}

// ─── BADGES SCREEN (Round 7 — server truth) ───────────────────────────────────
// Extracted from the App body. Reads the signed-in user's earned badge id sets
// (from `user_badges` via fetchMyBadges) and the shared BADGE_DEFS; no client
// award path (SEC-3). earnedSeason / earnedAllTime are Set<id> from the server.
export function BadgesScreen({ defs, earnedSeason, earnedAllTime, seasonYear, svgCache, onVisible, onLogGame, onOpenBadge }) {
  const [badgeFilter, setBadgeFilter] = useState("season-all");
  const seasonSet = earnedSeason instanceof Set ? earnedSeason : new Set();
  const allTimeSet = earnedAllTime instanceof Set ? earnedAllTime : new Set();
  const earnedCount = Math.max(seasonSet.size, allTimeSet.size);
  return (
    <div>
      <div style={{ padding:"24px 20px 16px", borderBottom:`1px solid ${T.border}` }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <img src="/brand-mark-192.png" alt="" style={{ width:32, height:32, objectFit:"contain", display:"block" }} />
            <span style={{ fontWeight:800, fontSize:16, color:T.text }}>{branding.appName}</span>
          </div>
          <button onClick={onLogGame} style={{ background:T.accent, border:"none", borderRadius:24, padding:"8px 18px", color:"#000", fontSize:13, fontWeight:800, cursor:"pointer" }}>+ Log Game</button>
        </div>
        <div style={{ marginTop:20 }}>
          <div style={{ fontSize:11, fontWeight:700, color:T.textMuted, letterSpacing:2, marginBottom:4 }}>YOUR</div>
          <div style={{ fontSize:32, fontWeight:900, lineHeight:1.1, color:T.text }}>Badges</div>
          <div style={{ fontSize:13, color:T.textMuted, marginTop:4 }}>{`${earnedCount} of ${BADGE_DEFS.length} earned`}</div>
        </div>
      </div>
      <div style={{ padding:"16px" }}>
        <div style={{ display:"flex", background:T.surface, borderRadius:T.radius, padding:4, marginBottom:16, border:`1px solid ${T.border}` }}>
          {["alltime","season"].map(v=>{
            const isActive = (v==="alltime"&&badgeFilter.startsWith("alltime"))||(v==="season"&&badgeFilter.startsWith("season"));
            return (
              <button key={v} onClick={()=>setBadgeFilter(v==="alltime"?"alltime-all":"season-all")} style={{ flex:1, padding:"10px 0", border:"none", cursor:"pointer", borderRadius:T.radiusSm, background:isActive?T.accent:"transparent", color:isActive?"#000":T.textMuted, fontSize:14, fontWeight:isActive?700:400, transition:"all 0.15s" }}>
                {v==="alltime"?"All Time":`${seasonYear} Season`}
              </button>
            );
          })}
        </div>
        <div style={{ display:"flex", gap:6, marginBottom:16, overflowX:"auto", paddingBottom:4 }}>
          {["all","milestones","exploration","attendance","performance","combo"].map(f=>{
            const prefix = badgeFilter.startsWith("season")?"season":"alltime";
            const active = badgeFilter===`${prefix}-${f}`;
            const labels = { all:"All", milestones:"Milestones", exploration:"Exploration", attendance:"Attendance", performance:"Performance", combo:"Combo Moves" };
            return (
              <button key={f} onClick={()=>setBadgeFilter(`${prefix}-${f}`)} style={{ background:active?T.accent:"transparent", border:`1px solid ${active?T.accent:T.border}`, borderRadius:20, padding:"6px 14px", cursor:"pointer", color:active?"#000":T.textMuted, fontSize:12, fontWeight:active?700:400, whiteSpace:"nowrap", flexShrink:0 }}>
                {labels[f]}
              </button>
            );
          })}
        </div>
        {(() => {
          const isSeason = badgeFilter.startsWith("season");
          const catPart = badgeFilter.split("-").slice(1).join("-");
          const activeBadges = isSeason ? seasonSet : allTimeSet;
          const catLabels = { milestones:"MILESTONES", exploration:"EXPLORATION", attendance:"ATTENDANCE", performance:"PERFORMANCE", combo:"COMBO MOVES" };
          const cats = catPart==="all" ? ["milestones","exploration","attendance","performance","combo"] : [catPart];
          return cats.map(cat => {
            const catBadges = (defs||BADGE_DEFS).filter(b=>b.cat===cat);
            return (
              <div key={cat} style={{ marginBottom:24 }}>
                <div style={{ fontSize:11, fontWeight:700, color:T.textDim, letterSpacing:2, marginBottom:12 }}>{catLabels[cat]||cat.toUpperCase()}</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                  {catBadges.map(b=>{
                    const earned = activeBadges.has(b.id);
                    return (
                      <div key={b.id} data-badge-id={b.id} onClick={()=>{ if(earned) onOpenBadge(b); }}
                        style={{ background:earned?"rgba(57,255,20,0.06)":T.surface, border:`1px solid ${earned?T.borderGreen:T.border}`, borderRadius:T.radius, padding:"16px 14px", cursor:earned?"pointer":"default" }}>
                        <div style={{ display:"flex", justifyContent:"center", marginBottom:10 }}>
                          <Badge badge={b} earned={earned} size="sm" svgCache={svgCache} onVisible={onVisible}/>
                        </div>
                        <div style={{ fontSize:13, fontWeight:700, color:earned?T.accent:T.text, lineHeight:1.3, textAlign:"center" }}>{b.name}</div>
                        <div style={{ fontSize:11, color:T.textMuted, marginTop:4, lineHeight:1.4, textAlign:"center" }}>{b.desc}</div>
                        <div style={{ textAlign:"center", marginTop:8 }}>
                          <span style={{ fontSize:11, color:T.textMuted }}>· {b.pts} XP</span>
                        </div>
                        {earned && <div style={{ textAlign:"center", marginTop:4 }}><span style={{ fontSize:10, color:T.accent, fontWeight:700 }}>✓ EARNED</span></div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          });
        })()}
      </div>
    </div>
  );
}

// ─── COUNTDOWN TO LOGGING WINDOW OPENING ──────────────────────────────────────
function CountdownToOpen({ targetMs }) {
  const [now, setNow] = useState(appNowMs());
  useEffect(() => {
    const interval = setInterval(() => setNow(appNowMs()), 1000);
    return () => clearInterval(interval);
  }, []);
  const diff = Math.max(0, targetMs - now);
  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  let label;
  if(days > 0) label = `${days}d ${hours}h ${minutes}m`;
  else if(hours > 0) label = `${hours}h ${minutes}m ${seconds}s`;
  else label = `${minutes}m ${seconds}s`;
  return (
    <div style={{ background:T.surfaceHigh, borderRadius:8, padding:"10px 12px", textAlign:"center" }}>
      <div style={{ fontSize:11, color:T.textDim, marginBottom:2 }}>Check-in opens in</div>
      <div style={{ fontSize:15, fontWeight:800, color:T.text, fontFamily:"monospace" }}>{label}</div>
    </div>
  );
}

function PackCountdown({ targetMs, onElapsed }) {
  const [now, setNow] = useState(appNowMs());
  useEffect(() => {
    const id = setInterval(() => setNow(appNowMs()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, targetMs - now);
  const totalSeconds = Math.floor(diff / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  useEffect(() => {
    if (diff === 0) onElapsed?.();
  }, [diff, onElapsed]);
  const label = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m ${totalSeconds % 60}s`;
  return (
    <div style={{ background:T.surfaceHigh, borderRadius:T.radiusSm, padding:"10px 14px", textAlign:"center", minWidth:116 }}>
      <div style={{ fontSize:11, color:T.textDim, marginBottom:2 }}>Next free pack in</div>
      <div style={{ fontSize:15, fontWeight:800, color:T.text, fontFamily:"monospace" }}>{label}</div>
    </div>
  );
}

// ─── BELT/RANK ICON COMPONENT ──────────────────────────────────────────────────
// Renders a small belt icon (color + stripe count) representing the user's current rank.
function BeltIcon({ rankTier, size=28, onClick }) {
  if(!rankTier) return null;
  const colors = BELT_COLORS[rankTier.belt] || BELT_COLORS.White;
  const w = size, h = size * 0.42;
  const stripeCount = Math.min(rankTier.stripes, 4); // visually cap at 4 stripes, matches reference belts
  const extraStripes = rankTier.stripes > 4 ? rankTier.stripes - 4 : 0; // black-belt degrees beyond 4 shown as a small number badge
  return (
    <div onClick={onClick} style={{ position:"relative", display:"inline-flex", alignItems:"center", cursor:onClick?"pointer":"default" }} title={`${rankTier.belt} Belt${rankTier.stripes>0?` · ${rankTier.stripes} stripe${rankTier.stripes!==1?"s":""}`:""}`}>
      <svg width={w} height={h} viewBox="0 0 40 17">
        {/* Belt — single horizontal bar */}
        <rect x="1" y="1" width="38" height="15" rx="3" fill={colors.base} stroke={colors.border} strokeWidth="1"/>
        {/* Stripes — vertical, on the right end of the belt */}
        {Array.from({length:stripeCount}).map((_,i)=>(
          <rect key={i} x={32 - i*4} y="2.5" width="1.8" height="12" fill={colors.stripe}/>
        ))}
      </svg>
      {extraStripes > 0 && (
        <div style={{ position:"absolute", bottom:-4, right:-4, background:T.gold, color:"#000", fontSize:9, fontWeight:800, borderRadius:"50%", width:14, height:14, display:"flex", alignItems:"center", justifyContent:"center", border:"1px solid #fff" }}>
          {extraStripes}
        </div>
      )}
    </div>
  );
}

// ─── ADMIN WEEKLY CONFIRMATION SCREEN ──────────────────────────────────────────
import {
  fetchTeamPlayers,
  addPlayer,
  stampPlayerIdentity,
  fetchMilestoneCodes,
  fetchAdminNotifications,
  acknowledgeNotification,
  syncRound,
} from "./lib/adminApi";
import { adminMovePlayer as squadsAdminMovePlayer, adminAddToSquad as squadsAdminAddToSquad } from "./lib/squadsApi";
import { fetchSquadDrift, applySquadDrift, ignoreSquadDrift, runSquadDriftCheck } from "./lib/squadDriftApi";

// Drafts fetched by "Fetch all for round" are persisted in sessionStorage keyed
// by fixture id (JSON) so they survive the admin leaving the screen and coming
// back. A stored draft is applied ONCE on open then deleted, so re-opening an
// un-confirmed fixture never clobbers the admin's hand edits; a fresh "Fetch
// from RLP" still overrides. Cleared on successful confirm.
// R16 (F3): the same store also holds Origin editor drafts
// (`{ type:"origin", … }`). sessionStorage is the ONLY copy: there is no React
// mirror to go stale, and every write goes through `updateDraftStore`, which
// re-reads the current object and changes only the caller's own key — no path
// ever writes back a snapshot taken earlier, so one fixture's write can never
// erase another fixture's draft. Clock invalidation and account change clear
// the whole store (lib/appClock.js).
const DRAFT_STORAGE_KEY = "skohr_admin_drafts";
function readDraftsFromStorage() {
  try { return JSON.parse(sessionStorage.getItem(DRAFT_STORAGE_KEY) || "{}") || {}; }
  catch { return {}; }
}
function writeDraftsToStorage(obj) {
  try { sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(obj)); } catch { /* ignore */ }
}
// `change(all)` edits the freshly read object in place and returns true when
// it changed something; only then is the store written.
function updateDraftStore(change) {
  const all = readDraftsFromStorage();
  if (change(all)) writeDraftsToStorage(all);
}
const isOriginDraft = (d) => !!d && d.type === "origin";

// Origin saves in flight, by fixture id: double-submit protection. It lives at
// module scope, not in AdminConfirmScreen, so it survives closing and reopening
// the editor AND leaving and re-entering Weekly Confirmation (which unmounts
// the screen) while a save is pending. A fixture is released when its save
// settles (success, refusal or throw), whichever screen instance is mounted
// then — clock invalidation and account change never leave it locked.
// Mounted screens subscribe so a remounted editor re-renders on release.
const originSavesInFlight = new Set();
const originSaveListeners = new Set();
function setOriginSaveInFlight(fixtureId, pending) {
  if (pending) originSavesInFlight.add(fixtureId); else originSavesInFlight.delete(fixtureId);
  originSaveListeners.forEach(fn => fn());
}

// ─── ROUND 13 — WEEKEND SQUAD-DRIFT SECTION ───────────────────────────────────
// Lists every player whose squad on Rugby League Project differs from ours
// (pending items from admin_squad_drift). Its loading/error/empty states are
// independent of the match queue. Move is two-step (it replaces the player's
// season squads, admin corrections included); Ignore hides the change. Every
// response is discarded after unmount or an account change (generation guard).
const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function driftDateLabel(isoDate) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(isoDate || ""));
  return m ? `${Number(m[3])} ${MONTHS_SHORT[Number(m[2]) - 1]} ${m[1]}` : "";
}
// The AEST calendar date of an instant (Brisbane keeps AEST all year).
function driftInstantLabel(iso) {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return "";
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-AU", { timeZone: "Australia/Brisbane", year: "numeric", month: "numeric", day: "numeric" })
    .formatToParts(when).map(p => [p.type, p.value]));
  return `${Number(parts.day)} ${MONTHS_SHORT[Number(parts.month) - 1]} ${parts.year}`;
}

function SquadDriftSection({ replayMode, onChecked }) {
  const [list, setList] = useState(null); // { season, lastRunAt, items } after a successful load
  const [loadState, setLoadState] = useState("loading"); // loading | ready | error
  const [confirming, setConfirming] = useState(null); // item id awaiting Move confirmation
  const [busyId, setBusyId] = useState(null);
  const [message, setMessage] = useState(null); // { role, text } for Move/Ignore outcomes
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState("");
  const request = useRef(0); // list-read ordering only: the newest list read wins
  const alive = useRef(true); // component lifetime; reversible so StrictMode's setup/cleanup/setup is safe
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  async function load({ quiet = false } = {}) {
    const generation = getClockGeneration();
    const mine = ++request.current;
    if (!quiet) setLoadState("loading");
    const res = await fetchSquadDrift();
    if (generation !== getClockGeneration() || !alive.current || mine !== request.current) return;
    if (!res.ok) {
      setLoadState("error");
      return;
    }
    setList({ season: res.season, lastRunAt: res.last_run_at ?? null, items: res.items });
    setLoadState("ready");
  }
  useEffect(() => { load(); }, []);

  async function act(item, run) {
    if (busyId) return;
    const generation = getClockGeneration();
    setMessage(null);
    setBusyId(item.id);
    const res = await run(item.id);
    if (generation !== getClockGeneration() || !alive.current) return;
    setBusyId(null);
    setConfirming(null);
    if (res.ok) return load({ quiet: true });
    if (res.error === "not_pending") {
      setMessage({ role: "status", text: "This change was already handled — refreshing." });
      return load({ quiet: true });
    }
    if (res.error === "stale_item") return setMessage({ role: "alert", text: "This player's squads changed since the check — run Check now." });
    setMessage({ role: "alert", text: res.error === "forbidden" ? "You're not signed in as an admin." : "That didn't go through — try again." });
  }

  async function checkNow() {
    if (checking) return;
    const generation = getClockGeneration();
    setChecking(true);
    setCheckError("");
    const res = await runSquadDriftCheck();
    if (generation !== getClockGeneration() || !alive.current) return;
    setChecking(false);
    if (!res.ok) {
      if (res.status === 502 || res.error === "feed_unavailable") return setCheckError("Rugby League Project couldn't be read — try again later.");
      if (res.status === 401 || res.status === 403 || res.error === "unauthenticated" || res.error === "forbidden") return setCheckError("You're not signed in as an admin.");
      return setCheckError("The squad check failed — try again later.");
    }
    load({ quiet: true });
    if (onChecked) onChecked();
  }

  const smallBtn = { background:T.surfaceHigh, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"6px 12px", color:T.text, fontSize:12, fontWeight:700, cursor:"pointer" };
  return (
    <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.radius, padding:"12px 14px", marginBottom:14 }}>
      <div style={{ fontWeight:800, fontSize:13 }}>Squad changes from Rugby League Project</div>
      <div style={{ fontSize:12, color:T.textMuted, marginTop:4 }}>Checked every weekend. Move applies the player's season from Rugby League Project; Ignore hides this change.</div>
      {loadState === "ready" && list && (
        <div style={{ fontSize:12, color:T.textMuted, marginTop:4 }}>{list.lastRunAt ? `Last successful check: ${driftInstantLabel(list.lastRunAt)}` : "No successful check yet"}</div>
      )}
      {replayMode && <div style={{ fontSize:12, color:T.textMuted, marginTop:4 }}>Real season data — not the replay clock.</div>}
      <div style={{ marginTop:8 }}>
        <button onClick={checkNow} disabled={checking} style={{ ...smallBtn, opacity:checking?0.6:1, cursor:checking?"default":"pointer" }}>{checking ? "Checking…" : "Check now"}</button>
      </div>
      {checkError && <div role="alert" style={{ color:T.red, fontSize:12, marginTop:8 }}>{checkError}</div>}
      {message && <div role={message.role} style={{ color:message.role === "alert" ? T.red : T.accentDark, fontSize:12, marginTop:8 }}>{message.text}</div>}
      {loadState === "loading" && <div style={{ color:T.textMuted, fontSize:12, marginTop:10 }}>Loading squad changes…</div>}
      {loadState === "error" && (
        <div style={{ color:T.textMuted, fontSize:12, marginTop:10 }}>
          <div>Couldn't load squad changes — try again.</div>
          <button onClick={() => load()} style={{ ...smallBtn, marginTop:6 }}>Retry</button>
        </div>
      )}
      {loadState === "ready" && list && list.items.length === 0 && (
        <div style={{ color:T.textDim, fontSize:12, marginTop:10 }}>No squad changes waiting.</div>
      )}
      {loadState === "ready" && list && list.items.map(item => {
        const ours = Array.isArray(item.from_teams) && item.from_teams.length ? item.from_teams.map(t => t.name).join(" / ") : "";
        const busy = busyId === item.id;
        return (
          <div key={item.id} style={{ borderTop:`1px solid ${T.border}`, marginTop:10, paddingTop:10 }}>
            <strong style={{ fontSize:13 }}>{item.name}</strong>
            <div style={{ fontSize:12, color:T.textSubtle, marginTop:2 }}>{ours ? `Ours: ${ours}` : "Ours: Not in our squads"}</div>
            {(item.stints || []).map((s, i) => (
              <div key={i} style={{ fontSize:12, color:T.textSubtle, marginTop:2 }}>{`Rugby League Project: ${s.team_name} from ${driftDateLabel(s.first_on)}`}</div>
            ))}
            {item.kind === "new_player" && (
              <div style={{ fontSize:11, color:T.textMuted, marginTop:4 }}>Move adds the player, or links a same-name player who has no Rugby League Project id yet.</div>
            )}
            {confirming === item.id ? (
              <div style={{ marginTop:8 }}>
                <div style={{ fontSize:12, color:T.text }}>{`Replace ${item.name}'s ${list.season} squads with Rugby League Project's? This replaces any admin corrections for the season.`}</div>
                <div style={{ display:"flex", gap:8, marginTop:6 }}>
                  <button onClick={() => act(item, applySquadDrift)} disabled={busy} style={{ ...smallBtn, background:T.accent, border:"none", color:"#000", opacity:busy?0.6:1 }}>Confirm</button>
                  <button onClick={() => setConfirming(null)} disabled={busy} style={smallBtn}>Cancel</button>
                </div>
              </div>
            ) : (
              <div style={{ display:"flex", gap:8, marginTop:8 }}>
                <button onClick={() => { setMessage(null); setConfirming(item.id); }} disabled={!!busyId} style={{ ...smallBtn, background:T.accent, border:"none", color:"#000" }}>Move</button>
                <button onClick={() => act(item, ignoreSquadDrift)} disabled={!!busyId} style={smallBtn}>Ignore</button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function AdminConfirmScreen({ onBack, userId }) {
  const [queue, setQueue] = useState(null); // null = loading/error; [] = empty
  const [queueError, setQueueError] = useState(false);
  const [selected, setSelected] = useState(null); // fixtureId currently being edited
  const [step, setStep] = useState(1); // 1 = pick players, 2 = tries/milestones
  const [homePlayers, setHomePlayers] = useState(null);
  const [awayPlayers, setAwayPlayers] = useState(null);
  const [playersLoading, setPlayersLoading] = useState(false);
  const [playersError, setPlayersError] = useState(false);
  const [played, setPlayed] = useState({}); // { [playerId]: true }
  const [stats, setStats] = useState({}); // { [playerId]: { tries, goals, fieldGoals } }
  const [milestoneSel, setMilestoneSel] = useState({}); // { [playerId]: code[] } — a SET of codes per player
  const [feedNotMatched, setFeedNotMatched] = useState({ home: [], away: [] });
  const [feedUsed, setFeedUsed] = useState(false);
  const [source, setSource] = useState("manual"); // 'feed' | 'manual'
  const [milestoneCodes, setMilestoneCodes] = useState([]);
  const [milestoneCodesMissing, setMilestoneCodesMissing] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notice, setNotice] = useState("");
  const [saving, setSaving] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [releaseState, setReleaseState] = useState({}); // { [round]: { busy, msg } }
  const [showEarlier, setShowEarlier] = useState(false);
  const [fetchAllState, setFetchAllState] = useState({}); // { [round]: { busy, summary } }
  const [addForm, setAddForm] = useState({
    home: { open: false, name: "", dob: "", externalRef: "", error: "" },
    away: { open: false, name: "", dob: "", externalRef: "", error: "" },
  });
  const [matchSel, setMatchSel] = useState({}); // unresolved: { [side]: { [key]: playerId } }
  const [ambSel, setAmbSel] = useState({}); // ambiguous: { [side]: { [key]: playerId } }
  const [feedErrors, setFeedErrors] = useState({}); // { [side]: { [key]: message } } per not-matched row
  // Round 10 remediation (M5) — `selectedRef` lets an in-flight async mutation
  // (moveOtherTeamPlayer) tell, once its awaits resolve, whether the admin is
  // still on the fixture it started on; if not, its local UI writes (ticks,
  // stats, not-matched rows, errors) are dropped — a response for a fixture
  // the admin has left must never land on whatever is now open.
  // `resolvedFeedKeysRef` remembers which not-matched rows THIS session has
  // already manually resolved (matched, added or moved), fixture-scoped and
  // reset whenever the not-matched list is rebuilt from scratch (open/feed
  // load/hand entry) — a fresh pre-fill recomputed from the raw feed has no
  // way to know a row was resolved unless the resolution also stamped
  // identity, so the reconciliation below re-excludes it explicitly.
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const resolvedFeedKeysRef = useRef({ home: new Set(), away: new Set() });
  // R12/F2 (Codex review 2026-10-21): the Origin editor's 900 ms delayed close
  // holds its pending timer here so a clock reset, a selection change or an
  // unmount can cancel it instead of firing on stale state.
  const originCloseTimerRef = useRef(null);
  // R16: two-panel Origin line-up editor state. `originLineup` holds the
  // selected identities per side (home first); `originFeed` holds the
  // unresolved pre-fill rows per side (ambiguous / unmatched / ref_conflict /
  // duplicate — never ticked). `originSearch*` drives the debounced
  // `admin_search_players` call per side; `originSearchRequestRef` bumps on
  // every keystroke so a stale response never overwrites a newer one.
  const [originLineup, setOriginLineup] = useState({ home: [], away: [] });
  const [originFeed, setOriginFeed] = useState({ home: [], away: [] });
  const [originSearchQuery, setOriginSearchQuery] = useState({ home: "", away: "" });
  const [originSearchResults, setOriginSearchResults] = useState({ home: [], away: [] });
  const [originSearchLoading, setOriginSearchLoading] = useState({ home: false, away: false });
  const [originPrefilling, setOriginPrefilling] = useState(false);
  const [originFeedUsed, setOriginFeedUsed] = useState(false);
  const originSearchTimerRef = useRef({ home: null, away: null });
  const originSearchRequestRef = useRef(0); // monotonic across editors — never reset to zero
  const originSearchSideReqRef = useRef({ home: 0, away: 0 }); // per-side request counter
  // F4: scope every Origin async callback (confirmation load, pre-fill,
  // search, save) by fixture id + an editor-open token + clock generation, and
  // by the fixture's edit revision where edits can happen meanwhile. The check
  // runs BEFORE any state update, finalisers included: a stale callback does
  // nothing at all. The open token bumps on every open and on unmount, so a
  // callback from a closed or unmounted editor is always stale. The edit
  // revision is per fixture and never reset (a reopen must not make an old
  // revision look current again).
  const originOpenTokenRef = useRef(0);
  const originEditRevisionRef = useRef({}); // { [fixtureId]: n }
  // Re-render when any Origin save starts or settles (`originSavesInFlight`).
  const [, bumpOriginSaves] = useState(0);
  useEffect(() => {
    const listener = () => bumpOriginSaves(n => n + 1);
    originSaveListeners.add(listener);
    return () => { originSaveListeners.delete(listener); };
  }, []);
  // The open Origin editor's lock: null (editable) | "loading" (the saved
  // confirmation is loading) | "saving" (a save is in flight) | "saved" (saved,
  // waiting for the delayed close). While locked, search, add/remove, pick/
  // exclude, pre-fill and Confirm are disabled, so no edit can race the
  // response. Reset on every open; only a current-scope callback clears it.
  // An editor opened (or remounted) on a fixture whose save is still pending
  // is locked as "saving" until that save settles.
  const [originLockState, setOriginLock] = useState(null);
  const originLock = originLockState ?? (selected != null && originSavesInFlight.has(selected) ? "saving" : null);

  function rowKey(sel) {
    return sel?.rowKey ?? String(sel?.externalRef || sel?.name || "");
  }

  // F3: Origin drafts live in the same `skohr_admin_drafts` sessionStorage store
  // as Fetch-all prefills, keyed by fixture id. An Origin draft carries
  // `{ type:"origin", lineup, feed, source }`; a Fetch-all prefill is the raw
  // prefill response body. On open, a stored Origin draft is restored and wins
  // over admin_get_confirmation; a stored Fetch-all prefill is applied through
  // the Origin pre-fill path and becomes the fixture's Origin draft. Every
  // write goes through `updateDraftStore` and touches only its own key.
  function readOriginDraft(fixtureId) {
    const d = readDraftsFromStorage()[fixtureId];
    return isOriginDraft(d) ? d : null;
  }
  function originDraftValue(lineup, feed, feedUsed) {
    return { type: "origin", lineup, feed, source: feedUsed ? "feed" : "manual" };
  }
  function writeOriginDraft(fixtureId, lineup, feed, feedUsed) {
    updateDraftStore(all => { all[fixtureId] = originDraftValue(lineup, feed, feedUsed); return true; });
  }
  function readStoredPrefill(fixtureId) {
    const d = readDraftsFromStorage()[fixtureId];
    return isOriginDraft(d) ? null : (d || null);
  }
  const originRevision = (fixtureId) => originEditRevisionRef.current[fixtureId] || 0;
  // F3: bump the fixture's edit revision and persist its Origin draft. Called
  // by every Origin mutation (add/remove/pick/exclude/pre-fill).
  function bumpOriginEdit(fixtureId, lineup, feed, feedUsed) {
    originEditRevisionRef.current[fixtureId] = originRevision(fixtureId) + 1;
    writeOriginDraft(fixtureId, lineup, feed, feedUsed);
  }
  // True while the editor that captured (fixtureId, openToken, generation) is
  // still the one on screen.
  function originScopeCurrent(fixtureId, openToken, generation) {
    return selectedRef.current === fixtureId && originOpenTokenRef.current === openToken && generation === getClockGeneration();
  }

  // Pure derivation of a side's "not yet matched" feed rows — shared by the
  // initial prefill load and moveOtherTeamPlayer's post-write refresh (M5).
  // Stable, unique key: externalRef if present, else `name#position` (position
  // within this side's not-matched list) — keeps sibling rows from re-indexing.
  function notMatchedRowsForSide(sels) {
    let pos = 0;
    const out = [];
    (sels || []).forEach(sel => {
      if (sel.playerId && (sel.resolution === "external_ref" || sel.resolution === "name")) return;
      out.push({ ...sel, rowKey: sel.externalRef ?? `${sel.name}#${pos}` });
      pos++;
    });
    return out;
  }

  const queueRequest = useRef(0);
  useEffect(() => () => { queueRequest.current += 1; }, []);
  async function loadQueue() {
    setQueue(null);
    setQueueError(false);
    const readGeneration = getClockGeneration();
    const request = ++queueRequest.current;
    const res = await fetchConfirmationQueue();
    if (readGeneration !== getClockGeneration() || request !== queueRequest.current) return;
    if (res === null) {
      setQueueError(true);
      setQueue([]);
    } else {
      setQueue(res);
    }
  }

  async function loadNotifications() {
    const operationGeneration = getClockGeneration();
    const res = await fetchAdminNotifications();
    if (operationGeneration !== getClockGeneration()) return;
    setNotifications(Array.isArray(res) ? res : []);
  }

  async function loadMilestoneCodes() {
    const operationGeneration = getClockGeneration();
    const res = await fetchMilestoneCodes();
    if (operationGeneration !== getClockGeneration()) return;
    const codes = Array.isArray(res) ? res : [];
    setMilestoneCodes(codes);
    // A failed read (null) or an empty result means the select can't offer real
    // choices — flag it so step 2 shows a notice and keeps the select disabled
    // rather than silently empty.
    setMilestoneCodesMissing(res === null || codes.length === 0);
  }

  useEffect(() => {
    loadQueue();
    loadNotifications();
    loadMilestoneCodes();
  }, []);

  // R16 — "App time: …" header, shown only under a staging replay; also
  // re-fetches the queue on every clock sync (a jump can bring a fixture's
  // kickoff instant into view or move it back out).
  const [appClock, setAppClock] = useState(() => getAppClock());
  useEffect(() => onAppClock((payload) => { setAppClock(payload); loadQueue(); }), []);
  // R12/F2: cancel any pending Origin editor delayed close on clock sync
  // (a reset bumps the generation; the callback also re-checks it), selection
  // change and unmount so the timer cannot fire on stale state.
  useEffect(() => onAppClock(() => {
    if (originCloseTimerRef.current !== null) { clearTimeout(originCloseTimerRef.current); originCloseTimerRef.current = null; }
  }), []);
  useEffect(() => {
    return () => {
      if (originCloseTimerRef.current !== null) { clearTimeout(originCloseTimerRef.current); originCloseTimerRef.current = null; }
    };
  }, []);
  useEffect(() => {
    // Selection changed (opened another fixture, or returned to the queue) —
    // drop any pending delayed close for the previous fixture.
    if (originCloseTimerRef.current !== null) { clearTimeout(originCloseTimerRef.current); originCloseTimerRef.current = null; }
    // F5: clear any pending search debounce timers on navigation.
    for (const s of ["home", "away"]) {
      if (originSearchTimerRef.current[s]) { clearTimeout(originSearchTimerRef.current[s]); originSearchTimerRef.current[s] = null; }
    }
  }, [selected]);
  // F5: clear debounce timers on unmount so a late callback never fires. F4:
  // unmount also retires the open token, so any in-flight Origin callback
  // (confirmation load, pre-fill, search, save) from this instance is dropped.
  useEffect(() => () => {
    originOpenTokenRef.current += 1;
    for (const s of ["home", "away"]) {
      if (originSearchTimerRef.current[s]) { clearTimeout(originSearchTimerRef.current[s]); originSearchTimerRef.current[s] = null; }
    }
  }, []);

  // R11: group the queue by scoringRound (server orders scoringRound DESC,
  // kick-off ASC). Origin I (scoringRound 13) sorts inside the round-13 group.
  // Items with a null scoringRound form one extra group headed "Not in a
  // round yet", shown after the round groups and outside the three-group cap.
  // R9 absent/null rule: an ABSENT scoringRound maps to the feed round; an
  // explicit null stays null (null group). fetchConfirmationQueue always
  // returns scoringRound, so the absent fallback only covers stale mock data.
  const { roundGroups, nullGroup } = (() => {
    const list = Array.isArray(queue) ? queue : [];
    const groups = [];
    const byRound = new Map();
    let ng = null;
    for (const item of list) {
      const year = item.year ?? Number(item.matchDate?.slice(0, 4));
      const sr = item.scoringRound === undefined ? item.round : item.scoringRound;
      if (sr == null) {
        if (!ng) ng = { key: `null:${year}`, year, round: null, items: [], releasedAt: null };
        ng.items.push(item);
        continue;
      }
      const key = `${year}:${item.scoringRound ?? item.round}`;
      if (!byRound.has(key)) {
        const g = { key, year, round: item.scoringRound ?? item.round, items: [], releasedAt: item.releasedAt ?? null };
        byRound.set(key, g);
        groups.push(g);
      }
      byRound.get(key).items.push(item);
    }
    return { roundGroups: groups, nullGroup: ng };
  })();

  const entry = Array.isArray(queue) ? queue.find(q => q.id === selected) : null;

  async function openFixture(fixtureId) {
    const operationGeneration = getClockGeneration();
    setSelected(fixtureId);
    selectedRef.current = fixtureId; // F4: set synchronously so async guards work before re-render
    setStep(1);
    // R12: every open resets loading, errors and rosters.
    setPlayersLoading(false); setPlayersError(false);
    setHomePlayers([]); setAwayPlayers([]);
    setPlayed({}); setStats({}); setMilestoneSel({});
    setFeedNotMatched({ home: [], away: [] });
    resolvedFeedKeysRef.current = { home: new Set(), away: new Set() };
    setFeedUsed(false); setSource("manual"); setNotice("");
    setMatchSel({}); setAmbSel({});
    setFeedErrors({});
    setAddForm({
      home: { open: false, name: "", dob: "", externalRef: "", error: "" },
      away: { open: false, name: "", dob: "", externalRef: "", error: "" },
    });
    // R16: reset the two-panel Origin editor state on every open.
    originOpenTokenRef.current += 1; // F4: new editor-open token (retires every callback of the previous editor)
    setOriginLock(null);
    setOriginLineup({ home: [], away: [] });
    setOriginFeed({ home: [], away: [] });
    setOriginSearchQuery({ home: "", away: "" });
    setOriginSearchResults({ home: [], away: [] });
    setOriginSearchLoading({ home: false, away: false });
    setOriginPrefilling(false);
    setOriginFeedUsed(false); // F9: source starts as manual for a new editor
    setNotice("");
    // F5: never reset the shared search request counter to zero; bump the
    // per-side counter on clear. Clear any pending debounce timers.
    for (const s of ["home", "away"]) {
      if (originSearchTimerRef.current[s]) { clearTimeout(originSearchTimerRef.current[s]); originSearchTimerRef.current[s] = null; }
    }
    const fx = (Array.isArray(queue) ? queue : []).find(q => q.id === fixtureId);
    // R16: an Origin fixture opens the two-panel line-up editor. F3: a stored
    // Origin draft (manual work) is restored and wins over
    // admin_get_confirmation. With no Origin draft, a Fetch-all stored
    // pre-fill is applied through the Origin pre-fill path and replaces itself
    // in the store with the fixture's Origin draft (one write, own key only);
    // otherwise the saved confirmation is loaded with the editor locked. The
    // Origin draft is never deleted on open — only a successful save clears
    // it. F9: source is restored from the draft or the confirmation; a new
    // manual editor starts as "manual".
    if (fx?.isOrigin) {
      const openToken = originOpenTokenRef.current;
      const storedDraft = readOriginDraft(fixtureId);
      if (storedDraft) {
        setOriginLineup(storedDraft.lineup || { home: [], away: [] });
        setOriginFeed(storedDraft.feed || { home: [], away: [] });
        setOriginFeedUsed(storedDraft.source === "feed");
        return;
      }
      const storedPrefill = readStoredPrefill(fixtureId);
      if (storedPrefill) {
        const applied = applyOriginPrefillResult(storedPrefill);
        writeOriginDraft(fixtureId, applied.lineup, applied.feed, true);
        return;
      }
      // No draft and no stored pre-fill — load the saved confirmation with the
      // editor locked, so no edit can be made for the response to overwrite.
      // F4: a response for a closed/reopened/unmounted editor or an old clock
      // generation does nothing (the lock belongs to the editor that set it).
      setOriginLock("loading");
      const confRes = await fetchOriginConfirmation(fixtureId);
      if (!originScopeCurrent(fixtureId, openToken, operationGeneration)) return;
      setOriginLock(null);
      if (confRes && Array.isArray(confRes.players)) {
        const home = [], away = [];
        for (const p of confRes.players) {
          const row = {
            playerId: p.player_id,
            name: p.display_name ?? p.name ?? "",
            clubName: p.club_name ?? null,
            tries: Number(p.tries) || 0,
            goals: Number(p.goals) || 0,
            fieldGoals: Number(p.field_goals) || 0,
          };
          if (p.side === "home") home.push(row);
          else if (p.side === "away") away.push(row);
        }
        setOriginLineup({ home, away });
        setOriginFeedUsed(confRes.source === "feed"); // F9: restore source
      }
      return;
    }
    if (fx?.homeTeamId || fx?.awayTeamId) {
      setPlayersLoading(true); setPlayersError(false);
      const [h, a] = await Promise.all([
        fx.homeTeamId ? fetchTeamPlayers(fx.homeTeamId, fx.matchDate) : Promise.resolve(null),
        fx.awayTeamId ? fetchTeamPlayers(fx.awayTeamId, fx.matchDate) : Promise.resolve(null),
      ]);
      if (operationGeneration !== getClockGeneration()) return;
      setPlayersLoading(false);
      if (h === null && a === null) setPlayersError(true);
    setHomePlayers(Array.isArray(h) ? h : []);
    setAwayPlayers(Array.isArray(a) ? a : []);
    }
    // Apply ONCE on open then drop the stored draft, so re-opening an
    // un-confirmed fixture never clobbers the admin's hand edits. A fresh
    // "Fetch from RLP" still overrides. F3: read from and delete in the
    // current store, this fixture's key only.
    let stored = null;
    updateDraftStore(all => {
      const d = all[fixtureId];
      if (!d || isOriginDraft(d)) return false;
      stored = d; delete all[fixtureId];
      return true;
    });
    if (stored) applyPrefillBody(stored);
  }

  function sideForPlayer(playerId) {
    if ((homePlayers || []).some(p => p.id === playerId)) return "home";
    if ((awayPlayers || []).some(p => p.id === playerId)) return "away";
    return null;
  }

  function tickPlayer(playerId, tick) {
    setPlayed(prev => {
      const next = { ...prev };
      if (tick) next[playerId] = true; else delete next[playerId];
      return next;
    });
    if (!tick) {
      setStats(prev => { const n = { ...prev }; delete n[playerId]; return n; });
      setMilestoneSel(prev => { const n = { ...prev }; delete n[playerId]; return n; });
    }
  }

  function setStat(playerId, key, delta) {
    setStats(prev => {
      const cur = prev[playerId] || { tries: 0, goals: 0, fieldGoals: 0 };
      const nextVal = Math.max(0, (cur[key] || 0) + delta);
      return { ...prev, [playerId]: { ...cur, [key]: nextVal } };
    });
  }

  function setPlayerMilestones(playerId, codes) {
    setMilestoneSel(prev => {
      const n = { ...prev };
      const next = (Array.isArray(codes) ? codes : []).filter(Boolean);
      if (next.length === 0) delete n[playerId]; else n[playerId] = next;
      return n;
    });
  }

  // Append the date of birth (d MMM yyyy) when another listed player on the
  // same side shares the name — namesakes are told apart by DOB. "DOB unknown"
  // when the row has no date of birth.
  function playerLabel(p, side) {
    const list = side === "home" ? homePlayers : awayPlayers;
    const shared = (list || []).filter(q => q.name === p.name).length > 1;
    if (!shared) return p.name;
    if (p.dateOfBirth) {
      try {
        return `${p.name} (${new Intl.DateTimeFormat("en-AU", { day: "numeric", month: "short", year: "numeric" }).format(new Date(p.dateOfBirth + "T00:00:00"))})`;
      } catch {
        return `${p.name} (DOB unknown)`;
      }
    }
    return `${p.name} (DOB unknown)`;
  }

  function applyPrefillBody(res) {
    const draft = prefillToDraft(res);
    const newPlayed = {};
    const newStats = {};
    const newMilestones = {};
    const markMatched = (sels) => {
      (sels || []).forEach(sel => {
        if (sel.playerId && (sel.resolution === "external_ref" || sel.resolution === "name")) {
          newPlayed[sel.playerId] = true;
          newStats[sel.playerId] = { tries: sel.tries || 0, goals: sel.goals || 0, fieldGoals: sel.fieldGoals || 0 };
        }
      });
    };
    markMatched(draft.home);
    markMatched(draft.away);
    const notMatched = { home: notMatchedRowsForSide(draft.home), away: notMatchedRowsForSide(draft.away) };
    // Milestones are a SET of codes per player — the pre-fill may return
    // several codes for one player (e.g. game250 + centurion); tick every one.
    (draft.milestones || []).forEach(m => {
      if (!m.playerId) return;
      if (!newMilestones[m.playerId]) newMilestones[m.playerId] = [];
      if (!newMilestones[m.playerId].includes(m.code)) newMilestones[m.playerId].push(m.code);
    });
    setPlayed(prev => ({ ...prev, ...newPlayed }));
    setStats(prev => ({ ...prev, ...newStats }));
    setMilestoneSel(prev => ({ ...prev, ...newMilestones }));
    setFeedNotMatched(notMatched);
    // A freshly (re)built not-matched list has no manual resolutions on it yet.
    resolvedFeedKeysRef.current = { home: new Set(), away: new Set() };
    setFeedUsed(true);
    setSource("feed");
    setFeedErrors({});
    setNotice(draft.complete
      ? "Loaded from the feed — check late changes before confirming."
      : "The feed has partial data for this match — check the not-matched names and confirm.");
  }

  async function loadFromFeed() {
    const operationGeneration = getClockGeneration();
    if (!entry) return;
    setFetching(true); setNotice("");
    const res = await prefillMatch(entry.id);
    if (operationGeneration !== getClockGeneration()) return;
    setFetching(false);
    if (!res.ok) {
      if (res.code === "feed_not_ready") {
        setNotice("This match has not been published on RLP yet — try again later or enter by hand.");
      } else if (res.code === "feed_unavailable") {
        setNotice("The feed is unavailable for this match — enter by hand (the factory has been notified).");
      } else if (res.code === "no_external_ref") {
        setNotice("No feed reference for this match yet — enter the teams by hand.");
      } else if (res.code === "budget_exhausted") {
        setNotice("The feed's monthly request budget is used up — enter the teams by hand.");
      } else if (res.code === "forbidden") {
        setNotice("You don't have permission to load the feed. Sign in as an admin and try again.");
      } else if (res.code === "unauthenticated") {
        setNotice("Your session has expired. Sign in again and retry.");
      } else {
        setNotice("Couldn't reach the feed. Check your connection and try again.");
      }
      // A per-match fetch failure may have produced an admin notification —
      // refresh the banner without a remount.
      await loadNotifications();
      if (operationGeneration !== getClockGeneration()) return;
      return;
    }
    applyPrefillBody(res);
  }

  function enterByHand() {
    setFeedNotMatched({ home: [], away: [] });
    resolvedFeedKeysRef.current = { home: new Set(), away: new Set() };
    setFeedUsed(false);
    setSource("manual");
    setNotice("");
  }

  // Returns true when this side either had nothing to refresh (no team id) or
  // refreshed successfully — false only on an actual fetch failure. Round 10
  // remediation (M5): moveOtherTeamPlayer needs to tell a real refresh
  // failure apart from "nothing to do" so it can decide whether the row stays
  // unresolved.
  async function refreshTeamPlayers(side) {
    const operationGeneration = getClockGeneration();
    const teamId = side === "home" ? entry?.homeTeamId : entry?.awayTeamId;
    if (!teamId) return true;
    const res = await fetchTeamPlayers(teamId, entry?.matchDate);
    if (operationGeneration !== getClockGeneration()) return;
    if (!Array.isArray(res)) return false;
    if (side === "home") setHomePlayers(res); else setAwayPlayers(res);
    return true;
  }

  function addPlayerErrorMessage(code) {
    if (code === "namesake_requires_date_of_birth" || code === "namesake_missing_date_of_birth") {
      return "Another player has the same name — add a date of birth to tell them apart.";
    }
    if (code === "duplicate_player") {
      // Round 10 remediation (craft) — namesakes are a GLOBAL uniqueness
      // constraint (name + date of birth), not scoped to this team; the copy
      // previously implied a per-team check.
      return "A player with this name and date of birth already exists — check the list or use a different date of birth.";
    }
    if (code === "external_ref_taken") {
      return "That feed reference is already on another player — check the list or clear it.";
    }
    if (code === "forbidden") {
      return "You don't have permission to add this player. Sign in as an admin and try again.";
    }
    return "Couldn't add this player. Try again.";
  }

  // Add a player. When called from the top-of-side "Add player" form (no
  // prefill) the error shows in the open form. When called from a not-matched
  // feed row (`prefill` carries the feed row's name/dob/externalRef + tries and
  // its stable `key`), the error shows inline beside that row (never in a form
  // that can be closed), the feed row's tries/goals/fieldGoals are carried onto
  // the new player, and the not-matched row is removed only on success.
  async function submitAddPlayer(side, prefill) {
    const operationGeneration = getClockGeneration();
    const teamId = side === "home" ? entry?.homeTeamId : entry?.awayTeamId;
    if (!teamId) return;
    const fromFeed = Boolean(prefill);
    const form = prefill || addForm[side];
    const name = (form.name || "").trim();
    if (!name) {
      const msg = "Enter a name.";
      if (fromFeed) {
        const k = form.key;
        setFeedErrors(prev => ({ ...prev, [side]: { ...(prev[side] || {}), [k]: msg } }));
      } else {
        setAddForm(prev => ({ ...prev, [side]: { ...prev[side], error: msg } }));
      }
      return;
    }
    const res = await addPlayer({ teamId, name, dateOfBirth: form.dob || null, externalRef: form.externalRef || null });
    if (operationGeneration !== getClockGeneration()) return;
    if (!res.ok) {
      const msg = addPlayerErrorMessage(res.code);
      if (fromFeed) {
        setFeedErrors(prev => ({ ...prev, [side]: { ...(prev[side] || {}), [form.key]: msg } }));
      } else {
        setAddForm(prev => ({ ...prev, [side]: { ...prev[side], error: msg } }));
      }
      return;
    }
    await refreshTeamPlayers(side);
    if (operationGeneration !== getClockGeneration()) return;
    tickPlayer(res.id, true);
    if (fromFeed && (form.tries || form.goals || form.fieldGoals)) {
      setStats(prev => ({ ...prev, [res.id]: { tries: form.tries || 0, goals: form.goals || 0, fieldGoals: form.fieldGoals || 0 } }));
    }
    if (fromFeed) {
      const k = form.key;
      resolvedFeedKeysRef.current[side].add(k);
      setFeedNotMatched(prev => ({ ...prev, [side]: (prev[side] || []).filter(s => rowKey(s) !== k) }));
      setFeedErrors(prev => {
        const next = { ...(prev[side] || {}) };
        delete next[k];
        return { ...prev, [side]: next };
      });
      setMatchSel(prev => {
        const next = { ...(prev[side] || {}) };
        delete next[k];
        return { ...prev, [side]: next };
      });
      setAmbSel(prev => {
        const next = { ...(prev[side] || {}) };
        delete next[k];
        return { ...prev, [side]: next };
      });
    } else {
      setAddForm(prev => ({ ...prev, [side]: { open: false, name: "", dob: "", externalRef: "", error: "" } }));
    }
  }

  async function stampOnMatch(side, playerId, sel) {
    const operationGeneration = getClockGeneration();
    const list = side === "home" ? homePlayers : awayPlayers;
    const player = (list || []).find(p => p.id === playerId);
    if (!player) return { ok: true };
    const mergedDob = player.dateOfBirth ?? (sel.dateOfBirth || null);
    const mergedRef = player.externalRef ?? (sel.externalRef || null);
    const needDob = !player.dateOfBirth && Boolean(sel.dateOfBirth);
    const needRef = !player.externalRef && Boolean(sel.externalRef);
    if (!needDob && !needRef) return { ok: true };
    const res = await stampPlayerIdentity({
      playerId,
      dateOfBirth: mergedDob,
      externalRef: mergedRef,
    });
    if (operationGeneration !== getClockGeneration()) return;
    if (!res.ok) return res;
    await refreshTeamPlayers(side);
    if (operationGeneration !== getClockGeneration()) return;
    return res;
  }

  async function matchFeedName(side, key, sel) {
    const operationGeneration = getClockGeneration();
    const playerId = (matchSel[side] || {})[key];
    if (!playerId) return;
    const stampRes = await stampOnMatch(side, playerId, sel);
    if (operationGeneration !== getClockGeneration()) return;
    if (stampRes && !stampRes.ok) {
      setFeedErrors(prev => ({ ...prev, [side]: { ...(prev[side] || {}), [key]: `Couldn't stamp this match — ${squadErrorCopy(stampRes.code)} The row stays unresolved.` } }));
      return;
    }
    tickPlayer(playerId, true);
    setStats(prev => ({ ...prev, [playerId]: { tries: sel.tries || 0, goals: sel.goals || 0, fieldGoals: sel.fieldGoals || 0 } }));
    resolvedFeedKeysRef.current[side].add(key);
    setFeedNotMatched(prev => ({ ...prev, [side]: (prev[side] || []).filter(s => rowKey(s) !== key) }));
    setFeedErrors(prev => {
      const next = { ...(prev[side] || {}) };
      delete next[key];
      return { ...prev, [side]: next };
    });
    setMatchSel(prev => {
      const n = { ...(prev[side] || {}) };
      delete n[key];
      return { ...prev, [side]: n };
    });
  }

  async function resolveAmbiguous(side, key, sel) {
    const operationGeneration = getClockGeneration();
    const playerId = (ambSel[side] || {})[key];
    if (!playerId) return;
    const stampRes = await stampOnMatch(side, playerId, sel);
    if (operationGeneration !== getClockGeneration()) return;
    if (stampRes && !stampRes.ok) {
      setFeedErrors(prev => ({ ...prev, [side]: { ...(prev[side] || {}), [key]: `Couldn't stamp this match — ${squadErrorCopy(stampRes.code)} The row stays unresolved.` } }));
      return;
    }
    tickPlayer(playerId, true);
    setStats(prev => ({ ...prev, [playerId]: { tries: sel.tries || 0, goals: sel.goals || 0, fieldGoals: sel.fieldGoals || 0 } }));
    resolvedFeedKeysRef.current[side].add(key);
    setFeedNotMatched(prev => ({ ...prev, [side]: (prev[side] || []).filter(s => rowKey(s) !== key) }));
    setFeedErrors(prev => {
      const next = { ...(prev[side] || {}) };
      delete next[key];
      return { ...prev, [side]: next };
    });
    setAmbSel(prev => {
      const n = { ...(prev[side] || {}) };
      delete n[key];
      return { ...prev, [side]: n };
    });
  }

  // R31: an 'other_team' feed row is a player our squads have on another
  // team. `effective` is the fixture's match_date ("from this match") or null
  // ("whole season — the squad was wrong"). A player with no membership this
  // season (currentTeamId null) is added rather than moved.
  //
  // Round 10 remediation (M5): a move can affect either team fielded in THIS
  // fixture (the player may have come from the other side), so both dated
  // rosters are refreshed, plus the pre-fill (a fresh not-matched list for
  // this side — other rows' resolution can change too). Any refresh failure
  // keeps the row unresolved with a copy error rather than ticking it on an
  // uncertain result; every error routes through squadErrorCopy so a raw
  // machine code is never shown. Every local write is scoped to the fixture
  // open when the move started (`selectedRef`) — a response for a fixture the
  // admin has since left is dropped rather than landing on whatever fixture
  // is open now. The fresh not-matched list is reconciled against rows this
  // session has already resolved (`resolvedFeedKeysRef`) rather than replacing
  // the side wholesale, so an unrelated move can't resurrect an already
  // matched/added row the raw feed still shows as unresolved.
  async function moveOtherTeamPlayer(side, key, sel, effective) {
    const operationGeneration = getClockGeneration();
    const teamId = side === "home" ? entry?.homeTeamId : entry?.awayTeamId;
    if (!teamId || !entry?.matchDate) return;
    const fixtureId = entry.id;
    const season = new Date(entry.matchDate + "T00:00:00Z").getUTCFullYear();
    const res = sel.currentTeamId
      ? await squadsAdminMovePlayer(sel.playerId, teamId, season, effective)
      : await squadsAdminAddToSquad(sel.playerId, teamId, season, effective);
      if (operationGeneration !== getClockGeneration()) return;
    if (selectedRef.current !== fixtureId) return; // the admin has left this fixture
    if (!res.ok) {
      setFeedErrors(prev => ({ ...prev, [side]: { ...(prev[side] || {}), [key]: `Couldn't update this player's squad — ${squadErrorCopy(res.code)}` } }));
      return;
    }
    const [homeOk, awayOk, prefillRes] = await Promise.all([
      refreshTeamPlayers("home"),
      refreshTeamPlayers("away"),
      prefillMatch(fixtureId),
    ]);
    if (operationGeneration !== getClockGeneration()) return;
    if (selectedRef.current !== fixtureId) return; // left again during the refresh
    if (!homeOk || !awayOk || !prefillRes.ok) {
      setFeedErrors(prev => ({ ...prev, [side]: { ...(prev[side] || {}), [key]: "Moved, but the roster couldn't be refreshed — reload this match before confirming." } }));
      return;
    }
    tickPlayer(sel.playerId, true);
    setStats(prev => ({ ...prev, [sel.playerId]: { tries: sel.tries || 0, goals: sel.goals || 0, fieldGoals: sel.fieldGoals || 0 } }));
    resolvedFeedKeysRef.current[side].add(key);
    const freshDraft = prefillToDraft(prefillRes);
    const freshRows = notMatchedRowsForSide(side === "home" ? freshDraft.home : freshDraft.away);
    const resolved = resolvedFeedKeysRef.current[side];
    setFeedNotMatched(prev => ({ ...prev, [side]: freshRows.filter(r => !resolved.has(rowKey(r))) }));
    setFeedErrors(prev => {
      const next = { ...(prev[side] || {}) };
      delete next[key];
      return { ...prev, [side]: next };
    });
  }

  async function dismissGroup(rows) {
    const operationGeneration = getClockGeneration();
    // Acknowledge every row in the group sequentially; remove each on success.
    for (const n of rows) {
      const res = await acknowledgeNotification(n.id);
      if (operationGeneration !== getClockGeneration()) return;
      if (res.ok) setNotifications(prev => prev.filter(x => x.id !== n.id));
    }
  }

  function goToStep2() {
    const ticked = Object.keys(played);
    if (ticked.length === 0) {
      setNotice("Add at least one player before moving on.");
      return;
    }
    const nextStats = { ...stats };
    ticked.forEach(pid => { if (!nextStats[pid]) nextStats[pid] = { tries: 0, goals: 0, fieldGoals: 0 }; });
    setStats(nextStats);
    setNotice("");
    setStep(2);
  }

  async function handleConfirm() {
    const operationGeneration = getClockGeneration();
    if (!entry) return;
    setSaving(true); setNotice("");
    const ticked = Object.keys(played);
    const players = ticked.map(pid => {
      const side = sideForPlayer(pid) || "home";
      const s = stats[pid] || { tries: 0, goals: 0, fieldGoals: 0 };
      return { player_id: pid, side, tries: s.tries || 0, goals: s.goals || 0, field_goals: s.fieldGoals || 0 };
    });
    const milestones = [];
    ticked.forEach(pid => {
      (milestoneSel[pid] || []).forEach(code => milestones.push({ player_id: pid, milestone_code: code }));
    });
    const res = await saveMatchConfirmation({ fixtureId: entry.id, players, milestones, source: feedUsed ? "feed" : "manual" });
    if (operationGeneration !== getClockGeneration()) return;
    setSaving(false);
    if (res.ok) {
      // Clear any stored draft for this fixture now that it's confirmed (F3:
      // this fixture's key only, in the current store).
      updateDraftStore(all => (entry.id in all ? delete all[entry.id] : false));
      setNotice("Confirmed — this match is saved for the round release.");
      await loadQueue();
      if (operationGeneration !== getClockGeneration()) return;
      setTimeout(() => { setSelected(null); setNotice(""); }, 900);
    } else if (res.code === "forbidden") {
      setNotice("You don't have permission to confirm this match. Sign in as an admin and try again.");
    } else {
      // token error → show the message and stay on the step
      setNotice(`Couldn't save this confirmation (${res.code}). Check the lineup and try again.`);
    }
  }

  // R16: confirm an Origin fixture's two-sided line-up. Saves with explicit
  // sides (never default-to-home), milestones:[] (Origin milestones are
  // refused — `origin_no_milestones`), and source from the feed flag.
  // Double-submit protection: the editor is locked ("saving") while the save
  // is in flight, and a per-fixture in-flight set refuses a second save for
  // the same fixture even from a reopened editor. F4: the response is scoped
  // by fixture id + open token + clock generation + the fixture's edit
  // revision at submission:
  // - success clears ONLY this fixture's draft, and only if the clock
  //   generation is unchanged and no edit was made after submission (e.g. by
  //   reopening the fixture); notice, queue reload and delayed close run only
  //   in the editor that submitted;
  // - a refusal is shown only by the editor that submitted, never under
  //   another fixture or a reopened editor.
  // The delayed close re-checks fixture + generation inside its callback and
  // is cancelled on clock reset, selection change and unmount (F2 — Codex
  // review 2026-10-21). Origin error tokens (`origin_lineup_size`,
  // `origin_no_milestones`, `bad_fixture_sides`) have explicit messages; the
  // PostgREST `.details` (`{"home":n,"away":n}`) is preserved through
  // rpcErrorResult so the counts are recoverable.
  async function handleConfirmOrigin() {
    const operationGeneration = getClockGeneration();
    if (!entry || originLock) return;
    const startingFixtureId = entry.id;
    const openToken = originOpenTokenRef.current;
    if (originSavesInFlight.has(startingFixtureId)) {
      setNotice("This line-up is still saving — wait a moment and check the queue.");
      return;
    }
    const editRevision = originRevision(startingFixtureId);
    setOriginSaveInFlight(startingFixtureId, true);
    setOriginLock("saving"); setNotice("");
    const players = [
      ...originLineup.home.map(p => ({ player_id: p.playerId, side: "home", tries: Number(p.tries) || 0, goals: Number(p.goals) || 0, field_goals: Number(p.fieldGoals) || 0 })),
      ...originLineup.away.map(p => ({ player_id: p.playerId, side: "away", tries: Number(p.tries) || 0, goals: Number(p.goals) || 0, field_goals: Number(p.fieldGoals) || 0 })),
    ];
    let res;
    try {
      res = await saveMatchConfirmation({
        fixtureId: startingFixtureId,
        players,
        milestones: [],
        source: originFeedUsed ? "feed" : "manual",
      });
    } finally {
      setOriginSaveInFlight(startingFixtureId, false);
    }
    if (operationGeneration !== getClockGeneration()) return;
    if (res.ok && originRevision(startingFixtureId) === editRevision) {
      updateDraftStore(all => (startingFixtureId in all ? delete all[startingFixtureId] : false));
    }
    if (!originScopeCurrent(startingFixtureId, openToken, operationGeneration)) return;
    if (res.ok) {
      setOriginLock("saved"); // stays locked until the delayed close
      setNotice("Confirmed — this match is saved for the round release.");
      await loadQueue();
      if (!originScopeCurrent(startingFixtureId, openToken, operationGeneration)) return;
      if (originCloseTimerRef.current !== null) { clearTimeout(originCloseTimerRef.current); originCloseTimerRef.current = null; }
      originCloseTimerRef.current = setTimeout(() => {
        originCloseTimerRef.current = null;
        if (originScopeCurrent(startingFixtureId, openToken, operationGeneration)) {
          setSelected(null); setNotice("");
        }
      }, 900);
      return;
    }
    setOriginLock(null);
    if (res.code === "forbidden") {
      setNotice("You don't have permission to confirm this match. Sign in as an admin and try again.");
    } else if (res.code === "origin_lineup_size") {
      const d = res.details;
      let counts = "";
      if (d) { try { const o = typeof d === "string" ? JSON.parse(d) : d; counts = ` (${o.home} home, ${o.away} away)`; } catch { /* ignore */ } }
      setNotice(`Each side needs 13–19 players${counts}.`);
    } else if (res.code === "origin_no_milestones") {
      setNotice("State of Origin milestones aren't recorded — remove them and try again.");
    } else if (res.code === "bad_fixture_sides") {
      setNotice("This fixture's sides aren't set up correctly. Refresh the queue and try again.");
    } else if (res.code === "orientation_mismatch") {
      setNotice("The feed's team names don't match the fixture's sides — enter the line-up by hand.");
    } else {
      setNotice(`Couldn't save this confirmation (${res.code}). Try again.`);
    }
  }

  // R16: the Origin search is debounced. F5: the per-side request counter
  // bumps on every keystroke (in `onOriginSearchInput`), clear and add — NOT in
  // the delayed request — so a stale response (a slower earlier query returning
  // after a newer one) is dropped before the debounce fires. The shared
  // `originSearchRequestRef` is monotonic and never reset to zero on open.
  // F4: every search callback is scoped by fixture id + editor-open token +
  // clock generation, checked before any state update. `p_on` is the fixture's
  // match_date so the club label reflects who was squaded that day.
  async function runOriginSearch(side, query, fixtureId, openToken, request, generation) {
    const current = () => request === originSearchSideReqRef.current[side] && originScopeCurrent(fixtureId, openToken, generation);
    if (!current()) return;
    if (!query || !query.trim()) {
      setOriginSearchResults(prev => ({ ...prev, [side]: [] }));
      setOriginSearchLoading(prev => ({ ...prev, [side]: false }));
      return;
    }
    setOriginSearchLoading(prev => ({ ...prev, [side]: true }));
    const data = await searchOriginPlayers(query, entry?.matchDate);
    if (!current()) return; // F5: newer keystroke; F4: wrong fixture / stale editor / clock reset
    setOriginSearchLoading(prev => ({ ...prev, [side]: false }));
    setOriginSearchResults(prev => ({ ...prev, [side]: data }));
  }

  function onOriginSearchInput(side, value) {
    setOriginSearchQuery(prev => ({ ...prev, [side]: value }));
    // F5: bump the per-side request counter on every keystroke so a stale
    // response is invalidated at input time, before the next debounce fires.
    const request = ++originSearchSideReqRef.current[side];
    if (originSearchTimerRef.current[side]) {
      clearTimeout(originSearchTimerRef.current[side]);
      originSearchTimerRef.current[side] = null;
    }
    const fixtureId = selectedRef.current;
    const openToken = originOpenTokenRef.current;
    const generation = getClockGeneration();
    originSearchTimerRef.current[side] = setTimeout(() => {
      originSearchTimerRef.current[side] = null;
      runOriginSearch(side, value, fixtureId, openToken, request, generation);
    }, 300);
  }

  // F6: turn a prefill response into the Origin editor's lineup + feed state.
  // Matched rows (playerId set, resolution external_ref|name) are ticked on
  // their mapped side carrying the dated club label (F2); every other row is
  // listed (never ticked) with a unique, stable occurrence id (side + index at
  // pre-fill time) so duplicate feed rows can be resolved/excluded individually.
  // F7: each feed row carries its parsed tries/goals/fieldGoals so a picked
  // candidate keeps those counts. Returns the computed { lineup, feed } for
  // persistence by the caller.
  function applyOriginPrefillResult(res) {
    const draft = prefillToDraft(res);
    const home = [], away = [], feedHome = [], feedAway = [];
    for (let i = 0; i < (draft.home || []).length; i++) {
      const sel = draft.home[i];
      if (sel.playerId && (sel.resolution === "external_ref" || sel.resolution === "name")) {
        home.push({ playerId: sel.playerId, name: sel.name, clubName: sel.clubName ?? null, tries: sel.tries || 0, goals: sel.goals || 0, fieldGoals: sel.fieldGoals || 0 });
      } else {
        feedHome.push({ ...sel, occurrenceId: `home-${i}` });
      }
    }
    for (let i = 0; i < (draft.away || []).length; i++) {
      const sel = draft.away[i];
      if (sel.playerId && (sel.resolution === "external_ref" || sel.resolution === "name")) {
        away.push({ playerId: sel.playerId, name: sel.name, clubName: sel.clubName ?? null, tries: sel.tries || 0, goals: sel.goals || 0, fieldGoals: sel.fieldGoals || 0 });
      } else {
        feedAway.push({ ...sel, occurrenceId: `away-${i}` });
      }
    }
    setOriginLineup({ home, away });
    setOriginFeed({ home: feedHome, away: feedAway });
    setOriginFeedUsed(true);
    setOriginPrefilling(false);
    setNotice(draft.complete ? "Loaded from the feed — check late changes before confirming." : "The feed has partial data for this match — check the not-matched names and confirm.");
    return { lineup: { home, away }, feed: { home: feedHome, away: feedAway } };
  }

  function originAddPlayer(side, player) {
    if (originLock) return;
    const fixtureId = selectedRef.current;
    const list = originLineup[side] || [];
    if (list.some(p => p.playerId === player.id)) return;
    const newLineup = { ...originLineup, [side]: [...list, { playerId: player.id, name: player.display_name, clubName: player.club_name ?? null, tries: 0, goals: 0, fieldGoals: 0 }] };
    setOriginLineup(newLineup);
    setOriginSearchQuery(prev => ({ ...prev, [side]: "" }));
    setOriginSearchResults(prev => ({ ...prev, [side]: [] }));
    // F5: bump the per-side search counter on add (clears the results).
    ++originSearchSideReqRef.current[side];
    if (fixtureId) bumpOriginEdit(fixtureId, newLineup, originFeed, originFeedUsed);
  }

  function originRemovePlayer(side, playerId) {
    if (originLock) return;
    const fixtureId = selectedRef.current;
    const newLineup = { ...originLineup, [side]: (originLineup[side] || []).filter(p => p.playerId !== playerId) };
    setOriginLineup(newLineup);
    if (fixtureId) bumpOriginEdit(fixtureId, newLineup, originFeed, originFeedUsed);
  }

  // R16: pre-fill from Rugby League Project. F4: the callback is scoped by
  // fixture id + editor-open token + clock generation + draft edit revision.
  // A pre-fill that resolves after the admin edited the line-up during the
  // fetch is discarded with a short notice (the line-up changed while fetching).
  // Editing stays available while a pre-fill is in flight. F8:
  // `orientation_mismatch` shows the explicit message (the draft is kept and
  // manual entry stays available).
  async function originPrefill() {
    const operationGeneration = getClockGeneration();
    if (!entry) return;
    const fixtureId = entry.id;
    if (originLock || originPrefilling) return;
    const openToken = originOpenTokenRef.current;
    const editRevision = originRevision(fixtureId);
    setOriginPrefilling(true); setNotice("");
    const res = await prefillMatch(fixtureId);
    // F4: left the fixture / stale or unmounted editor / clock reset — do
    // nothing at all (not even the finaliser: that flag now belongs to the
    // newer editor, which may have its own pre-fill in flight).
    if (!originScopeCurrent(fixtureId, openToken, operationGeneration)) return;
    if (!res.ok) {
      setOriginPrefilling(false);
      if (res.code === "orientation_mismatch") {
        // F8: show the explicit message; the draft is kept and manual entry
        // stays available.
        setNotice("The feed's team names don't match the fixture's sides — enter the line-up by hand.");
        return;
      }
      if (res.code === "feed_not_ready") setNotice("This match has not been published on RLP yet — try again later or enter by hand.");
      else if (res.code === "feed_unavailable") setNotice("The feed is unavailable for this match — enter by hand.");
      else if (res.code === "no_external_ref") setNotice("No feed reference for this match yet — enter by hand.");
      else if (res.code === "forbidden") setNotice("You don't have permission to load the feed. Sign in as an admin and try again.");
      else if (res.code === "unauthenticated") setNotice("Your session has expired. Sign in again and retry.");
      else setNotice("Couldn't reach the feed. Check your connection and try again.");
      return;
    }
    // F4: if the admin edited the line-up during the fetch, discard the
    // pre-fill and show a notice instead of clobbering those edits.
    if (editRevision !== originRevision(fixtureId)) {
      setOriginPrefilling(false);
      setNotice("The line-up changed while fetching — pre-fill again if you want the feed's version.");
      return;
    }
    const applied = applyOriginPrefillResult(res);
    bumpOriginEdit(fixtureId, applied.lineup, applied.feed, true);
  }

  // F6: Exclude/Pick act on the single occurrence only (by occurrenceId).
  function originExcludeFeed(side, occId) {
    if (originLock) return;
    const fixtureId = selectedRef.current;
    const newFeed = { ...originFeed, [side]: (originFeed[side] || []).filter(r => r.occurrenceId !== occId) };
    setOriginFeed(newFeed);
    if (fixtureId) bumpOriginEdit(fixtureId, originLineup, newFeed, originFeedUsed);
  }

  function originPickCandidate(side, occId, candidate) {
    if (originLock) return;
    const fixtureId = selectedRef.current;
    const feedRow = (originFeed[side] || []).find(r => r.occurrenceId === occId);
    // F7: keep the feed row's parsed tries/goals/fieldGoals and show the
    // candidate's club label.
    const tries = feedRow ? (feedRow.tries || 0) : 0;
    const goals = feedRow ? (feedRow.goals || 0) : 0;
    const fieldGoals = feedRow ? (feedRow.fieldGoals || 0) : 0;
    const list = originLineup[side] || [];
    if (list.some(p => p.playerId === candidate.playerId)) return;
    const newLineup = { ...originLineup, [side]: [...list, { playerId: candidate.playerId, name: candidate.name, clubName: candidate.clubName ?? null, tries, goals, fieldGoals }] };
    const newFeed = { ...originFeed, [side]: (originFeed[side] || []).filter(r => r.occurrenceId !== occId) };
    setOriginLineup(newLineup);
    setOriginFeed(newFeed);
    if (fixtureId) bumpOriginEdit(fixtureId, newLineup, newFeed, originFeedUsed);
  }

  // R16: Confirm is enabled only when both sides hold 13–19 and no listed feed
  // row is unresolved. A feed row is resolved when it's been picked (candidate
  // joined the side) or excluded (removed from the feed list).
  const originHomeCount = originLineup.home.length;
  const originAwayCount = originLineup.away.length;
  const originFeedOpen = (originFeed.home.length + originFeed.away.length) > 0;
  const originSideValid = (n) => n >= 13 && n <= 19;
  const originConfirmDisabled = !originSideValid(originHomeCount) || !originSideValid(originAwayCount) || originFeedOpen || !!originLock;

  async function handleRelease(roundGroup) {
    const operationGeneration = getClockGeneration();
    const round = roundGroup.round;
    const key = roundGroup.key;
    const season = new Date(roundGroup.items[0].matchDate + "T00:00:00Z").getUTCFullYear();
    setReleaseState(prev => ({ ...prev, [key]: { busy: true, msg: "" } }));
    const res = await releaseRound({ season, round });
    if (operationGeneration !== getClockGeneration()) return;
    if (res.ok) {
      setReleaseState(prev => ({ ...prev, [key]: { busy: false, msg: `Round ${round} released — ${res.released} XP entries now count` } }));
      await loadQueue();
      if (operationGeneration !== getClockGeneration()) return;
    } else if (res.code === "matches_unconfirmed") {
      const names = (res.fixtures || []).map(f => `${f.home_ref} v ${f.away_ref}`).join(", ");
      setReleaseState(prev => ({ ...prev, [key]: { busy: false, msg: `Confirm every match in this round first — still unconfirmed: ${names}` } }));
    } else if (res.code === "origin_lineup_incomplete") {
      // R5: both State of Origin line-ups must be complete before release.
      // Names each incomplete Origin game and both per-side counts; several
      // games joined by "; ".
      const ROMAN = { 1: "I", 2: "II", 3: "III" };
      const games = (res.fixtures || []).map(f => {
        const ordinal = ROMAN[f.origin_game] ?? String(f.origin_game ?? "");
        return `State of Origin ${ordinal} (${f.home_ref} ${f.home_count}, ${f.away_ref} ${f.away_count})`;
      }).join("; ");
      setReleaseState(prev => ({ ...prev, [key]: { busy: false, msg: `Confirm both State of Origin line-ups first — ${games}` } }));
    } else if (res.code === "round_not_finished") {
      // R5a: the finish-gate message names the release time in the FINAL
      // fixture's venue timezone (en-AU parts, lower-case am/pm, no comma
      // after the weekday). `releasable_at` is max kick-off + 3 h; the
      // home/away refs are the final fixture's.
      const fixtures = res.fixtures || [];
      const finalFx = fixtures.find(f => f.id === res.final_fixture_id) || fixtures[0] || null;
      let releaseTime = "";
      if (finalFx && res.releasable_at) {
        try {
          const tz = finalFx.venue_timezone;
          const raw = new Intl.DateTimeFormat("en-AU", {
            weekday: "short", day: "numeric", month: "short",
            hour: "numeric", minute: "2-digit", hour12: true,
            ...(tz ? { timeZone: tz } : {}),
          }).format(new Date(res.releasable_at));
          // en-AU inserts a comma after the weekday; the contract drops it
          // ("no comma after the weekday" — "Fri 29 May, 10:50 pm").
          releaseTime = raw.replace(/^(\w+),\s/, "$1 ");
        } catch { releaseTime = ""; }
      }
      const teams = finalFx ? `${finalFx.home_ref} v ${finalFx.away_ref}` : "the last game";
      setReleaseState(prev => ({ ...prev, [key]: { busy: false, msg: `Round ${round} can be released from ${releaseTime} — 3 hours after ${teams} kicked off.` } }));
    } else if (res.code === "forbidden") {
      setReleaseState(prev => ({ ...prev, [key]: { busy: false, msg: "You don't have permission to release this round. Sign in as an admin and try again." } }));
    } else if (res.code === "unauthenticated") {
      setReleaseState(prev => ({ ...prev, [key]: { busy: false, msg: "Your session has expired. Sign in again and retry." } }));
    } else if (res.code === "round_not_found") {
      setReleaseState(prev => ({ ...prev, [key]: { busy: false, msg: "This round wasn't found on the server. Refresh the queue and try again." } }));
    } else if (res.code === "budget_exhausted") {
      setReleaseState(prev => ({ ...prev, [key]: { busy: false, msg: "The feed's monthly request budget is used up — try again next month or confirm by hand." } }));
    } else {
      setReleaseState(prev => ({ ...prev, [key]: { busy: false, msg: "Couldn't release this round. Check your connection and try again." } }));
    }
  }

  async function handleFetchAll(group) {
    const operationGeneration = getClockGeneration();
    const round = group.round;
    const key = group.key;
    const season = new Date(group.items[0].matchDate + "T00:00:00Z").getUTCFullYear();
    setFetchAllState(prev => ({ ...prev, [key]: { busy: true, summary: "" } }));
    const syncRes = await syncRound({ season, round });
    if (operationGeneration !== getClockGeneration()) return;
    if (!syncRes.ok) {
      setFetchAllState(prev => ({ ...prev, [key]: { busy: false, summary: `Sync failed (${syncRes.code}) — stopped before fetching any match.` } }));
      return;
    }
    const fresh = await fetchConfirmationQueue();
    if (operationGeneration !== getClockGeneration()) return;
    if (!Array.isArray(fresh)) {
      // The queue reload after sync failed — stop and say so; never fall back
      // to the stale list silently.
      setFetchAllState(prev => ({ ...prev, [key]: { busy: false, summary: "Synced, but the list could not be refreshed — reload and try again" } }));
      await loadNotifications();
      if (operationGeneration !== getClockGeneration()) return;
      return;
    }
    setQueue(fresh);
    setQueueError(false);
    // R11: filter the fresh queue by scoringRound (equal to round for regular
    // fixtures); syncRound still syncs the feed round above. The absent-only
    // fallback (scoringRound === undefined ? round : scoringRound, matching the
    // queue grouping) keeps stale mock data grouped by round; an explicit
    // null never matches a release group.
    const items = fresh.filter(it => (it.scoringRound === undefined ? it.round : it.scoringRound) === round && Number(it.matchDate.slice(0, 4)) === season);
    let fetched = 0, notPublished = 0, unavailable = 0;
    for (const item of items) {
      if (item.confirmed || !item.externalRef) continue;
      // F3: an Origin draft (the admin's work) is never overwritten by a
      // Fetch-all response. Skipping up front only saves a feed request; the
      // binding decision is made again at write time, after the await,
      // against the CURRENT store, because the admin may have opened and
      // edited the fixture while the request was in flight. The response is
      // only stored (this fixture's key) — it never touches an open editor; an
      // Origin with no draft applies it on its next open through the Origin
      // pre-fill path. Club fixtures have no Origin draft.
      if (item.isOrigin && readOriginDraft(item.id)) continue;
      const res = await prefillMatch(item.id);
      if (operationGeneration !== getClockGeneration()) return;
      if (res.ok) {
        updateDraftStore(all => {
          if (item.isOrigin && isOriginDraft(all[item.id])) return false;
          all[item.id] = res;
          return true;
        });
        fetched++;
      } else if (res.code === "feed_not_ready") {
        notPublished++;
      } else {
        unavailable++;
      }
    }
    setFetchAllState(prev => ({ ...prev, [key]: { busy: false, summary: `${fetched} fetched · ${notPublished} not yet published on RLP · ${unavailable} unavailable` } }));
    // Failures may have produced admin notifications — refresh the banner.
    await loadNotifications();
    if (operationGeneration !== getClockGeneration()) return;
  }

  function fixtureDateLabel(item) {
    try {
      return new Intl.DateTimeFormat("en-AU", { weekday: "short", day: "numeric", month: "short" })
        .format(new Date(item.matchDate + "T00:00:00Z"));
    } catch {
      return item.matchDate;
    }
  }

  function confirmedDateLabel(item) {
    const iso = item?.confirmation?.confirmedAt;
    if (!iso) return "";
    try {
      return new Intl.DateTimeFormat("en-AU", { weekday: "short", day: "numeric", month: "short" })
        .format(new Date(iso));
    } catch {
      return iso;
    }
  }

  function releasedDateLabel(group) {
    const iso = group?.releasedAt;
    if (!iso) return "";
    try {
      return new Intl.DateTimeFormat("en-AU", { weekday: "short", day: "numeric", month: "short" })
        .format(new Date(iso));
    } catch {
      return iso;
    }
  }

  // ── BANNER ── unacknowledged admin notifications at the top of both views.
  // Grouped by (kind, seasonYear, round): one compact card per group — heading,
  // the affected matches by name (fixture_id resolved against the queue items,
  // falling back to the payload's external_ref), the distinct reasons, and a
  // `Dismiss all` button that acknowledges every row in the group sequentially.
  function renderBanner() {
    if (!notifications.length) return null;
    const groups = new Map();
    for (const n of notifications) {
      const key = `${n.kind}|${n.seasonYear ?? ""}|${n.round ?? ""}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(n);
    }
    return Array.from(groups.entries()).map(([key, rows]) => {
      const first = rows[0];
      const round = first.round;
      const heading = first.kind === "feed_unavailable"
        ? `Feed pre-fill unavailable — round ${round}`
        : (first.message || "Admin notification");
      const matchNames = Array.from(new Set(rows.map(n => {
        const fx = (Array.isArray(queue) ? queue : []).find(q => q.id === n.fixtureId);
        if (fx) return `${fx.homeRef} v ${fx.awayRef}`;
        return n.payload?.external_ref ? `external ref ${n.payload.external_ref}` : null;
      }).filter(Boolean)));
      const reasons = Array.from(new Set(rows.map(n => n.payload?.reason).filter(Boolean)));
      return (
        <div key={key} style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.radius, padding:"12px 14px", marginBottom:10 }}>
          <div style={{ fontWeight:800, fontSize:13 }}>{heading}</div>
          {matchNames.length > 0 && <div style={{ fontSize:12, color:T.textMuted, marginTop:4 }}>{matchNames.join(" · ")}</div>}
          {reasons.length > 0 && <div style={{ fontSize:11, color:T.textDim, marginTop:4 }}>Reason: {reasons.join(" · ")}</div>}
          <button onClick={()=>dismissGroup(rows)} style={{ marginTop:8, background:T.surfaceHigh, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"6px 12px", color:T.text, fontSize:12, fontWeight:700, cursor:"pointer" }}>Dismiss all</button>
        </div>
      );
    });
  }

  function renderSide(side, teamRef, players) {
    const form = addForm[side];
    return (
      <div style={{ marginBottom:16 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
          <div style={{ fontSize:11, fontWeight:700, color:T.textDim, letterSpacing:1 }}><TeamName team={teamRef}>{String(teamRef||"").toUpperCase()}</TeamName> — PLAYERS WHO PLAYED</div>
          <button onClick={()=>setAddForm(prev => ({ ...prev, [side]: { ...prev[side], open: !prev[side].open, error: "" } }))} style={{ background:"none", border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"4px 10px", color:T.text, fontSize:11, fontWeight:700, cursor:"pointer" }}>Add player</button>
        </div>
        {form.open && (
          <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:12, marginBottom:10 }}>
            <label htmlFor={`addp-name-${side}`} style={{ fontSize:11, color:T.textDim, display:"block", marginBottom:4 }}>Player name</label>
            <input id={`addp-name-${side}`} value={form.name} onChange={e=>setAddForm(prev=>({ ...prev, [side]: { ...prev[side], name: e.target.value, error: "" } }))} placeholder="Player name" style={{ width:"100%", background:T.bg, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"9px 10px", color:T.text, fontSize:13, fontFamily:"inherit", marginBottom:8 }}/>
            <label htmlFor={`addp-dob-${side}`} style={{ fontSize:11, color:T.textDim, display:"block", marginBottom:4 }}>Date of birth (optional — needed for namesakes)</label>
            <input id={`addp-dob-${side}`} type="date" value={form.dob} onChange={e=>setAddForm(prev=>({ ...prev, [side]: { ...prev[side], dob: e.target.value, error: "" } }))} style={{ width:"100%", background:T.bg, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"9px 10px", color:T.text, fontSize:13, fontFamily:"inherit", marginBottom:8 }}/>
            {form.error && <div style={{ fontSize:11, color:T.accent, marginBottom:8 }}>{form.error}</div>}
            <button onClick={()=>submitAddPlayer(side)} style={{ background:T.purple, border:"none", borderRadius:T.radiusSm, padding:"9px 0", color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer", width:"100%" }}>Add player</button>
          </div>
        )}
        {(players || []).map(p => (
          <label htmlFor={`played-${p.id}`} key={p.id} style={{ display:"flex", alignItems:"center", gap:10, background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"10px 12px", marginBottom:6, cursor:"pointer" }}>
            <input id={`played-${p.id}`} type="checkbox" checked={Boolean(played[p.id])} onChange={e=>tickPlayer(p.id, e.target.checked)} style={{ width:16, height:16 }}/>
            <span style={{ fontSize:13, fontWeight:600 }}>{playerLabel(p, side)}</span>
          </label>
        ))}
        {(!players || players.length === 0) && !playersLoading && (
          <div style={{ fontSize:11, color:T.textDim, padding:"6px 0" }}>No players loaded — add the player by hand.</div>
        )}
      </div>
    );
  }

  function renderNotMatchedRow(side, sel) {
    const list = side === "home" ? homePlayers : awayPlayers;
    const key = rowKey(sel);
    const selId = `${side}-nm-${key}`;
    const err = (feedErrors[side] || {})[key];
    return (
      <div key={`${side}-${key}`} style={{ borderBottom:`1px solid ${T.border}`, paddingBottom:10, marginBottom:10 }}>
        <div style={{ fontSize:13, fontWeight:600 }}>{sel.name || "(unnamed)"} <span style={{ fontSize:11, color:T.textDim }}>— {resolutionLabel(sel.resolution)}</span></div>
        {sel.resolution === "ambiguous" && sel.candidates.length > 0 && (
          <div style={{ marginTop:6 }}>
            <label htmlFor={`${selId}-amb`} style={{ fontSize:11, color:T.textDim, display:"block", marginBottom:4 }}>Pick the right player (ambiguous)</label>
            <select id={`${selId}-amb`} value={(ambSel[side]||{})[key]||""} onChange={e=>setAmbSel(prev=>({ ...prev, [side]: { ...(prev[side]||{}), [key]: e.target.value } }))} style={{ width:"100%", background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"8px 10px", color:T.text, fontSize:13, fontFamily:"inherit", marginBottom:6 }}>
              <option value="">Pick the right player (ambiguous)…</option>
              {sel.candidates.map(c => <option key={c.playerId} value={c.playerId}>{c.name}{c.dateOfBirth ? ` · ${c.dateOfBirth}` : " · DOB unknown"}</option>)}
            </select>
            <button onClick={()=>resolveAmbiguous(side, key, sel)} style={{ background:T.purple, border:"none", borderRadius:T.radiusSm, padding:"7px 14px", color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer", marginRight:6 }}>Match to this player</button>
            <button onClick={()=>submitAddPlayer(side, { name: sel.name, dob: sel.dateOfBirth || "", externalRef: sel.externalRef || "", tries: sel.tries || 0, goals: sel.goals || 0, fieldGoals: sel.fieldGoals || 0, key })} style={{ background:T.surfaceHigh, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"7px 14px", color:T.text, fontSize:12, fontWeight:700, cursor:"pointer" }}>Add player</button>
          </div>
        )}
        {sel.resolution === "other_team" && (
          <div style={{ marginTop:6 }}>
            <div style={{ fontSize:12, color:T.textMuted, marginBottom:8 }}>
              Plays for {sel.currentTeamName || "another team"} in our squads.
            </div>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
              <button onClick={()=>moveOtherTeamPlayer(side, key, sel, entry.matchDate)} style={{ background:T.purple, border:"none", borderRadius:T.radiusSm, padding:"7px 14px", color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer" }}>Moved to {side==="home"?entry.homeRef:entry.awayRef} from this match</button>
              <button onClick={()=>moveOtherTeamPlayer(side, key, sel, null)} style={{ background:T.surfaceHigh, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"7px 14px", color:T.text, fontSize:12, fontWeight:700, cursor:"pointer" }}>Squad was wrong — move for the whole season</button>
            </div>
          </div>
        )}
        {sel.resolution === "unresolved" && (
          <div style={{ marginTop:6 }}>
            <label htmlFor={`${selId}-match`} style={{ fontSize:11, color:T.textDim, display:"block", marginBottom:4 }}>Match to an existing player</label>
            <select id={`${selId}-match`} value={(matchSel[side]||{})[key]||""} onChange={e=>setMatchSel(prev=>({ ...prev, [side]: { ...(prev[side]||{}), [key]: e.target.value } }))} style={{ width:"100%", background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"8px 10px", color:T.text, fontSize:13, fontFamily:"inherit", marginBottom:6 }}>
              <option value="">Match to…</option>
              {(list||[]).map(p => <option key={p.id} value={p.id}>{playerLabel(p, side)}</option>)}
            </select>
            <button onClick={()=>matchFeedName(side, key, sel)} style={{ background:T.purple, border:"none", borderRadius:T.radiusSm, padding:"7px 14px", color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer", marginRight:6 }}>Match</button>
            <button onClick={()=>submitAddPlayer(side, { name: sel.name, dob: sel.dateOfBirth || "", externalRef: sel.externalRef || "", tries: sel.tries || 0, goals: sel.goals || 0, fieldGoals: sel.fieldGoals || 0, key })} style={{ background:T.surfaceHigh, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"7px 14px", color:T.text, fontSize:12, fontWeight:700, cursor:"pointer" }}>Add player</button>
          </div>
        )}
        {err && <div style={{ fontSize:11, color:T.accent, marginTop:6 }}>{err}</div>}
      </div>
    );
  }

  // ── DETAIL VIEW (a fixture is selected) ──
  if (selected && entry) {
    const ticked = Object.keys(played);
    return (
      <div style={{ minHeight:"100vh", background:T.bg, fontFamily:"system-ui,sans-serif", color:T.text, paddingBottom:40 }}>
        <div style={{ padding:"20px 16px", borderBottom:`1px solid ${T.border}` }}>
          <button onClick={()=>{ setSelected(null); setNotice(""); }} style={{ background:"none", border:"none", color:T.textMuted, fontSize:13, cursor:"pointer", marginBottom:10 }}>← Back to queue</button>
          <div style={{ fontWeight:800, fontSize:17 }}><TeamMatchup home={entry.homeRef} away={entry.awayRef} size={36} prominent /></div>
          <div style={{ fontSize:12, color:T.textMuted, marginTop:2 }}>{entry.venueName || "Venue TBA"} · {fixtureDateLabel(entry)}</div>
          {!entry.isOrigin && (
            <div style={{ fontSize:11, color:T.textDim, marginTop:8 }}>Step {step} of 2 — {step===1?"Confirm players who took the field":"Add try-scorers & milestones"}</div>
          )}
        </div>
        <div style={{ padding:16 }}>{renderBanner()}</div>

        {entry.isOrigin ? (() => {
          // R16: two-panel Origin line-up editor (home first). Each panel is
          // headed with the state mark and name, shows "{n} selected (13–19)",
          // a debounced search box on `admin_search_players` (p_on = fixture
          // match_date), and the selected identities. "Pre-fill from Rugby
          // League Project" ticks matched rows on their mapped side and lists
          // ambiguous / unmatched / ref_conflict / duplicate rows (never
          // ticked) for the admin to resolve or exclude. "Confirm line-ups" is
          // enabled only when both sides hold 13–19 and no listed feed row is
          // unresolved. Explicit sides are saved (never default-to-home).
          const sameNameSuffix = (results, p) => {
            const dupes = results.filter(r => r.display_name === p.display_name && (r.club_name ?? null) === (p.club_name ?? null));
            return dupes.length > 1 ? String(p.id ?? "").slice(0, 6) : "";
          };
          const renderOriginPanel = (side) => {
            const teamRef = side === "home" ? entry.homeRef : entry.awayRef;
            const list = originLineup[side] || [];
            const results = originSearchResults[side] || [];
            const query = originSearchQuery[side] || "";
            const loading = originSearchLoading[side];
            const count = list.length;
            return (
              <div aria-label={`${teamRef} line-up`} style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.radius, padding:14, marginBottom:16 }}>
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                  <TeamName team={teamRef} size={28} prominent />
                  <div style={{ fontSize:12, color:T.textMuted, marginLeft:"auto" }}>{count} selected (13–19)</div>
                </div>
                <label htmlFor={`origin-search-${side}`} style={{ fontSize:11, color:T.textDim, display:"block", marginBottom:4 }}>Search {teamRef} players</label>
                <input id={`origin-search-${side}`} type="search" value={query} disabled={!!originLock} onChange={e => onOriginSearchInput(side, e.target.value)} placeholder={`Search ${teamRef} players…`} style={{ width:"100%", background:T.bg, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"9px 10px", color:T.text, fontSize:13, fontFamily:"inherit", marginBottom:10, boxSizing:"border-box" }} />
                {loading && <div style={{ fontSize:11, color:T.textDim, marginBottom:8 }}>Searching…</div>}
                {!loading && results.map(p => {
                  const suffix = sameNameSuffix(results, p);
                  return (
                    <button key={p.id} aria-label={`Add ${p.display_name}`} disabled={!!originLock} onClick={() => originAddPlayer(side, p)} style={{ display:"flex", width:"100%", alignItems:"center", gap:8, background:T.surfaceHigh, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"9px 12px", color:T.text, fontSize:13, fontWeight:600, cursor:"pointer", marginBottom:6, textAlign:"left" }}>
                      <span style={{ flex:1 }}>{p.display_name}{p.club_name ? <span style={{ color:T.textMuted, fontWeight:400, marginLeft:6 }}>{p.club_name}</span> : null}{suffix ? <span style={{ color:T.textDim, fontWeight:400, marginLeft:6, fontFamily:"monospace" }}>{suffix}</span> : null}</span>
                      <span style={{ fontSize:11, color:T.textDim }}>Add</span>
                    </button>
                  );
                })}
                {list.length > 0 && (
                  <div style={{ marginTop:8 }}>
                    {list.map(p => (
                      <div key={p.playerId} style={{ display:"flex", alignItems:"center", gap:8, background:T.surfaceHigh, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"8px 12px", marginBottom:6 }}>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:13, fontWeight:600 }}>{p.name}</div>
                          {p.clubName && <div style={{ fontSize:11, color:T.textMuted }}>{p.clubName}</div>}
                        </div>
                        <button aria-label={`Remove ${p.name}`} disabled={!!originLock} onClick={() => originRemovePlayer(side, p.playerId)} style={{ background:"none", border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"4px 10px", color:T.text, fontSize:11, fontWeight:700, cursor:"pointer" }}>Remove</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          };
          const feedRowKey = (sel) => sel.occurrenceId ?? sel.rowKey ?? sel.externalRef ?? sel.name;
          // F6: same-name same-club feed candidates show the first 6 characters
          // of their UUID, reusing the search-results helper so the two Pick
          // buttons are distinguishable.
          const feedCandidateSuffix = (candidates, c) => {
            const dupes = candidates.filter(r => r.name === c.name && (r.clubName ?? null) === (c.clubName ?? null));
            return dupes.length > 1 ? String(c.playerId ?? "").slice(0, 6) : "";
          };
          const renderOriginFeedRow = (side, sel) => {
            const key = feedRowKey(sel);
            return (
              <div key={`${side}-${key}`} style={{ borderBottom:`1px solid ${T.border}`, paddingBottom:10, marginBottom:10 }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10 }}>
                  <div style={{ fontSize:13, fontWeight:600 }}>{sel.name || "(unnamed)"} <span style={{ fontSize:11, color:T.textDim }}>— {resolutionLabel(sel.resolution)}</span></div>
                  <button aria-label={`Exclude ${sel.name}`} disabled={!!originLock} onClick={() => originExcludeFeed(side, key)} style={{ background:T.surfaceHigh, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"5px 12px", color:T.text, fontSize:11, fontWeight:700, cursor:"pointer" }}>Exclude</button>
                </div>
                {(sel.candidates || []).map(c => {
                  const suffix = feedCandidateSuffix(sel.candidates || [], c);
                  return (
                  <button key={c.playerId} aria-label={`Pick ${c.name} for ${sel.name}`} disabled={!!originLock} onClick={() => originPickCandidate(side, key, c)} style={{ display:"block", width:"100%", textAlign:"left", background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"7px 12px", color:T.text, fontSize:12, fontWeight:600, cursor:"pointer", marginTop:6 }}>
                    Pick {c.name}{c.clubName ? ` · ${c.clubName}` : ""}{suffix ? <span style={{ color:T.textDim, fontWeight:400, marginLeft:6, fontFamily:"monospace" }}>{suffix}</span> : ""}
                  </button>
                  );
                })}
              </div>
            );
          };
          return (
            <div style={{ padding:16 }}>
              {notice && <div style={{ fontSize:12, color:T.accent, marginBottom:14, textAlign:"center" }}>{notice}</div>}
              {originLock === "loading" && <div style={{ fontSize:11, color:T.textDim, marginBottom:14, textAlign:"center" }}>Loading the saved line-up…</div>}
              <div style={{ display:"flex", gap:10, marginBottom:16 }}>
                <button onClick={originPrefill} disabled={originPrefilling || !!originLock || !entry.externalRef} style={{ flex:1, background:T.surfaceHigh, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"11px 0", color:T.text, fontSize:12, fontWeight:700, cursor:(originPrefilling||!entry.externalRef)?"default":"pointer", opacity:!entry.externalRef?0.55:1 }}>
                  {originPrefilling ? "Fetching…" : "Pre-fill from Rugby League Project"}
                </button>
              </div>
              {renderOriginPanel("home")}
              {renderOriginPanel("away")}
              {originFeedOpen && (
                <div style={{ marginTop:8, background:T.surfaceHigh, border:`1px solid ${T.border}`, borderRadius:T.radius, padding:14 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:T.textDim, letterSpacing:1, marginBottom:8 }}>FROM THE FEED, NOT MATCHED</div>
                  {originFeed.home.map(sel => renderOriginFeedRow("home", sel))}
                  {originFeed.away.map(sel => renderOriginFeedRow("away", sel))}
                </div>
              )}
              <button onClick={handleConfirmOrigin} disabled={originConfirmDisabled} style={{ width:"100%", background:originConfirmDisabled?T.surfaceHigh:T.accent, border:"none", borderRadius:T.radiusSm, padding:"13px 0", color:originConfirmDisabled?T.textMuted:"#000", fontSize:14, fontWeight:800, cursor:originConfirmDisabled?"default":"pointer", opacity:originConfirmDisabled?0.6:1, marginTop:8 }}>
                {originLock === "saving" ? "Saving…" : "Confirm line-ups"}
              </button>
            </div>
          );
        })() : (
        <>

        {step === 1 && (
          <div style={{ padding:16 }}>
            <div style={{ display:"flex", gap:10, marginBottom:16 }}>
              <button onClick={loadFromFeed} disabled={fetching || !entry.externalRef} aria-disabled={fetching || !entry.externalRef} style={{ flex:1, background:T.surfaceHigh, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"11px 0", color:T.text, fontSize:12, fontWeight:700, cursor:(fetching||!entry.externalRef)?"default":"pointer", opacity:!entry.externalRef?0.55:1 }}>
                {fetching ? "Fetching..." : "Fetch from RLP"}
              </button>
              <button onClick={enterByHand} style={{ flex:1, background:T.surfaceHigh, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"11px 0", color:T.text, fontSize:12, fontWeight:700, cursor:"pointer" }}>
                Enter by hand
              </button>
            </div>
            {!entry.externalRef && (
              <div style={{ fontSize:11, color:T.textDim, marginBottom:14, textAlign:"center" }}>No feed reference for this match yet — enter the teams by hand.</div>
            )}
            {notice && <div style={{ fontSize:12, color:T.accent, marginBottom:14, textAlign:"center" }}>{notice}</div>}

            {playersLoading && <div style={{ fontSize:12, color:T.textMuted, marginBottom:14, textAlign:"center" }}>Loading the squad…</div>}
            {playersError && <div style={{ fontSize:12, color:T.accent, marginBottom:14, textAlign:"center" }}>Couldn't load the squad — try again or add the player by hand.</div>}

            {renderSide("home", entry.homeRef, homePlayers)}
            {renderSide("away", entry.awayRef, awayPlayers)}

            {(feedNotMatched.home.length + feedNotMatched.away.length) > 0 && (
              <div style={{ marginTop:20, background:T.surfaceHigh, border:`1px solid ${T.border}`, borderRadius:T.radius, padding:14 }}>
                <div style={{ fontSize:11, fontWeight:700, color:T.textDim, letterSpacing:1, marginBottom:8 }}>FROM THE FEED, NOT MATCHED</div>
                {feedNotMatched.home.map((sel) => renderNotMatchedRow("home", sel))}
                {feedNotMatched.away.map((sel) => renderNotMatchedRow("away", sel))}
              </div>
            )}

            <button onClick={goToStep2} style={{ width:"100%", background:T.accent, border:"none", borderRadius:T.radiusSm, padding:"13px 0", color:"#000", fontSize:14, fontWeight:800, cursor:"pointer", marginTop:16 }}>
              Next: Tries & Milestones →
            </button>
          </div>
        )}

        {step === 2 && (
          <div style={{ padding:16 }}>
            {notice && <div style={{ fontSize:12, color:T.accent, marginBottom:14, textAlign:"center" }}>{notice}</div>}
            {/* milestone codes notice when fetchMilestoneCodes fails or returns empty */}
            {milestoneCodesMissing && (
              <div style={{ fontSize:12, color:T.textMuted, marginBottom:14, textAlign:"center" }}>Milestone codes could not be loaded — milestones can be added later</div>
            )}
            <div style={{ fontSize:11, fontWeight:700, color:T.textDim, letterSpacing:1, marginBottom:10 }}>TRIES / GOALS / FIELD GOALS</div>
            {ticked.map(pid => {
              const side = sideForPlayer(pid);
              const list = side === "home" ? homePlayers : awayPlayers;
              const p = (list || []).find(q => q.id === pid);
              const name = p ? playerLabel(p, side) : pid;
              const s = stats[pid] || { tries: 0, goals: 0, fieldGoals: 0 };
              return (
                <div key={pid} style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"10px 12px", marginBottom:8 }}>
                  <div style={{ fontSize:13, fontWeight:600, marginBottom:8 }}>{name}</div>
                  {["tries","goals","fieldGoals"].map(key => (
                    <div key={key} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
                      <div style={{ fontSize:12, color:T.textMuted, textTransform:"capitalize" }}>{key}</div>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <button onClick={()=>setStat(pid, key, -1)} aria-label={`Remove a ${key} for ${name}`} style={{ width:28, height:28, borderRadius:"50%", border:`1px solid ${T.border}`, background:T.surfaceHigh, color:T.text, fontSize:14, cursor:"pointer" }}>−</button>
                        <div style={{ width:20, textAlign:"center", fontWeight:700, fontSize:14 }}>{s[key]||0}</div>
                        <button onClick={()=>setStat(pid, key, 1)} aria-label={`Add a ${key} for ${name}`} style={{ width:28, height:28, borderRadius:"50%", border:`1px solid ${T.border}`, background:T.surfaceHigh, color:T.text, fontSize:14, cursor:"pointer" }}>+</button>
                      </div>
                    </div>
                  ))}
                  <div style={{ marginTop:8 }}>
                    <label htmlFor={`ms-${pid}`} style={{ fontSize:11, color:T.textDim, marginBottom:4, display:"block" }}>Milestones</label>
                    <select id={`ms-${pid}`} multiple value={milestoneSel[pid]||[]} disabled={milestoneCodesMissing} onChange={e=>setPlayerMilestones(pid, Array.from(e.target.selectedOptions).map(o=>o.value))} style={{ width:"100%", minHeight: Math.max(2, (milestoneCodes||[]).length + 1) * 22, background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"6px 8px", color:T.text, fontSize:13, fontFamily:"inherit", opacity:milestoneCodesMissing?0.55:1 }}>
                      {(milestoneCodes||[]).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                </div>
              );
            })}

            <div style={{ display:"flex", gap:10, marginTop:20 }}>
              <button onClick={()=>setStep(1)} style={{ flex:1, background:T.surfaceHigh, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"13px 0", color:T.text, fontSize:14, fontWeight:700, cursor:"pointer" }}>← Back</button>
              <button onClick={handleConfirm} disabled={saving} style={{ flex:2, background:T.accent, border:"none", borderRadius:T.radiusSm, padding:"13px 0", color:"#000", fontSize:14, fontWeight:800, cursor:saving?"default":"pointer", opacity:saving?0.6:1 }}>
                {saving ? "Saving…" : "✓ Confirm This Game"}
              </button>
            </div>
          </div>
        )}
        </>
        )}
      </div>
    );
  }

  // ── QUEUE VIEW ──
  // R17: an Origin queue item's status line reflects the R5 completeness
  // predicate (`originComplete`). An unreleased incomplete Origin shows
  // "Line-ups needed"; a complete one shows "Confirmed"; a released round
  // with an incomplete Origin (legacy empty confirmation) shows "Released
  // under previous rules — line-ups not recorded" and the item stays
  // openable to record facts. Club items keep the round-14 status line.
  function itemStatusLine(item) {
    if (item.isOrigin) {
      if (item.releasedAt && !item.originComplete) return "Released under previous rules — line-ups not recorded";
      if (!item.originComplete) return "Line-ups needed";
      if (item.confirmed) return `Confirmed · ${item.confirmation.source} · ${confirmedDateLabel(item)}`;
      return "Confirmed";
    }
    return item.confirmed
      ? `Confirmed · ${item.confirmation.source} · ${confirmedDateLabel(item)}`
      : "Awaiting confirmation";
  }
  const visibleRounds = roundGroups.slice(0, 3);
  const earlierRounds = roundGroups.slice(3);
  function renderGroup(group) {
    const released = Boolean(group.releasedAt);
    // F10: in an unreleased round header, an Origin item counts as confirmed
    // only when `originComplete` is true; club items and released rounds
    // unchanged.
    const confirmedCount = group.items.filter(i => {
      if (!i.confirmed) return false;
      if (!released && i.isOrigin && !i.originComplete) return false;
      return true;
    }).length;
    const allConfirmed = confirmedCount === group.items.length;
    const rs = releaseState[group.key] || { busy: false, msg: "" };
    const fa = fetchAllState[group.key] || { busy: false, summary: "" };
    const releaseDisabled = released || !allConfirmed || rs.busy;
    return (
      <div key={group.key} style={{ marginBottom:20 }}>
        <div style={{ fontSize:12, fontWeight:700, color:T.textMuted, letterSpacing:2, marginBottom:8, marginTop:4 }}>
          {released
            ? `ROUND ${group.round} · Released · ${releasedDateLabel(group)}`
            : `ROUND ${group.round} · ${confirmedCount} OF ${group.items.length} CONFIRMED`}
        </div>
        <button onClick={()=>handleFetchAll(group)} disabled={fa.busy || released} style={{ width:"100%", background:T.surfaceHigh, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"10px 0", color:T.textMuted, fontSize:12, fontWeight:700, cursor:(fa.busy||released)?"default":"pointer", marginBottom:10, opacity:fa.busy?0.6:1 }}>
          {fa.busy ? "Fetching…" : `Fetch all for round ${group.round}`}
        </button>
        {fa.summary && <div style={{ fontSize:11, color:T.accent, marginBottom:8, textAlign:"center" }}>{fa.summary}</div>}
        {group.items.map(item => (
          <div key={item.id} onClick={()=>openFixture(item.id)} style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.radius, padding:"14px 16px", marginBottom:10, cursor:"pointer" }}>
            <div style={{ fontWeight:700, fontSize:14 }}><TeamMatchup home={item.homeRef} away={item.awayRef} /></div>
            {originTag(item) && (
              <div style={{ fontSize:11, color:T.textDim, marginTop:3 }}>{originTag(item)}</div>
            )}
            <div style={{ fontSize:12, color:T.textMuted, marginTop:3 }}>{item.venueName || "Venue TBA"} · {fixtureDateLabel(item)}</div>
            <div style={{ fontSize:11, color:item.confirmed?T.accent:T.gold, marginTop:4 }}>
              {itemStatusLine(item)}
            </div>
          </div>
        ))}
        <button onClick={()=>handleRelease(group)} disabled={releaseDisabled} aria-disabled={releaseDisabled} style={{ width:"100%", background:released?T.surfaceHigh:(allConfirmed?T.purple:T.surfaceHigh), border:`1px solid ${(released||allConfirmed)?"transparent":T.border}`, borderRadius:T.radiusSm, padding:"12px 0", color:released?T.textMuted:(allConfirmed?"#fff":T.textMuted), fontSize:13, fontWeight:700, cursor:releaseDisabled?"default":"pointer", opacity:rs.busy?0.6:1 }}>
          {released ? "Round released" : rs.busy ? "Releasing…" : `Release round ${group.round} XP`}
        </button>
        {!released && !allConfirmed && (
          <div style={{ fontSize:11, color:T.textDim, marginTop:6, textAlign:"center" }}>Confirm every match first</div>
        )}
        {rs.msg && (
          <div style={{ fontSize:12, color:T.accent, marginTop:8, textAlign:"center" }}>{rs.msg}</div>
        )}
      </div>
    );
  }
  // R11: items with a null scoringRound form one extra group, shown after the
  // round groups and outside the three-group cap, with no release and no
  // Fetch all.
  function renderNullGroup(group) {
    return (
      <div key={group.key} style={{ marginBottom:20 }}>
        <div style={{ fontSize:12, fontWeight:700, color:T.textMuted, letterSpacing:2, marginBottom:8, marginTop:4 }}>Not in a round yet</div>
        {group.items.map(item => (
          <div key={item.id} onClick={()=>openFixture(item.id)} style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.radius, padding:"14px 16px", marginBottom:10, cursor:"pointer" }}>
            <div style={{ fontWeight:700, fontSize:14 }}><TeamMatchup home={item.homeRef} away={item.awayRef} /></div>
            {originTag(item) && (
              <div style={{ fontSize:11, color:T.textDim, marginTop:3 }}>{originTag(item)}</div>
            )}
            <div style={{ fontSize:12, color:T.textMuted, marginTop:3 }}>{item.venueName || "Venue TBA"} · {fixtureDateLabel(item)}</div>
            <div style={{ fontSize:11, color:item.confirmed?T.accent:T.gold, marginTop:4 }}>
              {itemStatusLine(item)}
            </div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div style={{ minHeight:"100vh", background:T.bg, fontFamily:"system-ui,sans-serif", color:T.text, paddingBottom:40 }}>
      <div style={{ padding:"20px 16px", borderBottom:`1px solid ${T.border}` }}>
        <button onClick={onBack} style={{ background:"none", border:"none", color:T.textMuted, fontSize:13, cursor:"pointer", marginBottom:10 }}>← Back</button>
        <div style={{ fontWeight:800, fontSize:18 }}>Weekly Confirmation (Admin)</div>
        <div style={{ fontSize:12, color:T.textMuted, marginTop:2 }}>Confirm each match, then release the round's XP.</div>
        {appClock?.replay_mode && (
          <div style={{ fontSize:12, color:T.accentDark, marginTop:6 }}>App time: {replayClockLabel(appClock.app_now)}</div>
        )}
      </div>
      <div style={{ padding:16 }}>
        {renderBanner()}
        <SquadDriftSection replayMode={!!appClock?.replay_mode} onChecked={loadNotifications} />
        {queue === null && !queueError && (
          <div style={{ textAlign:"center", padding:"40px 0", color:T.textMuted, fontSize:13 }}>Loading the confirmation queue…</div>
        )}
        {queueError && (
          <div style={{ textAlign:"center", padding:"40px 0", color:T.textMuted, fontSize:13 }}>
            Couldn't load the confirmation queue — check your connection and try again.
            <div style={{ marginTop:12 }}><button onClick={loadQueue} style={{ background:T.accent, border:"none", borderRadius:T.radiusSm, padding:"10px 18px", color:"#000", fontSize:13, fontWeight:700, cursor:"pointer" }}>Retry</button></div>
          </div>
        )}
        {!queueError && queue !== null && queue.length === 0 && (
          <div style={{ textAlign:"center", padding:"40px 0", color:T.textDim, fontSize:13 }}>No games pending confirmation right now. 🎉</div>
        )}
        {!queueError && queue !== null && visibleRounds.map(renderGroup)}
        {!queueError && queue !== null && earlierRounds.length > 0 && (
          <button onClick={()=>setShowEarlier(v => !v)} aria-expanded={showEarlier} style={{ width:"100%", background:T.surfaceHigh, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"11px 0", color:T.textMuted, fontSize:12, fontWeight:700, cursor:"pointer", marginBottom:20 }}>
            {showEarlier ? "Hide earlier rounds" : "Show earlier rounds"}
          </button>
        )}
        {!queueError && queue !== null && showEarlier && earlierRounds.map(renderGroup)}
        {!queueError && queue !== null && nullGroup && renderNullGroup(nullGroup)}
      </div>
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export function SetPasswordScreen({ title = "Set a new password", intro, onSubmit, busy, error, notice, onCancel, showMark = true }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [validation, setValidation] = useState("");
  const inputStyle = { width:"100%", background:T.surfaceHigh, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"11px 14px", color:T.text, fontSize:14, marginBottom:10, boxSizing:"border-box", outline:"none" };
  async function submit(event) {
    const operationGeneration = getClockGeneration();
    event.preventDefault();
    if (busy) return;
    if (password !== confirm) return setValidation("Passwords don't match");
    if (password.length < 6) return setValidation("Use at least 6 characters.");
    setValidation("");
    await onSubmit(password);
    if (operationGeneration !== getClockGeneration()) return;
    setPassword("");
    setConfirm("");
  }
  return (
    <form onSubmit={submit} style={{ background:T.surface, borderRadius:T.radiusLg, padding:24, border:`1px solid ${T.border}` }}>
      {showMark && <img src="/brand-mark-192.png" alt="" aria-hidden="true" style={{ width:64, height:64, objectFit:"contain", display:"block", margin:"0 auto 14px" }} />}
      <h2 style={{ color:T.text, fontSize:20 }}>{title}</h2>
      {intro && <p style={{ color:T.textMuted, fontSize:13 }}>{intro}</p>}
      <input type="password" aria-label="New password" placeholder="New password" autoComplete="new-password" value={password} disabled={busy} onChange={e=>{setPassword(e.target.value); setValidation("");}} style={inputStyle} />
      <input type="password" aria-label="Confirm new password" placeholder="Confirm new password" autoComplete="new-password" value={confirm} disabled={busy} onChange={e=>{setConfirm(e.target.value); setValidation("");}} style={inputStyle} />
      {(validation || error) && <div role="alert" style={{ color:T.red, fontSize:12, marginBottom:10 }}>{validation || error}</div>}
      {notice && <div role="status" style={{ color:T.accentDark, fontSize:12, marginBottom:10 }}>{notice}</div>}
      <button type="submit" disabled={busy} style={{ width:"100%", background:T.accent, border:"none", borderRadius:T.radiusSm, padding:"13px 0", color:"#000", fontSize:15, fontWeight:800, cursor:busy?"default":"pointer", opacity:busy?0.6:1 }}>{busy ? "Please wait…" : "Save password"}</button>
      {onCancel && <button type="button" disabled={busy} onClick={onCancel} style={{ background:"none", border:"none", color:T.textMuted, textDecoration:"underline", cursor:"pointer", marginTop:12 }}>Not now</button>}
    </form>
  );
}

// ─── ADMIN SQUADS SCREEN ────────────────────────────────────────────────────
import {
  adminSquad,
  adminMovePlayer,
  adminEndMembership,
  adminAddToSquad,
  adminPlayerByRef,
  adminRollSquads,
  runSquadBuild,
  fetchTeamIdsByName,
} from "./lib/squadsApi";
import { addPlayer as addSquadPlayer } from "./lib/adminApi";
import { parseRlpPlayerRef } from "@nrl/shared";

const SQUADS_SEASON_OPTIONS = [CURRENT_SEASON, CURRENT_SEASON + 1];
const SQUADS_TEAM_NAMES = [...TEAM_NAMES].sort((a, b) => a.localeCompare(b));
const END_REASONS = ["transfer", "released", "retired", "correction", "other"];

function squadErrorCopy(code) {
  switch (code) {
    case "forbidden": return "You don't have permission to do that. Sign in as an admin and try again.";
    case "unauthenticated": return "Your session has expired. Sign in again and retry.";
    case "unknown_team": return "That team could not be found.";
    case "unknown_player": return "That player could not be found.";
    case "unknown_membership": return "That squad entry could not be found — refresh and try again.";
    case "invalid_date": return "That date is outside the selected season.";
    case "invalid_kind": return "That squad type isn't valid.";
    case "invalid_reason": return "Pick a reason for ending this membership.";
    case "invalid_seasons": return "Squads can only be rolled forward into the very next season.";
    case "same_team": return "This player is already in that squad.";
    case "no_membership": return "This player has no squad membership covering that date to move.";
    case "membership_overlap": return "This player already has a squad membership covering that date.";
    case "namesake_requires_date_of_birth": return "Another player has this name — add a date of birth to tell them apart.";
    case "namesake_missing_date_of_birth": return "An existing namesake has no date of birth — add one before adding this player.";
    case "duplicate_player": return "A player with this name and date of birth already exists.";
    case "external_ref_taken": return "That feed reference is already on another player.";
    case "invalid_name": return "Enter a name.";
    case "network_error": return "Couldn't reach the server. Check your connection and try again.";
    default: return "Something went wrong — try again.";
  }
}

function resolutionLabel(resolution) {
  switch (resolution) {
    case "other_team": return "in another squad";
    case "unresolved": return "not in our squads";
    case "ambiguous": return "more than one match";
    case "unmatched": return "no identity match";
    case "ref_conflict": return "reference conflict";
    case "duplicate": return "duplicate feed row";
    default: return resolution;
  }
}

function squadDateLabel(iso) {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat("en-AU", { day: "numeric", month: "short", year: "numeric" }).format(new Date(iso + "T00:00:00"));
  } catch {
    return iso;
  }
}

// Round 10 remediation (M2/M4) — a date-mode End/Move or a blank-start Add
// must land within the season being viewed, not silently correct/delete a
// different season's membership.
function dateWithinSeason(iso, season) {
  return Boolean(iso) && iso >= `${season}-01-01` && iso <= `${season}-12-31`;
}

export function AdminSquadsScreen({ onBack }) {
  const [season, setSeason] = useState(CURRENT_SEASON);
  const [teamIds, setTeamIds] = useState(null); // Map<name, id> | null
  const [teamIdsError, setTeamIdsError] = useState(false);
  const [teamName, setTeamName] = useState(SQUADS_TEAM_NAMES[0]);
  const [squad, setSquad] = useState(null); // array | null
  const [squadLoading, setSquadLoading] = useState(false);
  const [squadError, setSquadError] = useState(false);
  const [rowNotice, setRowNotice] = useState("");
  const [rowBusy, setRowBusy] = useState(null);

  const [moveOpen, setMoveOpen] = useState(null); // membershipId
  const [moveTeam, setMoveTeam] = useState("");
  const [moveMode, setMoveMode] = useState("date"); // 'date' | 'whole'
  const [moveDate, setMoveDate] = useState("");

  const [endOpen, setEndOpen] = useState(null);
  const [endDate, setEndDate] = useState("");
  const [endReason, setEndReason] = useState("released");

  const [removeConfirm, setRemoveConfirm] = useState(null);

  const [pasteValue, setPasteValue] = useState("");
  const [pasteError, setPasteError] = useState("");
  const [lookup, setLookup] = useState(null); // { found, player, memberships } | null
  const [lookupRef, setLookupRef] = useState("");
  const [lookupBusy, setLookupBusy] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDob, setNewDob] = useState("");
  const [newStartsOn, setNewStartsOn] = useState("");
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState("");

  const [rollConfirming, setRollConfirming] = useState(false);
  const [rollBusy, setRollBusy] = useState(false);
  const [rollNotice, setRollNotice] = useState("");

  const [buildBusy, setBuildBusy] = useState(false);
  const [buildSummary, setBuildSummary] = useState(null); // { ...runSquadBuild result, season } | null
  const [buildError, setBuildError] = useState("");
  const [buildApplying, setBuildApplying] = useState(false);
  const buildRequestRef = useRef(0);

  useEffect(() => {
    (async () => {
      const map = await fetchTeamIdsByName();
      if (map) { setTeamIds(map); setTeamIdsError(false); }
      else setTeamIdsError(true);
    })();
  }, []);

  // Round 10 remediation (M3): changing the season invalidates any preview
  // in flight or on screen — Apply must never fire against a stale preview.
  useEffect(() => {
    buildRequestRef.current++;
    setBuildSummary(null);
    setBuildError("");
    setBuildBusy(false);
  }, [season]);

  const teamNamesById = useMemo(() => {
    const map = new Map();
    if (teamIds) { for (const [name, id] of teamIds) map.set(id, name); }
    return map;
  }, [teamIds]);

  // Round 10 remediation (should-fix) — bind rows/actions to season+team: a
  // new load clears any rows on screen immediately (so they can't stay
  // actionable while a slow old response is still in flight), and a request
  // whose season/team has since changed is ignored when it lands.
  const squadRequestRef = useRef(0);
  // Closure review follow-up: `loadSquad` itself was correctly guarded, but
  // an old mutation/Apply CONTINUATION calling it was not — that call bumps
  // the shared `squadRequestRef` regardless of which season/team it's for,
  // so it could look like "the newest request" and overwrite the roster the
  // admin has since navigated to. `contextRef` always holds the LIVE
  // season+team; every mutation continuation below re-checks it against the
  // team+season it actually started under before touching the squad list,
  // row notice or busy state.
  const contextRef = useRef({ teamName, season });
  contextRef.current = { teamName, season };
  function sameContext(ctx) {
    return contextRef.current.teamName === ctx.teamName && contextRef.current.season === ctx.season;
  }

  async function loadSquad() {
    const requestId = ++squadRequestRef.current;
    const teamId = teamIds?.get(teamName);
    setSquad(null);
    if (!teamId) { if (requestId === squadRequestRef.current) setSquad([]); return; }
    setSquadLoading(true); setSquadError(false);
    const rows = await adminSquad(teamId, season);
    if (requestId !== squadRequestRef.current) return; // superseded by a newer season/team request
    setSquadLoading(false);
    if (rows === null) { setSquadError(true); setSquad([]); }
    else setSquad(rows);
  }

  useEffect(() => {
    if (teamIds) loadSquad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamIds, teamName, season]);

  function resetRowState() {
    setMoveOpen(null); setMoveTeam(""); setMoveMode("date"); setMoveDate("");
    setEndOpen(null); setEndDate(""); setEndReason("released");
    setRemoveConfirm(null);
  }

  async function handleMove(row) {
    const ctx = { teamName, season };
    setRowBusy(row.membershipId); setRowNotice("");
    const toTeamId = teamIds?.get(moveTeam);
    if (!toTeamId) { setRowBusy(null); if (sameContext(ctx)) setRowNotice(squadErrorCopy("unknown_team")); return; }
    // Round 10 remediation (M2): a dated move requires a season-bounded date
    // — null is sent only for the explicit "whole season" correction path.
    // Should-fix: whole-season Move on an already-ended row corrects THAT
    // row (its own start date) in place, rather than null (which selects the
    // latest OPEN membership — not necessarily this historical row).
    let effective = null;
    if (moveMode === "date") {
      if (!dateWithinSeason(moveDate, season)) { setRowBusy(null); if (sameContext(ctx)) setRowNotice(squadErrorCopy("invalid_date")); return; }
      effective = moveDate;
    } else if (row.endsOn) {
      effective = row.startsOn;
    }
    const res = await adminMovePlayer(row.playerId, toTeamId, season, effective);
    setRowBusy(null);
    // Round 10 remediation (should-fix) — the admin may have switched
    // season/team while this move was in flight; its own success refresh
    // must never overwrite whatever season/team is on screen now.
    if (!sameContext(ctx)) return;
    if (!res.ok) { setRowNotice(squadErrorCopy(res.code)); return; }
    resetRowState();
    setRowNotice(`Moved ${row.name} to ${moveTeam}.`);
    await loadSquad();
  }

  async function handleEnd(row) {
    const ctx = { teamName, season };
    setRowBusy(row.membershipId); setRowNotice("");
    // Round 10 remediation (M2): End requires a season-bounded date — a blank
    // date must never reach the RPC (null there means "delete this
    // membership", bypassing the separately confirmed Remove flow).
    if (!dateWithinSeason(endDate, season)) { setRowBusy(null); if (sameContext(ctx)) setRowNotice(squadErrorCopy("invalid_date")); return; }
    const res = await adminEndMembership(row.membershipId, endDate, endReason);
    setRowBusy(null);
    if (!sameContext(ctx)) return; // the admin has since switched season/team
    if (!res.ok) { setRowNotice(squadErrorCopy(res.code)); return; }
    resetRowState();
    setRowNotice(res.mode === "ended" ? `Ended ${row.name}'s membership.` : `Removed ${row.name} from the squad.`);
    await loadSquad();
  }

  async function handleRemove(row) {
    const ctx = { teamName, season };
    setRowBusy(row.membershipId); setRowNotice("");
    const res = await adminEndMembership(row.membershipId, null);
    setRowBusy(null);
    if (!sameContext(ctx)) return; // the admin has since switched season/team
    if (!res.ok) { setRowNotice(squadErrorCopy(res.code)); return; }
    resetRowState();
    setRowNotice(`Removed ${row.name} from the squad.`);
    await loadSquad();
  }

  function resetAddState() {
    setPasteValue(""); setPasteError(""); setLookup(null); setLookupRef("");
    setNewName(""); setNewDob(""); setNewStartsOn(""); setAddError("");
  }

  async function handlePasteLookup() {
    setPasteError(""); setLookup(null); setLookupRef(""); setAddError("");
    const result = parseRlpPlayerRef(pasteValue);
    if (!result.ok) {
      setPasteError(result.reason === "slug_link"
        ? "That's a name link, not a player link — open the player on a team or match page, press and hold their name and choose Copy link."
        : "That's not a Rugby League Project player link");
      return;
    }
    setLookupBusy(true);
    const res = await adminPlayerByRef(result.ref);
    setLookupBusy(false);
    if (!res.ok) { setPasteError(squadErrorCopy(res.code)); return; }
    setLookupRef(result.ref);
    setLookup(res);
  }

  async function handleAddExisting(mode) {
    if (!lookup?.player) return;
    const teamId = teamIds?.get(teamName);
    if (!teamId) return;
    // Round 10 remediation (M4): this form promises "blank starts the
    // season" — a blank date must honour the SELECTED season, not whatever
    // the server's app_config default is; an explicit date is validated
    // against the selected season.
    if (newStartsOn && !dateWithinSeason(newStartsOn, season)) { setAddError(squadErrorCopy("invalid_date")); return; }
    setAddBusy(true); setAddError("");
    const startsOn = newStartsOn || `${season}-01-01`;
    const res = mode === "add"
      ? await adminAddToSquad(lookup.player.id, teamId, season, startsOn)
      : await adminMovePlayer(lookup.player.id, teamId, season, startsOn);
    setAddBusy(false);
    if (!res.ok) { setAddError(squadErrorCopy(res.code)); return; }
    resetAddState();
    setRowNotice(`Added ${lookup.player.name} to ${teamName}.`);
    await loadSquad();
  }

  async function handleAddNew() {
    const teamId = teamIds?.get(teamName);
    if (!teamId) return;
    const name = newName.trim();
    if (!name) { setAddError(squadErrorCopy("invalid_name")); return; }
    // Round 10 remediation (M4): blank start sends `${season}-01-01` so a
    // player added while viewing next season lands in next season, not
    // wherever app_config's current season happens to be; an explicit date
    // is validated against the selected season.
    if (newStartsOn && !dateWithinSeason(newStartsOn, season)) { setAddError(squadErrorCopy("invalid_date")); return; }
    setAddBusy(true); setAddError("");
    const res = await addSquadPlayer({
      teamId, name, dateOfBirth: newDob || null, externalRef: lookupRef || null, startsOn: newStartsOn || `${season}-01-01`,
    });
    setAddBusy(false);
    if (!res.ok) { setAddError(squadErrorCopy(res.code)); return; }
    resetAddState();
    setRowNotice(`Added ${name} to ${teamName}.`);
    await loadSquad();
  }

  async function handleRollSquads() {
    setRollBusy(true); setRollNotice("");
    const res = await adminRollSquads(season, season + 1);
    setRollBusy(false);
    setRollConfirming(false);
    if (!res.ok) { setRollNotice(squadErrorCopy(res.code)); return; }
    setRollNotice(`Started ${season + 1} squads — ${res.created} created, ${res.skipped} already had a squad.`);
  }

  async function handleBuildDryRun() {
    // Round 10 remediation (M3): the preview is bound to the season/params it
    // was requested for. Bumping the request ref invalidates any preview
    // still in flight for a DIFFERENT season/click (its response is ignored
    // when it lands); the resolved summary carries its own `season` so Apply
    // can never silently target whatever season the picker has since moved to.
    const requestId = ++buildRequestRef.current;
    const requestSeason = season;
    setBuildBusy(true); setBuildError(""); setBuildSummary(null);
    const res = await runSquadBuild({ season: requestSeason, dryRun: true, prune: true });
    if (requestId !== buildRequestRef.current) return; // superseded by a season change or a newer preview
    setBuildBusy(false);
    if (!res.ok) { setBuildError(squadErrorCopy(res.code === "feed_unavailable" ? "network_error" : res.code)); return; }
    setBuildSummary({ ...res, season: requestSeason });
  }

  async function handleBuildApply() {
    // Apply is bound to the previewed season/params, not the picker's live
    // value — disabled below unless buildSummary.season === season anyway,
    // this is the defensive second check (M3).
    if (!buildSummary || buildSummary.season !== season) return;
    const requestSeason = buildSummary.season;
    setBuildApplying(true); setBuildError("");
    const res = await runSquadBuild({ season: requestSeason, dryRun: false, prune: true });
    setBuildApplying(false);
    // Round 10 remediation (should-fix) — an Apply that outlives the season
    // the admin previewed it for must not touch the roster on screen now: it
    // previously called the OLD season/team's loadSquad(), which bumped the
    // shared squadRequestRef and so looked like the newest request, silently
    // replacing whatever season the admin had since moved on to.
    if (contextRef.current.season !== requestSeason) return;
    if (!res.ok) { setBuildError(squadErrorCopy(res.code === "feed_unavailable" ? "network_error" : res.code)); return; }
    setBuildSummary({ ...res, season: requestSeason });
    setRowNotice(`Rebuilt ${requestSeason} squads from Rugby League Project.`);
    await loadSquad();
  }

  function renderRow(row) {
    const ended = Boolean(row.endsOn);
    const busy = rowBusy === row.membershipId;
    return (
      <div key={row.membershipId} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radiusSm, padding: "10px 12px", marginBottom: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>{row.name}</div>
        <div style={{ fontSize: 11, color: T.textDim, marginTop: 2 }}>
          {row.dateOfBirth ? squadDateLabel(row.dateOfBirth) : "DOB unknown"} · {squadDateLabel(row.startsOn)} – {row.endsOn ? squadDateLabel(row.endsOn) : "current"} · {row.kind}
          {ended && ` · ended ${squadDateLabel(row.endsOn)} (${row.endReason})`}
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
          <button onClick={() => { resetRowState(); setMoveOpen(row.membershipId); setMoveTeam(teamName); }} disabled={busy} style={{ background: T.surfaceHigh, border: `1px solid ${T.border}`, borderRadius: T.radiusSm, padding: "6px 10px", color: T.text, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Move</button>
          <button onClick={() => { resetRowState(); setEndOpen(row.membershipId); }} disabled={busy} style={{ background: T.surfaceHigh, border: `1px solid ${T.border}`, borderRadius: T.radiusSm, padding: "6px 10px", color: T.text, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>End</button>
          <button onClick={() => { resetRowState(); setRemoveConfirm(row.membershipId); }} disabled={busy} style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: T.radiusSm, padding: "6px 10px", color: T.accent, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Remove</button>
        </div>

        {moveOpen === row.membershipId && (
          <div style={{ marginTop: 10, borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>
            <label htmlFor={`move-team-${row.membershipId}`} style={{ fontSize: 11, color: T.textDim, display: "block", marginBottom: 4 }}>Move to team</label>
            <select id={`move-team-${row.membershipId}`} value={moveTeam} onChange={e => setMoveTeam(e.target.value)} style={{ width: "100%", marginBottom: 8 }}>
              {SQUADS_TEAM_NAMES.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <label style={{ fontSize: 11, color: T.textDim, display: "block", marginBottom: 4 }}>
              <input type="radio" name={`move-mode-${row.membershipId}`} checked={moveMode === "date"} onChange={() => setMoveMode("date")} /> From date
            </label>
            {moveMode === "date" && (
              <input type="date" aria-label="Move effective date" min={`${season}-01-01`} max={`${season}-12-31`} value={moveDate} onChange={e => setMoveDate(e.target.value)} style={{ width: "100%", marginBottom: 8 }} />
            )}
            <label style={{ fontSize: 11, color: T.textDim, display: "block", marginBottom: 8 }}>
              <input type="radio" name={`move-mode-${row.membershipId}`} checked={moveMode === "whole"} onChange={() => setMoveMode("whole")} /> Whole season — the squad was wrong
            </label>
            <button onClick={() => handleMove(row)} disabled={busy || (moveMode === "date" && !dateWithinSeason(moveDate, season))} style={{ background: T.purple, border: "none", borderRadius: T.radiusSm, padding: "8px 14px", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>{busy ? "Moving…" : "Confirm move"}</button>
          </div>
        )}

        {endOpen === row.membershipId && (
          <div style={{ marginTop: 10, borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>
            <label htmlFor={`end-date-${row.membershipId}`} style={{ fontSize: 11, color: T.textDim, display: "block", marginBottom: 4 }}>End date</label>
            <input id={`end-date-${row.membershipId}`} type="date" min={`${season}-01-01`} max={`${season}-12-31`} value={endDate} onChange={e => setEndDate(e.target.value)} style={{ width: "100%", marginBottom: 8 }} />
            <label htmlFor={`end-reason-${row.membershipId}`} style={{ fontSize: 11, color: T.textDim, display: "block", marginBottom: 4 }}>Reason</label>
            <select id={`end-reason-${row.membershipId}`} value={endReason} onChange={e => setEndReason(e.target.value)} style={{ width: "100%", marginBottom: 8 }}>
              {END_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <button onClick={() => handleEnd(row)} disabled={busy || !dateWithinSeason(endDate, season)} style={{ background: T.purple, border: "none", borderRadius: T.radiusSm, padding: "8px 14px", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>{busy ? "Ending…" : "Confirm end"}</button>
          </div>
        )}

        {removeConfirm === row.membershipId && (
          <div style={{ marginTop: 10, borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>
            <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 8 }}>Remove {row.name} from this squad entirely? This is a correction — use it only when they were never really in this squad.</div>
            <button onClick={() => handleRemove(row)} disabled={busy} style={{ background: T.accent, border: "none", borderRadius: T.radiusSm, padding: "8px 14px", color: "#000", fontSize: 12, fontWeight: 700, cursor: "pointer", marginRight: 8 }}>{busy ? "Removing…" : "Confirm remove"}</button>
            <button onClick={resetRowState} style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: T.radiusSm, padding: "8px 14px", color: T.text, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Cancel</button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: "system-ui,sans-serif", color: T.text, paddingBottom: 40 }}>
      <div style={{ padding: "20px 16px", borderBottom: `1px solid ${T.border}` }}>
        <button onClick={onBack} style={{ background: "none", border: "none", color: T.textMuted, fontSize: 13, cursor: "pointer", marginBottom: 10 }}>← Back</button>
        <div style={{ fontWeight: 800, fontSize: 18 }}>Squads (Admin)</div>
        <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>Manage each team's squad by season.</div>
      </div>

      <div style={{ padding: 16 }}>
        {teamIdsError && <div style={{ fontSize: 12, color: T.accent, marginBottom: 14 }}>Couldn't load team ids — refresh and try again.</div>}

        <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <label htmlFor="squads-season" style={{ fontSize: 11, color: T.textDim, display: "block", marginBottom: 4 }}>Season</label>
            <select id="squads-season" value={season} onChange={e => setSeason(Number(e.target.value))} style={{ width: "100%" }}>
              {SQUADS_SEASON_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div style={{ flex: 2 }}>
            <label htmlFor="squads-team" style={{ fontSize: 11, color: T.textDim, display: "block", marginBottom: 4 }}>Team</label>
            <select id="squads-team" value={teamName} onChange={e => setTeamName(e.target.value)} style={{ width: "100%" }}>
              {SQUADS_TEAM_NAMES.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>

        {rowNotice && <div style={{ fontSize: 12, color: T.accent, marginBottom: 12 }}>{rowNotice}</div>}

        {squadLoading && <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 12 }}>Loading the squad…</div>}
        {squadError && <div style={{ fontSize: 12, color: T.accent, marginBottom: 12 }}>Couldn't load this squad — try again.</div>}
        {!squadLoading && Array.isArray(squad) && squad.length === 0 && (
          <div style={{ fontSize: 12, color: T.textDim, marginBottom: 12 }}>No players in the {teamName} {season} squad yet.</div>
        )}
        {Array.isArray(squad) && squad.map(renderRow)}

        {/* ── Add player ── */}
        <div style={{ background: T.surfaceHigh, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 14, marginTop: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.textDim, letterSpacing: 1, marginBottom: 8 }}>ADD PLAYER</div>
          <label htmlFor="squads-paste" style={{ fontSize: 11, color: T.textDim, display: "block", marginBottom: 4 }}>Paste their Rugby League Project player link</label>
          <input id="squads-paste" value={pasteValue} onChange={e => { setPasteValue(e.target.value); setPasteError(""); }} placeholder="Paste their player link, or a numeric id" style={{ width: "100%", marginBottom: 8, boxSizing: "border-box" }} />
          <button onClick={handlePasteLookup} disabled={lookupBusy || !pasteValue.trim()} style={{ background: T.purple, border: "none", borderRadius: T.radiusSm, padding: "8px 14px", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>{lookupBusy ? "Looking up…" : "Look up"}</button>
          {pasteError && <div style={{ fontSize: 12, color: T.accent, marginTop: 8 }}>{pasteError}</div>}

          {lookup?.found && lookup.player && (
            <div style={{ marginTop: 12, borderTop: `1px solid ${T.border}`, paddingTop: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                Already in the app: {lookup.player.name} ({lookup.memberships?.find(m => !m.ends_on)?.team_name || "no current team"})
              </div>
              <label htmlFor="squads-add-from" style={{ fontSize: 11, color: T.textDim, display: "block", marginTop: 8, marginBottom: 4 }}>From date (optional — blank starts the season)</label>
              <input id="squads-add-from" type="date" value={newStartsOn} onChange={e => setNewStartsOn(e.target.value)} style={{ width: "100%", marginBottom: 8 }} />
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button onClick={() => handleAddExisting("add")} disabled={addBusy} style={{ background: T.purple, border: "none", borderRadius: T.radiusSm, padding: "8px 14px", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Add to this squad from…</button>
                <button onClick={() => handleAddExisting("move")} disabled={addBusy} style={{ background: T.surfaceHigh, border: `1px solid ${T.border}`, borderRadius: T.radiusSm, padding: "8px 14px", color: T.text, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Move here from…</button>
              </div>
              {addError && <div style={{ fontSize: 12, color: T.accent, marginTop: 8 }}>{addError}</div>}
            </div>
          )}

          {lookup && !lookup.found && (
            <div style={{ marginTop: 12, borderTop: `1px solid ${T.border}`, paddingTop: 12 }}>
              <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 8 }}>No player has this reference yet — add them.</div>
              <label htmlFor="squads-new-name" style={{ fontSize: 11, color: T.textDim, display: "block", marginBottom: 4 }}>Name</label>
              <input id="squads-new-name" value={newName} onChange={e => setNewName(e.target.value)} style={{ width: "100%", marginBottom: 8, boxSizing: "border-box" }} />
              <label htmlFor="squads-new-dob" style={{ fontSize: 11, color: T.textDim, display: "block", marginBottom: 4 }}>Date of birth (optional — needed for namesakes)</label>
              <input id="squads-new-dob" type="date" value={newDob} onChange={e => setNewDob(e.target.value)} style={{ width: "100%", marginBottom: 8 }} />
              <label htmlFor="squads-new-starts" style={{ fontSize: 11, color: T.textDim, display: "block", marginBottom: 4 }}>Starts on (optional — blank starts the season)</label>
              <input id="squads-new-starts" type="date" value={newStartsOn} onChange={e => setNewStartsOn(e.target.value)} style={{ width: "100%", marginBottom: 8 }} />
              <button onClick={handleAddNew} disabled={addBusy} style={{ background: T.purple, border: "none", borderRadius: T.radiusSm, padding: "8px 14px", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>{addBusy ? "Adding…" : "Add player"}</button>
              {addError && <div style={{ fontSize: 12, color: T.accent, marginTop: 8 }}>{addError}</div>}
            </div>
          )}
        </div>

        {/* ── Roll squads ── */}
        <div style={{ marginTop: 16 }}>
          {!rollConfirming ? (
            <button onClick={() => setRollConfirming(true)} style={{ width: "100%", background: T.surfaceHigh, border: `1px solid ${T.border}`, borderRadius: T.radiusSm, padding: "11px 0", color: T.text, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Start {season + 1} squads</button>
          ) : (
            <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 14 }}>
              <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 10 }}>Carry every {season} open squad membership forward into {season + 1}? Players already in a {season + 1} squad are skipped.</div>
              <button onClick={handleRollSquads} disabled={rollBusy} style={{ background: T.accent, border: "none", borderRadius: T.radiusSm, padding: "10px 16px", color: "#000", fontSize: 13, fontWeight: 800, cursor: "pointer", marginRight: 8 }}>{rollBusy ? "Starting…" : `Start ${season + 1} squads`}</button>
              <button onClick={() => setRollConfirming(false)} style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: T.radiusSm, padding: "10px 16px", color: T.text, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Cancel</button>
            </div>
          )}
          {rollNotice && <div style={{ fontSize: 12, color: T.accent, marginTop: 10 }}>{rollNotice}</div>}
        </div>

        {/* ── Rebuild from RLP ── */}
        <div style={{ marginTop: 16, background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Rebuild from Rugby League Project</div>
          <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 10 }}>Reads every finished {season} match and recomputes who played for whom. Preview first, then apply.</div>
          <button onClick={handleBuildDryRun} disabled={buildBusy} style={{ background: T.surfaceHigh, border: `1px solid ${T.border}`, borderRadius: T.radiusSm, padding: "10px 16px", color: T.text, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>{buildBusy ? "Reading the feed…" : "Preview"}</button>
          {buildError && <div style={{ fontSize: 12, color: T.accent, marginTop: 10 }}>{buildError}</div>}
          {buildSummary && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 12, color: T.textMuted }}>
                {buildSummary.matchesRead} matches read · {buildSummary.playersMatched} matched · {buildSummary.playersCreated} created · {buildSummary.membershipsWritten} memberships written · {buildSummary.pruned} pruned
              </div>
              {buildSummary.changes.length > 0 && (
                <ul style={{ fontSize: 12, color: T.textMuted, margin: "8px 0", paddingLeft: 18 }}>
                  {buildSummary.changes.map((c, i) => {
                    const fromNames = (c.from_team_ids || []).map(id => teamNamesById.get(id) || id);
                    const toNames = (c.to_team_ids || []).map(id => teamNamesById.get(id) || id);
                    return (
                      <li key={`${c.external_ref}-${i}`}>{c.name}: {fromNames.length ? fromNames.join(", ") : "new"} → {toNames.length ? toNames.join(", ") : "no squad"}</li>
                    );
                  })}
                </ul>
              )}
              <button onClick={handleBuildApply} disabled={buildApplying || buildSummary.season !== season} style={{ background: T.accent, border: "none", borderRadius: T.radiusSm, padding: "10px 16px", color: "#000", fontSize: 13, fontWeight: 800, cursor: "pointer", marginTop: 4 }}>{buildApplying ? "Applying…" : "Apply"}</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── ADMIN: REPLAY CLOCK SCREEN (R14) ───────────────────────────────────────
// Visible only to admins while replay_mode is on. Every jump/set/real action
// re-syncs the shared appClock state so the rest of the app (fixtures, pack
// timers, the team prompt) reacts to it immediately — see appClock.js's
// onAppClock subscribers.
function replayClockErrorCopy(code) {
  switch (code) {
    case "forbidden": return "Admins only.";
    case "replay_off": return "The app clock is not in replay mode.";
    case "no_next_fixture": return "No fixture is ahead of the app clock.";
    case "invalid_at": return "Pick a date and time within the current season.";
    case "invalid_action": return "Unknown clock action.";
    case "clock_backwards": return "The clock can't go backwards while members have progress. Run a clean slate first.";
    case "unknown_member": return "That member no longer exists.";
    case "network_error": return "Couldn't reach the server. Check your connection and try again.";
    default: return "Something went wrong. Please try again.";
  }
}

function replayClockLabel(iso) {
  if (!iso) return "—";
  const date = new Date(iso);
  // Intl doesn't allow dateStyle/timeStyle alongside timeZoneName, so the
  // zone label is spelled out as individual components instead.
  return new Intl.DateTimeFormat("en-AU", {
    year: "numeric", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit", timeZoneName: "short",
  }).format(date);
}

export function AdminReplayScreen({ onBack }) {
  const [clock, setClock] = useState(() => getAppClock());
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(true);
  const [membersError, setMembersError] = useState(false);
  const [memberBusy, setMemberBusy] = useState({});
  const [actionBusy, setActionBusy] = useState(null); // the action currently in flight
  const [actionError, setActionError] = useState("");
  const [showSetTime, setShowSetTime] = useState(false);
  const [setAtValue, setSetAtValue] = useState("");
  const [confirmingReal, setConfirmingReal] = useState(false);

  useEffect(() => onAppClock((payload) => setClock(payload)), []);

  async function loadMembers() {
    setMembersLoading(true);
    const rows = await adminLocationStandins();
    setMembersLoading(false);
    if (rows === null) { setMembersError(true); return; }
    setMembersError(false);
    setMembers(rows);
  }

  useEffect(() => {
    syncAppClock();
    loadMembers();
  }, []);

  async function runAction(action, at = null) {
    setActionBusy(action);
    setActionError("");
    const res = await adminAppClock(action, at);
    await syncAppClock();
    setActionBusy(null);
    if (res.ok) {
      setShowSetTime(false);
      setConfirmingReal(false);
    } else {
      setActionError(replayClockErrorCopy(res.code));
    }
  }

  async function toggleStandin(userId, on) {
    setMemberBusy((prev) => ({ ...prev, [userId]: true }));
    const res = await adminSetLocationStandin(userId, on);
    await syncAppClock();
    setMemberBusy((prev) => ({ ...prev, [userId]: false }));
    if (res.ok) {
      setMembers((prev) => prev.map((m) => (m.userId === userId ? { ...m, locationStandin: res.locationStandin } : m)));
    } else {
      setActionError(replayClockErrorCopy(res.code));
    }
  }

  const nextFixture = clock?.next_fixture ?? null;
  const nextFixtureView = nextFixture
    ? { kickoffUtc: new Date(nextFixture.kickoff_at), venue: { timezone: nextFixture.venue?.timezone } }
    : null;
  const buttonStyle = (busy) => ({
    width: "100%", background: T.purple, border: "none", borderRadius: T.radiusSm, padding: "13px 0",
    color: "#fff", fontSize: 14, fontWeight: 700, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1, marginTop: 8,
  });

  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: "system-ui,sans-serif", color: T.text, paddingBottom: 40 }}>
      <div style={{ padding: "20px 16px", borderBottom: `1px solid ${T.border}` }}>
        <button onClick={onBack} style={{ background: "none", border: "none", color: T.textMuted, fontSize: 13, cursor: "pointer", marginBottom: 10 }}>← Back</button>
        <div style={{ fontWeight: 800, fontSize: 18 }}>Replay clock (Admin)</div>
      </div>
      <div style={{ padding: 16 }}>
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 16, marginBottom: 14 }}>
          <div style={{ fontSize: 12, color: T.textMuted }}>App time</div>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{replayClockLabel(clock?.app_now)}</div>
          <div style={{ fontSize: 12, color: T.textMuted }}>Real time</div>
          <div style={{ fontSize: 14, color: T.textMuted, marginBottom: 8 }}>{replayClockLabel(clock?.real_now)}</div>
          <div style={{ fontSize: 13, fontWeight: 700 }}>
            {clock?.current_round == null ? "Before round 1" : `Round ${clock.current_round}`}
          </div>
          {nextFixtureView && (
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 8 }}>
              Next: {nextFixture.home_ref} v {nextFixture.away_ref} — {nextFixture.venue?.name}
              <br />
              {kickoffVenueLabel(nextFixtureView)}
              {kickoffViewerLabel(nextFixtureView) && ` (your time: ${kickoffViewerLabel(nextFixtureView)})`}
            </div>
          )}
        </div>

        <button disabled={actionBusy != null} onClick={() => runAction("next_game")} style={buttonStyle(actionBusy === "next_game")}>
          {actionBusy === "next_game" ? "Jumping…" : "Jump to next game"}
        </button>
        <button disabled={actionBusy != null} onClick={() => runAction("next_round")} style={buttonStyle(actionBusy === "next_round")}>
          {actionBusy === "next_round" ? "Jumping…" : "Jump to next round"}
        </button>
        <button disabled={actionBusy != null} onClick={() => runAction("end_round")} style={buttonStyle(actionBusy === "end_round")}>
          {actionBusy === "end_round" ? "Jumping…" : "Jump to end of round"}
        </button>

        <button disabled={actionBusy != null} onClick={() => setShowSetTime((v) => !v)} style={buttonStyle(false)}>
          Set app time…
        </button>
        {showSetTime && (
          <div style={{ background: T.surfaceHigh, borderRadius: T.radiusSm, padding: 12, marginTop: 8 }}>
            <input
              type="datetime-local"
              aria-label="Set app time"
              value={setAtValue}
              onChange={(e) => setSetAtValue(e.target.value)}
              style={{ width: "100%", background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radiusSm, padding: "10px 12px", color: T.text, fontSize: 14, marginBottom: 10, boxSizing: "border-box" }}
            />
            <button
              disabled={actionBusy != null || !setAtValue}
              onClick={() => runAction("set", new Date(setAtValue))}
              style={{ width: "100%", background: T.accent, border: "none", borderRadius: T.radiusSm, padding: "11px 0", color: "#000", fontSize: 13, fontWeight: 800, cursor: "pointer" }}
            >
              {actionBusy === "set" ? "Applying…" : "Apply"}
            </button>
          </div>
        )}

        {!confirmingReal ? (
          <button disabled={actionBusy != null} onClick={() => setConfirmingReal(true)} style={{ ...buttonStyle(false), background: T.surfaceHigh, color: T.text, border: `1px solid ${T.border}` }}>
            Back to real time
          </button>
        ) : (
          <div style={{ background: T.surfaceHigh, borderRadius: T.radiusSm, padding: 12, marginTop: 8 }}>
            <div style={{ fontSize: 13, color: T.text, marginBottom: 10 }}>Set the app clock back to real time?</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setConfirmingReal(false)} style={{ flex: 1, background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radiusSm, padding: "10px 0", color: T.textMuted, fontSize: 13, cursor: "pointer" }}>Cancel</button>
              <button disabled={actionBusy != null} onClick={() => runAction("real")} style={{ flex: 1, background: T.accent, border: "none", borderRadius: T.radiusSm, padding: "10px 0", color: "#000", fontSize: 13, fontWeight: 800, cursor: "pointer" }}>
                {actionBusy === "real" ? "Applying…" : "Back to real time"}
              </button>
            </div>
          </div>
        )}

        <div style={{ fontSize: 12, color: T.textDim, marginTop: 12, lineHeight: 1.5 }}>
          Once anyone has logged a game or claimed a pack, the clock only moves forward. To start again, run a clean slate first.
        </div>

        {actionError && <div role="alert" style={{ color: T.red, fontSize: 12, marginTop: 12 }}>{actionError}</div>}

        <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginTop: 24, marginBottom: 10 }}>Members</div>
        {membersLoading && <div style={{ fontSize: 13, color: T.textMuted }}>Loading members…</div>}
        {!membersLoading && membersError && (
          <div style={{ fontSize: 13, color: T.textMuted }}>
            Couldn't load members. <button onClick={loadMembers} style={{ background: "none", border: "none", padding: 0, color: T.accent, fontWeight: 700, cursor: "pointer" }}>Retry</button>
          </div>
        )}
        {!membersLoading && !membersError && members.map((m) => (
          <div key={m.userId} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${T.border}` }}>
            <div style={{ fontSize: 13, color: T.text }}>{m.displayName}{m.isAdmin ? " (admin)" : ""}</div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: T.textMuted, cursor: "pointer" }}>
              Check in from anywhere (test mode)
              <input
                type="checkbox"
                checked={m.locationStandin}
                disabled={Boolean(memberBusy[m.userId])}
                onChange={(e) => toggleStandin(m.userId, e.target.checked)}
              />
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── ROUND 13 — REGISTRATION SWITCH SURFACES ──────────────────────────────────
// Declared above the app so neither adds inputs to the admin region above.

// Signed-out coming-soon card while sign-ups are closed: leave an email for the
// launch list. The address is held only in this component's state while typed.
function InterestPanel() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState("");
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  async function submit() {
    if (busy) return;
    setError("");
    if (!email.trim()) return setError("Enter a valid email address.");
    if (!consent) return setError("Tick the box so we can email you about the launch.");
    setBusy(true);
    const res = await registerInterest(email.trim(), consent);
    if (!alive.current) return;
    setBusy(false);
    if (res.ok === true) return setDone(true);
    if (res.error === "consent_required") return setError("Tick the box so we can email you about the launch.");
    setError(res.error === "invalid_email" ? "Enter a valid email address." : "Couldn't save your email just now. Please try again in a minute.");
  }

  return (
    <div style={{ background:T.surface, borderRadius:T.radiusLg, padding:24, border:`1px solid ${T.border}`, marginTop:16 }}>
      <div style={{ fontSize:15, fontWeight:800, color:T.text, marginBottom:6 }}>New sign-ups are closed for now</div>
      <div style={{ fontSize:13, color:T.textMuted, marginBottom:14 }}>Leave your email and we'll let you know when SKOHR opens.</div>
      {done ? (
        <div role="status" style={{ color:T.accentDark, fontSize:13 }}>Thanks — we'll email you when sign-ups open.</div>
      ) : (
        <>
          <input type="email" placeholder="Email" aria-label="Email for updates" value={email} onChange={e=>setEmail(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&submit()}
            style={{ width:"100%", background:T.surfaceHigh, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"11px 14px", color:T.text, fontSize:14, marginBottom:10, boxSizing:"border-box", outline:"none" }}/>
          <div style={{ display:"flex", alignItems:"flex-start", gap:8, marginBottom:10 }}>
            <input type="checkbox" id="interest-consent" checked={consent} onChange={e=>setConsent(e.target.checked)} style={{ marginTop:2 }}/>
            <label htmlFor="interest-consent" style={{ color:T.textMuted, fontSize:13 }}>I'm happy for SKOHR to email me about the launch.</label>
          </div>
          {error && <div role="alert" style={{ color:T.red, fontSize:12, marginBottom:10 }}>{error}</div>}
          <button onClick={submit} disabled={busy} style={{ width:"100%", background:T.accent, border:"none", borderRadius:T.radiusSm, padding:"13px 0", color:"#000", fontSize:15, fontWeight:800, cursor:busy?"default":"pointer", opacity:busy?0.6:1 }}>
            {busy ? "Please wait…" : "Register your interest"}
          </button>
        </>
      )}
    </div>
  );
}

// One CSV field: always double-quoted (RFC 4180, `"` doubled); a leading
// = + - @ tab or CR gets an apostrophe inside the quotes so a spreadsheet
// won't read it as a formula. The stored value is never altered.
function csvField(value) {
  let s = String(value ?? "");
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return `"${s.replace(/"/g, '""')}"`;
}

function interestCsv(rows) {
  const lines = ["email,consented_at,created_at", ...rows.map(r => `${csvField(r.email)},${csvField(r.consented_at)},${csvField(r.created_at)}`)];
  return lines.join("\r\n") + "\r\n";
}

// The yyyy-mm-dd of the app clock's now (Brisbane keeps AEST all year).
function appDateStamp() {
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-CA", { timeZone: "Australia/Brisbane", year: "numeric", month: "2-digit", day: "2-digit" })
    .formatToParts(appNow()).map(p => [p.type, p.value]));
  return `${parts.year}-${parts.month}-${parts.day}`;
}

// More tab (admin): the sign-up switch and the interest list's CSV export.
function RegistrationAdminPanel() {
  const [open, setOpen] = useState(null); // null = not loaded
  const [statusError, setStatusError] = useState(false);
  const [count, setCount] = useState(null);
  const [countError, setCountError] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  // `life` is bumped on every effect setup and cleanup, so a reply from a
  // discarded (StrictMode) setup or an unmounted panel is ignored. `statusSeq`
  // and `countSeq` order each resource: a read takes a ticket when it starts, a
  // successful toggle / CSV refresh takes one when it lands, and only the newest
  // ticket may write — an obsolete read can never overwrite a newer result.
  const life = useRef(0);
  const statusSeq = useRef(0);
  const countSeq = useRef(0);

  async function loadStatus() {
    const l = life.current, t = ++statusSeq.current;
    setStatusError(false);
    const res = await fetchRegistrationStatus();
    if (l !== life.current || t !== statusSeq.current) return;
    if (res.ok) setOpen(res.open); else setStatusError(true);
  }
  async function loadCount() {
    const l = life.current, t = ++countSeq.current;
    setCountError(false);
    const res = await adminInterestRegistrations();
    if (l !== life.current || t !== countSeq.current) return;
    if (res.ok) setCount(res.count); else setCountError(true);
  }
  useEffect(() => {
    life.current += 1;
    loadStatus();
    loadCount();
    return () => { life.current += 1; };
  }, []);

  async function toggle() {
    if (busy) return;
    setBusy(true);
    setError("");
    setNotice("");
    const l = life.current;
    const res = await adminSetRegistration(!open);
    if (l !== life.current) return;
    setBusy(false);
    setConfirming(false);
    if (res.ok) { statusSeq.current += 1; setStatusError(false); setOpen(res.open); } else setError("Couldn't change the sign-up setting. Please try again.");
  }

  async function download() {
    if (busy) return;
    setBusy(true);
    setError("");
    setNotice("");
    const l = life.current;
    const res = await adminInterestRegistrations();
    if (l !== life.current) return;
    setBusy(false);
    if (!res.ok) return setError("Couldn't load the interest list. Please try again.");
    countSeq.current += 1;
    setCount(res.count);
    setCountError(false);
    const url = URL.createObjectURL(new Blob([interestCsv(res.rows)], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `skohr-interest-${appDateStamp()}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    setNotice("Downloaded.");
  }

  const smallBtn = { background:T.surfaceHigh, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"8px 14px", color:T.text, fontSize:13, fontWeight:700, cursor:"pointer" };
  return (
    <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.radius, padding:16, marginTop:14 }}>
      <div style={{ fontSize:13, fontWeight:700, color:T.text, marginBottom:10 }}>Registration (admin)</div>
      {open !== null && <div style={{ fontSize:13, color:T.text, marginBottom:10 }}>{open ? "Sign-ups: Open" : "Sign-ups: Closed"}</div>}
      {statusError && (
        <div role="alert" style={{ fontSize:12, color:T.red, marginBottom:10 }}>
          Couldn't load the registration status — try again. <button onClick={loadStatus} style={smallBtn}>Retry</button>
        </div>
      )}
      {open !== null && !confirming && (
        <button onClick={() => { setError(""); setNotice(""); setConfirming(true); }} disabled={busy} style={{ ...smallBtn, marginBottom:10 }}>{open ? "Close sign-ups" : "Open sign-ups"}</button>
      )}
      {open !== null && confirming && (
        <div style={{ marginBottom:10 }}>
          <div style={{ fontSize:13, color:T.text, marginBottom:6 }}>Are you sure?</div>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={toggle} disabled={busy} style={{ ...smallBtn, background:T.accent, border:"none", color:"#000", opacity:busy?0.6:1 }}>Confirm</button>
            <button onClick={() => setConfirming(false)} disabled={busy} style={smallBtn}>Cancel</button>
          </div>
        </div>
      )}
      {countError ? (
        <div role="alert" style={{ fontSize:12, color:T.red, marginBottom:10 }}>
          Couldn't load the interest list — try again. <button onClick={loadCount} style={smallBtn}>Retry</button>
        </div>
      ) : (
        <div style={{ fontSize:13, color:T.textMuted, marginBottom:10 }}>{count === null ? "Interested emails: …" : `Interested emails: ${count}`}</div>
      )}
      <button onClick={download} disabled={busy} style={smallBtn}>Download CSV</button>
      {notice && <div role="status" style={{ color:T.accentDark, fontSize:12, marginTop:10 }}>{notice}</div>}
      {error && <div role="alert" style={{ color:T.red, fontSize:12, marginTop:10 }}>{error}</div>}
    </div>
  );
}

export default function App() {
  const [db, setDb] = useState(() => loadDB());
  // `user` = the authenticated identity from Supabase (id, email, name, isAdmin),
  // NOT a localStorage record. null until a session is restored/created.
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  // R-1 (closure remediation): while a signed-in session waits for its first
  // successful clock sync (the bootstrap gate), the loading screen reports
  // "Syncing…" instead of the generic "Loading…". Cleared once the identity
  // publishes; a same-identity repeat auth event never sets it.
  const [authSyncing, setAuthSyncing] = useState(false);
  const [authBusy, setAuthBusy] = useState(false);
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordNotice, setPasswordNotice] = useState("");
  const [changeNotice, setChangeNotice] = useState("");
  const [changeError, setChangeError] = useState("");
  const [authNotice, setAuthNotice] = useState("");
  const [tab, setTab] = useState("home");
  useEffect(() => { setPasswordNotice(""); }, [tab]);
  const [authMode, setAuthMode] = useState("login");
  const [authForm, setAuthForm] = useState({ email:"", password:"", name:"" });
  const [authError, setAuthError] = useState("");
  // R15 — the registration switch as the signed-out visitor sees it: null while
  // loading, else a boolean (a failed or malformed status counts as closed).
  // Fetched once per signed-out transition, never while the session restores;
  // `registrationGen` discards a reply that lands after sign-in or a newer
  // transition. Nothing in bootstrap, sign-in or recovery awaits it.
  const [registrationOpen, setRegistrationOpen] = useState(null);
  const registrationGen = useRef(0);
  const signedOut = !authLoading && !user;
  useEffect(() => {
    if (!signedOut) return undefined;
    const gen = ++registrationGen.current;
    setRegistrationOpen(null);
    fetchRegistrationStatus().then(res => {
      if (gen !== registrationGen.current) return;
      setRegistrationOpen(res.ok ? res.open : false);
    });
    return () => { registrationGen.current += 1; };
  }, [signedOut]);
  // A stale register mode cannot outlive a closed switch (reset mode is kept).
  useEffect(() => {
    if (registrationOpen !== true && authMode === "register") setAuthMode("login");
  }, [registrationOpen, authMode]);
  const [modal, setModal] = useState(null); // { type, data }
  const [packGrants, setPackGrants] = useState([]);
  const [packBusy, setPackBusy] = useState(false);
  const packMutations = useRef(0);
  const [nextStandardAt, setNextStandardAt] = useState(null);
  // R12 reactive refresh: bumped on every applied clock sync so the pack-grant
  // and standard-pack-timer reads below recompute without a reload — a jump
  // can open a grant's next-eligible window or surface a fresh countdown.
  const [clockTick, setClockTick] = useState(0);
  const [epochTick, setEpochTick] = useState(0);
  useEffect(() => onAppClock((_, change) => {
    if (change?.reset) {
      setDb({});
      setEpochTick(t => t + 1);
      setModal(null);
      setDrilldown(null);
      setTeamPromptDismissedThisSession(false);
    }
    setClockTick(t => t + 1);
  }), []);
  const [cardFilter, setCardFilter] = useState("all");
  const [cardView, setCardView] = useState("collection");
  const [badgeSvgCache, setBadgeSvgCache] = useState({});
  const [tradeRefresh, setTradeRefresh] = useState(0);
  const [tradeError, setTradeError] = useState(null);
  const [tradeNotice, setTradeNotice] = useState(null);
  const tradeState = useTradeBoard(user, tab, tradeRefresh + clockTick, epochTick);
  const board = tradeState.board;
  const tradeUser = useRef(user?.id);
  tradeUser.current = user?.id;
  useEffect(()=>{ setTradeError(null); setTradeNotice(null); },[user?.id]);
  const [refreshTick, setRefreshTick] = useState(0);
  useEffect(() => {
    // Re-check for newly-finished games needing player data every 2 minutes while app is open,
    // and also immediately on app load/reload — catches games that finished after the app was closed
    const interval = setInterval(() => setRefreshTick(t => t + 1), 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);
  // Pending + released server-verified XP (Phase 4 round 5 wave C). The signed-in
  // user's xp_ledger rows, summed by status ('pending' written by wave A server
  // check-ins; 'released' once an admin confirms and releases the round).
  // Display-only and ADDITIVE — never folded into the transitional client-side
  // total (calcPointsBreakdown). Refreshed by fetching when the home tab is
  // entered, keyed on the active tab and the user id (NO event bus, NO polling).
  const [pendingXp, setPendingXp] = useState({ pending: { total: 0, count: 0 }, released: { total: 0, count: 0 } });
  useEffect(() => {
    if ((tab !== "home" && !epochTick) || !user?.id) return;
    const readGeneration = getClockGeneration();
    let active = true;
    (async () => {
      const res = await fetchXpLedgerSummary(user.id);
      if (active && readGeneration === getClockGeneration()) setPendingXp(res);
    })();
    return () => { active = false; };
  }, [tab, user?.id, clockTick, epochTick]);
  // Round 7 — earned badges + earning events from `user_badges` /
  // `user_badge_events` (RLS owner-select). Loaded when the signed-in user is
  // known and refreshed whenever the Badges tab is opened (cheap; RLS-scoped).
  // Display-only; the client never writes reward state (SEC-3).
  const myBadges = useMyBadges(user, tab, epochTick);
  const [drilldown, setDrilldown] = useState(null);

  // ── Round 9: admin member-team tool state (More tab) ──
  // Admins read every profile row through RLS (id/display_name/supported_team_id)
  // and can set any member's team through admin_set_member_team. Loaded when an
  // admin opens the More tab; sorted by display_name. The member's current team
  // is mirrored into the option text ("{name} — {team or No team}").
  const [adminProfiles, setAdminProfiles] = useState([]);
  const [adminMemberId, setAdminMemberId] = useState("");
  const [adminTeamName, setAdminTeamName] = useState("");
  const [adminTeamBusy, setAdminTeamBusy] = useState(false);
  const [adminTeamNotice, setAdminTeamNotice] = useState("");
  const [adminTeamError, setAdminTeamError] = useState("");
  useEffect(() => {
    if (tab !== "more" || !user?.isAdmin) return;
    const readGeneration = getClockGeneration();
    let active = true;
    (async () => {
      // Admins read every profile row through RLS (the member roster). The
      // teams table has no authenticated grant, so the supported-team NAME for
      // the option text is resolved from the public_progress read model
      // (SEC-8 allowlist, authenticated-readable, one row per profile).
      const [{ data: profileRows, error: profileErr }, { data: progressRows, error: progressErr }] = await Promise.all([
        supabase.from("profiles").select("id, display_name, supported_team_id"),
        supabase.from("public_progress").select("id, supported_team_name"),
      ]);
      if (!active || readGeneration !== getClockGeneration()) return;
      if (profileErr) { setAdminTeamError(leaderboardErrorCopy("server_error")); return; }
      const nameById = new Map();
      for (const r of (progressRows || [])) nameById.set(r.id, r.supported_team_name);
      const rows = (profileRows || [])
        .map((r) => ({ id: r.id, displayName: r.display_name, teamName: nameById.get(r.id) ?? null }))
        .sort((a, b) => String(a.displayName).localeCompare(String(b.displayName)));
      setAdminProfiles(rows);
      if (rows.length && !adminMemberId) setAdminMemberId(rows[0].id);
      if (progressErr) setAdminTeamError(leaderboardErrorCopy("server_error"));
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, user?.isAdmin]);
  async function handleAdminSetTeam() {
    if (!adminMemberId) return;
    setAdminTeamBusy(true); setAdminTeamError(""); setAdminTeamNotice("");
    const teamName = !adminTeamName || adminTeamName === "__none__" ? null : adminTeamName;
    const res = await adminSetMemberTeam(adminMemberId, teamName);
    setAdminTeamBusy(false);
    if (res.ok) {
      const member = adminProfiles.find((p) => p.id === res.userId);
      setAdminTeamNotice(`Team updated for ${member?.displayName ?? "that member"}.`);
      // Mirror the server change into the loaded list so the option text stays current.
      setAdminProfiles((prev) => prev.map((p) => p.id === res.userId ? { ...p, teamName: res.teamName } : p));
    } else {
      setAdminTeamError(leaderboardErrorCopy(res.code));
    }
  }

;


  const persist = useCallback((newDb) => { setDb(newDb); saveDB(newDb); }, []);

  // ── SUPABASE SESSION ──
  // Establish identity from Supabase Auth (replaces the plaintext-password
  // localStorage login, spec §4/§5). Ensures a local game-state record exists
  // for the authenticated user id (transitional; game state moves server-side
  // in later phases) and loads the admin role from the profiles table.
  useEffect(() => {
    let active = true;
    // Session generation guard: each new-identity / sign-out applySession call
    // bumps a counter; after every await a superseded call (a newer auth event
    // landed) bails out so a late bootstrap for the previous user can't
    // overwrite the active session. A same-identity repeat event (e.g.
    // TOKEN_REFRESHED) does NOT bump it — the user and screen stay as they are.
    let sessionGen = 0;
    let currentSession = null;
    let publishKey = null;
    // The identity currently published via setUser (null until the first
    // successful bootstrap). Drives the R-1 gate: only an actual identity
    // change blanks the app and bootstraps on the clock.
    let publishedUserId = null;
    // R-1 (B): bounded retry for the bootstrap sync while a signed-in session
    // is waiting on its first successful clock sync. Cleared on success,
    // sign-out, account switch and unmount.
    let bootstrapRetryTimer = null;
    let bootstrapRetryAttempt = 0;
    const BOOTSTRAP_DELAYS = [2000, 4000, 8000, 16000];
    function clearBootstrapRetry() {
      if (bootstrapRetryTimer !== null) { clearTimeout(bootstrapRetryTimer); bootstrapRetryTimer = null; }
      bootstrapRetryAttempt = 0;
    }
    const unsubscribeClock = onAppClock(() => {
      if (currentSession?.user) publishSession(currentSession.user, sessionGen);
    });
    async function applySession(session) {
      if (!active) return;
      currentSession = session;
      if (!session?.user) {
        // Sign-out: invalidate any in-flight bootstrap, drop the identity and
        // the retry timer, and leave the clock unsigned. Behaves exactly as
        // before the remediation.
        ++sessionGen;
        clearBootstrapRetry();
        setAuthSyncing(false);
        resetAppClockForAccount(null);
        publishedUserId = null;
        setUser(null);
        setAuthLoading(false);
        return;
      }
      const authUser = session.user;
      if (publishedUserId === authUser.id) {
        // Same identity (e.g. Supabase's periodic TOKEN_REFRESHED): keep the
        // user and the screen exactly as they are. Re-sync the clock in the
        // background; the onAppClock listener re-publishes through the guarded
        // path if the clock actually moved (publishKey dedups a no-op sync).
        // sessionGen is intentionally NOT bumped — there is no in-flight
        // bootstrap to supersede, and the listener re-publishes with the
        // already-published generation so a no-op refresh doesn't re-fetch.
        await syncAppClock();
        return;
      }
      // New identity (or first sign-in): blank the app and gate on a successful
      // clock sync before any profile fetch, user publish or time-dependent read.
      const myGen = ++sessionGen;
      setUser(null);
      setAuthLoading(true);
      setAuthSyncing(true);
      resetAppClockForAccount(authUser.id);
      clearBootstrapRetry();
      await bootstrapSync(authUser, myGen);
    }
    // R-1 (B): the bootstrap sync is retried on a bounded backoff (2 s, 4 s,
    // 8 s, 16 s, then every 30 s) until a sync succeeds or the session ends.
    // setTimeout delays are used (no Date.now()); the clock module's own
    // timing source remains the only behaviour-bearing time read.
    async function bootstrapSync(authUser, myGen) {
      if (!active || sessionGen !== myGen) return;
      const synced = await syncAppClock();
      if (!active || sessionGen !== myGen) return;
      if (synced) {
        clearBootstrapRetry();
        await publishSession(authUser, myGen);
        return;
      }
      const delay = bootstrapRetryAttempt < BOOTSTRAP_DELAYS.length
        ? BOOTSTRAP_DELAYS[bootstrapRetryAttempt]
        : 30000;
      bootstrapRetryAttempt += 1;
      bootstrapRetryTimer = setTimeout(() => {
        bootstrapRetryTimer = null;
        void bootstrapSync(authUser, myGen);
      }, delay);
    }
    async function publishSession(authUser, myGen) {
      const readGeneration = getClockGeneration();
      const key = `${myGen}:${readGeneration}`;
      if (publishKey === key) return;
      publishKey = key;
      const profile = await fetchProfile(authUser.id);
      if (!active || sessionGen !== myGen || readGeneration !== getClockGeneration()) return;
      const progress = await fetchMyProgress(authUser.id);
      if (!active || sessionGen !== myGen || readGeneration !== getClockGeneration()) return;
      const name = profile?.display_name
        || authUser.user_metadata?.name
        || (authUser.email ? authUser.email.split("@")[0] : "Player");
      // Create the local row and mirror the server team lock before signing in.
      // When the progress read fails (null), preserve the last known local
      // supportedTeam rather than clearing it; a null profile season still maps
      // to undefined so a new account prompts.
      setDb(prev => {
        const u = prev.users?.[authUser.id] ?? defaultGameState(authUser.id, name);
        const next = { ...prev, users: { ...(prev.users||{}), [authUser.id]: {
          ...u,
          teamConfirmedSeason: profile ? (profile.team_confirmed_season ?? undefined) : u.teamConfirmedSeason,
          supportedTeam: progress ? progress.supportedTeamName : (u.supportedTeam ?? null),
        } } };
        saveDB(next);
        return next;
      });
      setUser({
        id: authUser.id,
        email: authUser.email,
        name,
        // Admin is a server-side role/flag now, NOT a hard-coded email (spec §5).
        isAdmin: Boolean(profile?.is_admin),
      });
      setAuthLoading(false);
      setAuthSyncing(false);
      publishedUserId = authUser.id;
    }
    supabase.auth.getSession().then(({ data }) => applySession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY") setRecoveryMode(true);
      applySession(session);
    });
    return () => { active = false; clearBootstrapRetry(); unsubscribeClock(); sub.subscription.unsubscribe(); };
  }, []);

  // ── MIGRATE old accounts: clear 2025 games ──
  useEffect(() => {
    if(!user || !db.users) return;
    const u = db.users[user.id];
    if(!u) return;
    let changed = false;
    let patch = {};

    // Populate allTimeAttended from attended for existing users
    if(!u.allTimeAttended && (u.attended||[]).length > 0) {
      patch.allTimeAttended = [...(u.attended||[])];
      changed = true;
    }
    // Clear any 2025 game IDs (ids < 100)
    const cleanAttended = (u.attended || []).filter(id => id >= 100);
    if(cleanAttended.length !== (u.attended||[]).length) {
      patch.attended = cleanAttended;
      changed = true;
    }
    if(changed) {
      const newDb = { ...db, users: { ...db.users, [user.id]: { ...u, ...patch } } };
      const readGeneration = getClockGeneration();
      setTimeout(() => { if (readGeneration === getClockGeneration()) persist(newDb); }, 0);
    }
  }, [user?.id]);

  const me = user ? (db.users||{})[user.id] : null;
  const ownedCards = me?.cards || [];
  const cardCopies = me?.cardCopies || Object.fromEntries(ownedCards.map(id => [id, 1]));
  const tradesCompleted = board?.tradesCompleted || 0;
  const updateTradeMirror = useRef(updateMe);
  updateTradeMirror.current = updateMe;
  useEffect(() => {
    if (board && me && me.tradesCompleted !== tradesCompleted) updateTradeMirror.current({ tradesCompleted });
  }, [board, me, tradesCompleted]);
  const lockedCopies = me?.lockedCopies || {};
  // Round 12 (R4) — Home stats are server truth. The four tiles, the belt rank,
  // the pending line and the tile drill-down lists all read `stats`, the
  // camelCased `get_my_stats()` response held by `useMyStats`. The local
  // `gameState` model (attended/allTimeAttended/gamePlayers/confirmedGames) is
  // no longer read for any of these numbers — check-in writes nothing to it and
  // an admin release is picked up on the next fetch (Home entry, after a
  // check-in, every clock change and the 120-second refresh tick).
  const [statsRefreshTick, setStatsRefreshTick] = useState(0);
  const stats = useMyStats(user, tab, refreshTick, clockTick, epochTick, statsRefreshTick);
  const myXp = useMyXp(user, tab, refreshTick + clockTick);

  // ── AUTH (Supabase — replaces plaintext-password localStorage login) ──
  async function handlePasswordUpdate(password) {
    if (passwordBusy) return;
    setPasswordBusy(true);
    setPasswordError("");
    setPasswordNotice("");
    try {
      const { error } = await updatePassword(password);
      if (error) return setPasswordError(friendlyAuthError(error));
      setPasswordNotice(recoveryMode ? "Password updated. You're signed in." : "Password updated.");
      setRecoveryMode(false);
    } catch (error) {
      setPasswordError(friendlyAuthError(error));
    } finally {
      setPasswordBusy(false);
    }
  }

  async function handlePasswordChange(password) {
    if (passwordBusy) return;
    setPasswordBusy(true);
    setChangeError("");
    setChangeNotice("");
    try {
      const { error } = await updatePassword(password);
      if (error) return setChangeError(friendlyAuthError(error));
      setChangeNotice("Password updated.");
    } catch (error) {
      setChangeError(friendlyAuthError(error));
    } finally {
      setPasswordBusy(false);
    }
  }

  async function handleAuth() {
    setAuthError("");
    setAuthNotice("");
    if(authBusy) return;
    if(authMode === "reset") {
      if(!authForm.email.trim()) return setAuthError("Email required.");
      setAuthBusy(true);
      try {
        const { error } = await requestPasswordReset(authForm.email.trim());
        if(error) return setAuthError(friendlyAuthError(error));
        setAuthNotice("If that email has an account, a reset link is on its way. Check your inbox (and spam).");
        setAuthMode("login");
      } catch (error) {
        setAuthError(friendlyAuthError(error));
      } finally {
        setAuthBusy(false);
      }
    } else if(authMode === "register") {
      if(registrationOpen !== true) return setAuthError("New sign-ups are closed for now.");
      if(!authForm.name || !authForm.email || !authForm.password) return setAuthError("All fields required.");
      setAuthBusy(true);
      const gen = registrationGen.current;
      const { error, needsConfirmation } = await signUp(authForm.email.trim(), authForm.password, authForm.name.trim());
      if(error && /Database error (saving|creating) new user/i.test(error.message || "")) {
        // The trigger refuses new accounts while closed, but this generic
        // failure has other causes — say "closed" only once the status confirms it.
        const status = await fetchRegistrationStatus();
        setAuthBusy(false);
        if(gen !== registrationGen.current) return;
        if(status.ok && status.open === false) {
          setRegistrationOpen(false);
          setAuthMode("login");
          return setAuthError("New sign-ups are closed for now.");
        }
        return setAuthError("Couldn't create your account just now. Please try again in a minute.");
      }
      setAuthBusy(false);
      if(error) return setAuthError(friendlyAuthError(error));
      if(needsConfirmation) {
        setAuthNotice("Account created. Check your email to confirm, then sign in.");
        setAuthMode("login");
      }
      // On success with a session, onAuthStateChange sets the user.
    } else {
      if(!authForm.email || !authForm.password) return setAuthError("Email and password required.");
      setAuthBusy(true);
      const { error } = await signIn(authForm.email.trim(), authForm.password);
      setAuthBusy(false);
      if(error) return setAuthError(friendlyAuthError(error));
      // onAuthStateChange sets the user.
    }
  }

  async function logout() {
    await signOut();
    setUser(null);
    setTab("home");
  }

  function updateMe(patch) {
    const uid = me.id;
    setDb(prev => {
      const u = prev.users?.[uid];
      if (!u) return prev;
      const next = { ...prev, users: { ...prev.users, [uid]: { ...u, ...patch } } };
      saveDB(next);
      return next;
    });
  }

  // One-shot bridge import followed by server-truth replacement. A null ids
  // result means the read failed, so the local mirror must remain untouched.
  useEffect(() => {
    if(!user?.id || !me) return;
    const readGeneration = getClockGeneration();
    let active = true;
    (async () => {
      const markerKey = `bridgeImported:${user.id}`;
      const localIds = me.cards || [];
      if(localIds.length > 0 && !localStorage.getItem(markerKey)) {
        const imported = await importBridge(localIds);
        // Account-switch isolation: a sign-out / sign-in on this device while
        // the bridge import was pending runs this effect's cleanup (active=false)
        // and re-runs it for the new user. The continuation must not issue reads
        // (or stamp the marker) for the user captured at effect time — without
        // this check it would call fetchMyGrants with the PREVIOUS user's id
        // before the activity guard below could stop it. Check after every await.
        if(!active || readGeneration !== getClockGeneration()) return;
        if(imported.ok || imported.code === "already_imported") {
          localStorage.setItem(markerKey, "true");
        }
      }
      let collectionSnapshot = packMutations.current;
      let [collection, grants] = await Promise.all([fetchMyCollection(), fetchMyGrants(user.id)]);
      if(!active || readGeneration !== getClockGeneration()) return;
      setPackGrants(grants);
      if(collectionSnapshot !== packMutations.current) {
        collectionSnapshot = packMutations.current;
        collection = await fetchMyCollection();
        if(!active || readGeneration !== getClockGeneration()) return;
        if(collectionSnapshot !== packMutations.current) {
          console.warn("Collection changed during startup reconciliation; preserving the merged mirror");
          return;
        }
      }
      if(collection.ids !== null) updateMe({ cards: collection.ids, cardCopies: collection.copies, lockedCopies: collection.lockedCopies });
    })();
    return () => { active = false; };
  }, [user?.id, me?.id, epochTick, clockTick]);
  // Grants are the volatile half of the pack surface: a live check-in mints a
  // booster grant mid-session, so the Available Packs card re-fetches on every
  // home/cards-tab entry (same pattern as the pending-XP line). Collection +
  // bridge import stay once-per-session above.
  useEffect(() => {
    if (!user?.id) return;
    const readGeneration = getClockGeneration();
    let active = true;
    fetchMyGrants(user.id).then(grants => { if (active && readGeneration === getClockGeneration()) setPackGrants(grants); });
    return () => { active = false; };
  }, [tab, user?.id, clockTick, epochTick]);

  // The standard-pack countdown's data source must match the server cadence:
  // claim_standard_pack keys on the newest standard grant's created_at
  // REGARDLESS of status (it stays the basis after a grant is opened). The
  // granted-only packGrants list loses opened rows, so deriving the countdown
  // from it made nextStandardPackAt return null after an open and both tabs
  // falsely offered Claim. fetchNewestStandardGrant is status-agnostic
  // (kind='standard' only) and re-fetches on the same home/cards triggers as
  // the unopened-packs list above.
  useEffect(() => {
    if (!user?.id) return;
    const readGeneration = getClockGeneration();
    let active = true;
    fetchNewestStandardGrant(user.id).then(grant => {
      if (active && readGeneration === getClockGeneration()) setNextStandardAt(nextStandardPackAt(grant ? [grant] : []));
    });
    return () => { active = false; };
  }, [tab, user?.id, clockTick, epochTick]);

  // Account-switch isolation: pack grants, the standard-pack countdown and the
  // pending-XP summary are the signed-in user's own data but live in unkeyed App
  // state, and the read effects above return on no user without clearing. After
  // A → sign-out → B, B's first home/cards render would otherwise show A's
  // available packs and countdown until B's reads settle. Reset them on identity
  // change so a previous account's values never render for the next one; late
  // responses from the previous account's read effects are still discarded by
  // each effect's own `active` guard above.
  useEffect(() => {
    setPackGrants([]);
    setNextStandardAt(null);
    setPendingXp({ pending: { total: 0, count: 0 }, released: { total: 0, count: 0 } });
  }, [user?.id, epochTick]);

  function showPackFailure(result) {
    const messages = {
      grant_not_found: "That pack is no longer available.",
      already_opened: "That pack has already been opened.",
      unauthenticated: "Your session has expired. Please sign in again.",
      invalid_input: "We couldn't open that pack. Please refresh and try again.",
      server_error: "The pack service hit a problem. Please try again.",
      network_error: "We couldn't reach the pack service. Check your connection and try again.",
    };
    setModal({ type:"info", data:messages[result.code] || messages.server_error });
  }

  function revealServerPack(result) {
    packMutations.current += 1;
    const collection = mergePackDraws({ ids:ownedCards, copies:cardCopies }, result.draws);
    updateMe({ cards:collection.ids, cardCopies:collection.copies });
    setModal({ type:"packReveal", data:{ draws:result.draws } });
    playChime("pack");
  }

  async function openGrant(grant) {
    const operationGeneration = getClockGeneration();
    primeAudio();
    if(packBusy) return;
    setPackBusy(true);
    try {
      const result = await openGrantedPack(grant.id);
      if (operationGeneration !== getClockGeneration()) return;
      if(result.ok) {
        setPackGrants(current => current.filter(item => item.id !== grant.id));
        revealServerPack(result);
      } else if(result.code !== "in_flight") showPackFailure(result);
    } finally {
      setPackBusy(false);
    }
  }

  async function claimStandard() {
    const operationGeneration = getClockGeneration();
    primeAudio();
    if(packBusy) return;
    setPackBusy(true);
    try {
      const result = await claimStandardPack();
      if (operationGeneration !== getClockGeneration()) return;
      if(result.ok) {
        setNextStandardAt(result.nextStandardAt);
        revealServerPack(result);
      } else if(result.code === "not_eligible") {
        setNextStandardAt(result.nextEligibleAt);
      } else if(result.code !== "in_flight") {
        showPackFailure(result);
      }
    } finally {
      setPackBusy(false);
    }
  }

  const standardClaimLabel = (() => {
    if(!nextStandardAt) return null;
    const remainingMs = new Date(nextStandardAt).getTime() - appNowMs();
    if(remainingMs <= 0) return null;
    const totalMinutes = Math.ceil(remainingMs / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `Next standard pack in ${hours > 0 ? `${hours}h ` : ""}${minutes}m`;
  })();


  // Badge artwork: the prototype generated SVGs by calling api.anthropic.com from
  // the client — removed (spec §4, does not work outside the sandbox and leaks no
  // key handling). The Badge component falls back to its emoji icon when the
  // cache has no SVG, so this is a no-op. Generated badge art can return later
  // as a server-side, cached asset if the client wants it.
  const generateBadgeSvg = useCallback(() => {}, []);


  // ── LIVE CHECK-IN ──
  const [checkInBusy, setCheckInBusy] = useState(false);

  // Returns "in_flight" when the caller should NOT release checkInBusy itself
  // (another check-in already owns it), otherwise "handled". A successful OR
  // duplicate check-in refreshes the server stats (R4) — the local model is no
  // longer written here.
  function processCheckInResult(fixture, result) {
    if(result.ok) {
      setStatsRefreshTick(t => t + 1);
      setModal({ type:"checkinResult", data:{ fixture, ...result } });
      playChime("checkin");
      return "handled";
    }

    if(result.code === "in_flight") return "in_flight";

    if(result.code === "duplicate") {
      setStatsRefreshTick(t => t + 1);
      setModal({ type:"checkinError", data:{
        fixture,
        title:"Already checked in",
        message:"Already checked in — this game is on your log.",
      }});
      return "handled";
    }

    const messages = {
      window_closed: `Check-in is available from ${formatDurationMinutes(CHECKIN_PRE_MIN)} before kickoff until ${formatDurationMinutes(CHECKIN_POST_MIN)} after fulltime.`,
      out_of_range: "You need to be at the ground to check in.",
      impossible_jump: "We couldn't verify this check-in from your recent location history.",
      rate_limited: "Too many check-ins in the last hour — try again soon.",
      unauthenticated: "Your session has expired. Please sign in again, then retry your check-in.",
      location_denied: "Location access is off. Enable location permission for SKOHR in your browser settings, then retry.",
      location_unavailable: "We couldn't get your location. Try again near open sky.",
      location_unsupported: "This browser can't provide your location. Try a supported browser or device.",
      invalid_input: "We couldn't submit this check-in. Please refresh and try again.",
      fixture_not_found: "This game isn't available for check-in. Please refresh and try again.",
      server_error: "The check-in service hit a problem. Please try again.",
      network_error: "We couldn't reach the check-in service. Check your connection and try again.",
      // Round 11 (R15) — the caller's stand-in flag isn't on, or replay is off.
      standin_not_allowed: "Test mode isn't switched on for your account.",
    };
    setModal({ type:"checkinError", data:{
      fixture,
      title:"Check-in not completed",
      message:messages[result.code] || messages.server_error,
    }});
    return "handled";
  }

  // `standin` (R15) — a flagged tester checking in "from anywhere" under a
  // replay: this path sends `{ standin: true }` and never touches
  // navigator.geolocation. The ordinary path still uses it (via checkinApi.js's
  // performCheckIn), so it's worth a quick capability check here first — no
  // point invoking the check-in function at all if this browser can't provide
  // a location.
  async function liveCheckIn(fixture, { standin = false } = {}) {
    const operationGeneration = getClockGeneration();
    primeAudio();
    if(checkInBusy) return;
    setCheckInBusy(true);
    let ownsBusy = true;
    try {
      if (standin) {
        const result = await performCheckIn(fixture.id, { standin: true });
        if (operationGeneration !== getClockGeneration()) return;
        if (processCheckInResult(fixture, result) === "in_flight") ownsBusy = false;
        return;
      }
      if (typeof navigator === "undefined" || !navigator.geolocation || typeof navigator.geolocation.getCurrentPosition !== "function") {
        setModal({ type:"checkinError", data:{
          fixture,
          title:"Check-in not completed",
          message:"This browser can't provide your location. Try a supported browser or device.",
        }});
        return;
      }
      const result = await performCheckIn(fixture.id);
      if (operationGeneration !== getClockGeneration()) return;
      if (processCheckInResult(fixture, result) === "in_flight") ownsBusy = false;
    } finally {
      if(ownsBusy) setCheckInBusy(false);
    }
  }

  // ── FREE STANDARD PACK ──
  // Compatibility boundary for round-2 check-in pins; the client-side standard
  // pack cadence is display-only (the countdown below) and server-driven pack
  // flows live above. The section delimiter was renamed from the prototype
  // free-pack marker as part of the round-4 prototype-copy sweep.

  // ── SUPPORTED TEAM PROMPT (account creation + start of each new season) ──
  const [teamPromptDismissedThisSession, setTeamPromptDismissedThisSession] = useState(false);
  const [teamPickPending, setTeamPickPending] = useState(false);
  const [teamPickError, setTeamPickError] = useState(null);
  useEffect(() => {
    if(!isAppClockReady() || !me || teamPromptDismissedThisSession) return;
    // New account that's never been asked at all (supportedTeam is undefined, not just null —
    // null means they explicitly chose "None" already, which should NOT re-prompt mid-season)
    if(me.teamConfirmedSeason === undefined || me.teamConfirmedSeason === null) {
      setModal({ type:"pickTeam", data:{ reason:"new-account" } });
      return;
    }
    // New season check — prompt once we're past last season but before the new season has actually
    // kicked off (i.e. anytime up to and including the new season's Round 1 date), and they haven't
    // confirmed for the current season yet. The "8+ days before kickoff" requirement just means this
    // prompt should realistically appear well ahead of time, which it will since CURRENT_SEASON gets
    // bumped manually well in advance of the new season's fixtures being loaded.
    if(me.teamConfirmedSeason !== CURRENT_SEASON) {
      const seasonStart = new Date(SEASON_START_DATE);
      const now = appNow();
      if(now < seasonStart) {
        setModal({ type:"pickTeam", data:{ reason:"new-season" } });
      }
    }
  }, [me?.id, me?.teamConfirmedSeason, teamPromptDismissedThisSession, clockTick]);

  async function confirmSupportedTeam(team) {
    const operationGeneration = getClockGeneration();
    // RUL-11: persist the pick through `set_supported_team` (null/None clears
    // it). The local model mirrors the server response; a `team_locked` re-syncs
    // from the server (through the functional updateMe, guarded to the same
    // user) and other failures KEEP the picker open with an inline error and a
    // retry (no info modal). `uid` is captured before the awaits; if the user
    // changes mid-flight the result is discarded.
    const uid = me?.id;
    setTeamPickPending(true);
    setTeamPickError(null);
    const res = await setSupportedTeam(team === "none" || team == null ? null : team);
    if (operationGeneration !== getClockGeneration()) return;
    if (tradeUser.current !== uid) { setTeamPickPending(false); return; }
    if (res.ok) {
      updateMe({ supportedTeam: res.teamName, teamConfirmedSeason: res.confirmedSeason });
      setTeamPickPending(false);
      setTeamPickError(null);
      setTeamPromptDismissedThisSession(true);
      setModal(null);
      return;
    }
    if (res.code === "team_locked") {
      setTeamPickPending(false);
      setModal({ type: "info", data: leaderboardErrorCopy("team_locked") });
      const profile = await fetchProfile(uid);
      if (operationGeneration !== getClockGeneration()) return;
      if (tradeUser.current !== uid) return;
      const progress = await fetchMyProgress(uid);
      if (operationGeneration !== getClockGeneration()) return;
      if (tradeUser.current !== uid) return;
      updateMe({
        teamConfirmedSeason: profile?.team_confirmed_season ?? undefined,
        supportedTeam: progress ? progress.supportedTeamName : (me?.supportedTeam ?? null),
      });
      return;
    }
    setTeamPickPending(false);
    setTeamPickError(leaderboardErrorCopy(res.code));
  }

  // ── FOREVER SETS (server truth, R26) ── ForeverSection owns its own reads,
  // busy/actor lifecycle and Forever-specific state entirely (round 10
  // remediation, M1/M6); this just keeps the shared collection mirror (used
  // by stats and the Trading Board) in sync once a set locks cards.
  const foreverActor = useRef(user?.id);
  foreverActor.current = user?.id;
  async function refreshCollectionMirror() {
    const operationGeneration = getClockGeneration();
    const actor = user?.id;
    const collection = await fetchMyCollection();
    if (operationGeneration !== getClockGeneration()) return;
    if (actor !== foreverActor.current) return;
    if (collection.ids !== null) updateMe({ cards: collection.ids, cardCopies: collection.copies, lockedCopies: collection.lockedCopies });
    setRefreshTick(t => t + 1); // my XP shows the new pending row once released
  }

  async function tradeAction(action, notice, accepted = false) {
    const operationGeneration = getClockGeneration();
    const actor = user?.id;
    setTradeError(null);
    setTradeNotice(null);
    const result = await action();
    if (operationGeneration !== getClockGeneration()) return;
    if (tradeUser.current !== actor) return result;
    if (result.ok) {
      setTradeNotice(notice);
      setTradeRefresh(t=>t+1);
      if (accepted) {
        setModal({ type:"info", data:"Trade complete! Cards have been swapped." });
        const collection = await fetchMyCollection();
        if (operationGeneration !== getClockGeneration()) return;
        if (tradeUser.current === actor && collection.ids !== null) updateTradeMirror.current({ cards: collection.ids, cardCopies: collection.copies, lockedCopies: collection.lockedCopies });
      }
    } else {
      setTradeError(result.code);
      if (result.code === "offer_unavailable") setTradeRefresh(t=>t+1);
    }
    return result;
  }
  function onPostListing(items) { return tradeAction(()=>postListing(items), "Listing posted"); }
  function onCancelListing(id) { return tradeAction(()=>cancelListing(id), "Listing cancelled"); }
  function onPostCounter(id, items) { return tradeAction(()=>postCounter(id, items), "Counter-offer sent"); }
  function onWithdrawCounter(id) { return tradeAction(()=>withdrawCounter(id), "Counter-offer withdrawn"); }
  function onDeclineCounter(id) { return tradeAction(()=>declineCounter(id), "Counter-offer declined"); }
  function onAcceptCounter(id) { return tradeAction(()=>acceptCounter(id), null, true); }

  // ── LEADERBOARD ──
  // Round 8 — the board now reads the server read model (`get_leaderboard`)
  // rather than the prototype's local db.users pool. Season | Weekly toggle
  // and the existing team filter drive the fetch; the signed-in user's own
  // row is appended when it falls outside the ranked list.
  const [leaderboardPeriod, setLeaderboardPeriod] = useState("season");
  const [leaderboardTeamFilter, setLeaderboardTeamFilter] = useState("all");
  const leaderboard = useLeaderboard(user, tab, leaderboardPeriod, leaderboardTeamFilter, epochTick);

  // ── ADMIN: WEEKLY CONFIRMATION ──
  // The admin confirmation screen now runs on SERVER truth (match_confirmations
  // table + release-round Edge Function). The transitional client model's
  // confirmedGames/gamePlayers are no longer populated by this screen — their
  // consumers move to server data in the XP-migration wave (see CHANGES.md).

  // ─── LOADING (restoring Supabase session) ──────────────────────────────────────
  if(authLoading || (user && !me)) return (
    <div style={{ minHeight:"100vh", background:T.bg, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"system-ui,sans-serif", color:T.textMuted, fontSize:14 }}>
      {authSyncing ? "Syncing…" : "Loading…"}
    </div>
  );

  // ─── AUTH SCREEN ─────────────────────────────────────────────────────────────
  if(!user) { const mode = authMode==="register" && registrationOpen!==true ? "login" : authMode; return (
    <div style={{ minHeight:"100vh", background:T.bg, display:"flex", alignItems:"center", justifyContent:"center", padding:20, fontFamily:"system-ui,sans-serif" }}>
      <div style={{ width:"100%", maxWidth:380 }}>
        <div style={{ textAlign:"center", marginBottom:32 }}>
          <img src="/brand-mark-192.png" alt="" aria-hidden="true" style={{ width:64, height:64, objectFit:"contain", display:"block", margin:"0 auto 14px" }} />
          <div style={{ fontSize:28, fontWeight:900, color:T.text, letterSpacing:"-0.5px" }}>{branding.appName}</div>
          <div style={{ fontSize:13, color:T.textMuted, marginTop:4 }}>Log games. Collect cards. Trade with friends.</div>
        </div>
        <div style={{ background:T.surface, borderRadius:T.radiusLg, padding:24, border:`1px solid ${T.border}` }}>
          {registrationOpen===true && (
          <div style={{ display:"flex", marginBottom:20, background:T.bg, borderRadius:T.radiusSm, padding:3 }}>
            {["login","register"].map(m=>(
              <button key={m} onClick={()=>setAuthMode(m)} style={{ flex:1, padding:"8px 0", border:"none", cursor:"pointer", borderRadius:T.radiusSm-2, background:mode===m?T.accent:"transparent", color:mode===m?"#000":T.textMuted, fontSize:13, fontWeight:600, transition:"all 0.15s" }}>
                {m==="login"?"Sign In":"Create Account"}
              </button>
            ))}
          </div>
          )}
          {mode==="register" && (
            <input placeholder="Display Name" value={authForm.name} onChange={e=>setAuthForm({...authForm,name:e.target.value})}
              style={{ width:"100%", background:T.surfaceHigh, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"11px 14px", color:T.text, fontSize:14, marginBottom:10, boxSizing:"border-box", outline:"none" }}/>
          )}
          {mode==="reset" && <p style={{ color:T.textMuted, fontSize:13 }}>Enter your email and we'll send a reset link.</p>}
          <input placeholder="Email" value={authForm.email} onChange={e=>setAuthForm({...authForm,email:e.target.value})}
            style={{ width:"100%", background:T.surfaceHigh, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"11px 14px", color:T.text, fontSize:14, marginBottom:10, boxSizing:"border-box", outline:"none" }}/>
          {mode!=="reset" && <input type="password" placeholder="Password" value={authForm.password} onChange={e=>setAuthForm({...authForm,password:e.target.value})}
            onKeyDown={e=>e.key==="Enter"&&handleAuth()}
            style={{ width:"100%", background:T.surfaceHigh, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"11px 14px", color:T.text, fontSize:14, marginBottom:10, boxSizing:"border-box", outline:"none" }}/>}
          {mode==="login" && <button disabled={authBusy} onClick={()=>{setAuthMode("reset"); setAuthError(""); setAuthNotice(""); setAuthForm({...authForm,password:""});}} style={{ background:"none", border:"none", color:T.textMuted, textDecoration:"underline", cursor:"pointer", marginBottom:12 }}>Forgot password?</button>}
          {authError && <div style={{ color:T.red, fontSize:12, marginBottom:10 }}>{authError}</div>}
          {authNotice && <div style={{ color:T.accentDark, fontSize:12, marginBottom:10 }}>{authNotice}</div>}
          <button onClick={handleAuth} disabled={authBusy} style={{ width:"100%", background:T.accent, border:"none", borderRadius:T.radiusSm, padding:"13px 0", color:"#000", fontSize:15, fontWeight:800, cursor:authBusy?"default":"pointer", opacity:authBusy?0.6:1 }}>
            {authBusy ? "Please wait…" : (mode==="reset"?"Send reset link":mode==="login"?"Sign In":"Create Account")}
          </button>
          {mode==="reset" && <button disabled={authBusy} onClick={()=>{setAuthMode("login"); setAuthError("");}} style={{ background:"none", border:"none", color:T.textMuted, textDecoration:"underline", cursor:"pointer", marginTop:12 }}>Back to sign in</button>}
        </div>
        {registrationOpen===false && <InterestPanel />}
        <div style={{ textAlign:"center", marginTop:16, fontSize:12, color:T.textDim }}>
          Your account is secured by email and password.
        </div>
      </div>
    </div>
  ); }

  if(recoveryMode && user) return (
    <div style={{ minHeight:"100vh", background:T.bg, display:"flex", alignItems:"center", justifyContent:"center", padding:20, fontFamily:"system-ui,sans-serif" }}>
      <div style={{ width:"100%", maxWidth:380 }}>
        <SetPasswordScreen intro="Choose a new password for your account." onSubmit={handlePasswordUpdate} busy={passwordBusy} error={passwordError} />
      </div>
    </div>
  );

  // ─── MAIN APP ─────────────────────────────────────────────────────────────────
  const NAV = [
    { id:"home",   label:"Home",   icon:<Ico.Home/> },
    { id:"badges", label:"Badges", icon:<Ico.Badges/> },
    { id:"cards",  label:"Cards",  icon:<Ico.Cards/> },
    { id:"board",  label:"Board",  icon:<Ico.Board/> },
    { id:"more",   label:"More",   icon:<Ico.More/> },
  ];

  // Shared page header component
  const PageHeader = ({label, title, subtitle, action}) => (
    <div style={{ padding:"24px 20px 16px", borderBottom:`1px solid ${T.border}` }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <button onClick={()=>setTab("home")} aria-label="Home" style={{ background:"transparent", border:"none", padding:0, cursor:"pointer", display:"flex", alignItems:"center" }}>
            <img src="/brand-mark-192.png" alt="" style={{ width:32, height:32, objectFit:"contain", display:"block" }} />
          </button>
          <span style={{ fontWeight:800, fontSize:16, color:T.text }}>{branding.appName}</span>
        </div>
        {action || <button onClick={logout} style={{ background:"transparent", border:`1px solid ${T.border}`, borderRadius:20, padding:"5px 12px", color:T.textMuted, fontSize:12, cursor:"pointer" }}>Sign Out</button>}
      </div>
      <div style={{ marginTop:20 }}>
        {label && <div style={{ fontSize:11, fontWeight:700, color:T.textMuted, letterSpacing:2, marginBottom:4 }}>{label}</div>}
        <div style={{ fontSize:32, fontWeight:900, lineHeight:1.1, color:T.text }}>{title}</div>
        {subtitle && <div style={{ fontSize:13, color:T.textMuted, marginTop:4 }}>{subtitle}</div>}
      </div>
    </div>
  );

  return (
    <>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } } @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.6} }`}</style>
    <div style={{ minHeight:"100vh", background:T.bg, fontFamily:"system-ui,sans-serif", color:T.text, paddingBottom:72, maxWidth:600, margin:"0 auto", position:"relative" }}>

      {/* ── HOME TAB ── */}
      {tab==="home" && (
        <div>
          <PageHeader label="PROFILE" title={
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <span>{me?.name || "Home"}</span>
              {me && <BeltIcon rankTier={getRankForGames(stats.allTime.games||0)} size={30} onClick={()=>setModal({type:"rankDetail"})}/>}
            </div>
          } action={
            <button onClick={()=>setTab("logGame")} style={{ background:T.accent, border:"none", borderRadius:24, padding:"8px 18px", color:"#000", fontSize:13, fontWeight:800, cursor:"pointer", display:"flex", alignItems:"center", gap:6 }}>
              + Log Game
            </button>
          }/>
          <div style={{ padding:"16px" }}>

            {/* Points card */}
            <XpCard
              releasedTotal={myXp.releasedTotal}
              pendingTotal={myXp.pendingTotal}
              pendingGames={stats.pendingGames}
              onOpen={()=>setModal({type:"pointsBreakdown"})}
            />

            {/* Standard pack */}
            <div style={{ background:"rgba(57,255,20,0.07)", border:"1px solid rgba(57,255,20,0.2)", borderRadius:T.radius, padding:"14px 16px", marginBottom:16, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div>
                <div style={{ fontWeight:700, fontSize:14, color:T.text }}>Available Packs</div>
                <div style={{ fontSize:12, color:T.textMuted, marginTop:2 }}>
                  {packGrants.length} pack{packGrants.length===1?"":"s"} ready to open
                </div>
              </div>
              <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                {packGrants[0] && <button disabled={packBusy} onClick={()=>openGrant(packGrants[0])} style={{ background:T.accent, border:"none", borderRadius:24, padding:"8px 16px", color:"#000", fontSize:13, fontWeight:800, cursor:"pointer" }}>Open</button>}
                {standardClaimLabel
                  ? <PackCountdown targetMs={nextStandardAt ? new Date(nextStandardAt).getTime() : 0} onElapsed={() => setNextStandardAt(null)} />
                  : <button disabled={packBusy} onClick={claimStandard} style={{ background:T.accent, border:"none", borderRadius:24, padding:"8px 16px", color:"#000", fontSize:13, fontWeight:800, cursor:"pointer", opacity:packBusy?0.5:1 }}>Claim</button>}
              </div>
            </div>

            {/* Current Season stats */}
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:12, fontWeight:700, color:T.textMuted, letterSpacing:2, marginBottom:10 }}>{stats.seasonYear != null ? `${stats.seasonYear} SEASON` : ""}</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                {[
                  { label:"GAMES ATTENDED", value:stats.season.games||0, highlight:true, onClick:()=>setDrilldown({type:"games"}) },
                  { label:"STADIUMS VISITED", value:stats.season.stadiums||0, highlight:false, onClick:()=>setDrilldown({type:"stadiums"}) },
                  { label:"PLAYERS SEEN", value:stats.season.players||0, highlight:false, onClick:()=>setDrilldown({type:"players"}) },
                  { label:"TEAMS SEEN", value:stats.season.teams||0, highlight:false, onClick:()=>setDrilldown({type:"teams"}) },
                ].map(s=>(
                  <div key={s.label} onClick={s.onClick} style={{ background:s.highlight?T.accent:T.surface, border:`1px solid ${s.highlight?T.accent:T.border}`, borderRadius:T.radius, padding:"18px 16px", cursor:"pointer" }}>
                    <div style={{ fontSize:36, fontWeight:900, color:s.highlight?"#000":T.text, lineHeight:1 }}>{s.value}</div>
                    <div style={{ fontSize:10, fontWeight:700, color:s.highlight?"rgba(0,0,0,0.6)":T.textMuted, letterSpacing:1, marginTop:6 }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* All Time stats */}
            <div style={{ marginBottom:8 }}>
              <div style={{ fontSize:12, fontWeight:700, color:T.textMuted, letterSpacing:2, marginBottom:10 }}>ALL TIME</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                {[
                  { label:"GAMES ATTENDED", value:stats.allTime.games||0, type:"games" },
                  { label:"STADIUMS VISITED", value:stats.allTime.stadiums||0, type:"stadiums" },
                  { label:"PLAYERS SEEN", value:stats.allTime.players||0, type:"players" },
                  { label:"TEAMS SEEN", value:stats.allTime.teams||0, type:"teams" },
                ].map(s=>(
                  <div key={s.label} onClick={()=>setDrilldown({type:s.type, allTime:true})} style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.radius, padding:"18px 16px", cursor:"pointer" }}>
                    <div style={{ fontSize:36, fontWeight:900, color:T.text, lineHeight:1 }}>{s.value}</div>
                    <div style={{ fontSize:10, fontWeight:700, color:T.textMuted, letterSpacing:1, marginTop:6 }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      )}

      {/* ── GAMES TAB ── */}
      {/* ── ADMIN: WEEKLY CONFIRMATION SCREEN ── */}
      {tab==="adminConfirm" && user?.isAdmin && (
        <AdminConfirmScreen key={`${user.id}:${epochTick}`} onBack={()=>setTab("more")} userId={user.id} />
      )}

      {/* ── ADMIN: SQUADS SCREEN ── */}
      {tab==="adminSquads" && user?.isAdmin && (
        <AdminSquadsScreen onBack={()=>setTab("more")} />
      )}

      {/* ── ADMIN: REPLAY CLOCK SCREEN (R14) ── */}
      {tab==="adminReplay" && user?.isAdmin && getAppClock()?.replay_mode && (
        <AdminReplayScreen onBack={()=>setTab("more")} />
      )}

      {tab==="logGame" && (() => {
        function LogGameTab() {
          const [upcoming, setUpcoming] = useState([]);
          const [checkedInFixtureIds, setCheckedInFixtureIds] = useState(new Set());
          const [fixturesLoading, setFixturesLoading] = useState(true);
          const [fixturesError, setFixturesError] = useState(false);
          const [retryCount, setRetryCount] = useState(0);
          // R15 — per-fixture stand-in checkbox state, unchecked by default.
          const [standinChecked, setStandinChecked] = useState({});

          // R12 reactive refresh: a clock sync whose payload changed (offset
          // or epoch moved) re-fetches the fixture list without a reload — a
          // forward jump can open/close windows and surface replayed
          // 'final' fixtures that weren't visible a moment ago.
          useEffect(() => onAppClock(() => setRetryCount((count) => count + 1)), []);

          useEffect(() => {
            const readGeneration = getClockGeneration();
            let active = true;
            setFixturesLoading(true);
            setFixturesError(false);
            Promise.all([fetchUpcomingFixtures(), fetchMyCheckIns(user.id)])
              .then(([fixtures, checkIns]) => {
                if (!active || readGeneration !== getClockGeneration()) return;
                setUpcoming(fixtures);
                setCheckedInFixtureIds(checkIns);
              })
              .catch(() => { if (active && readGeneration === getClockGeneration()) setFixturesError(true); })
              .finally(() => { if (active && readGeneration === getClockGeneration()) setFixturesLoading(false); });
            return () => { active = false; };
          }, [retryCount]);

          const now = appNow();
          return (
          <div>
            <PageHeader label="" title="Log a Game" action={
              <button onClick={()=>setTab("home")} style={{ background:T.surfaceHigh, border:`1px solid ${T.border}`, borderRadius:"50%", width:36, height:36, display:"flex", alignItems:"center", justifyContent:"center", color:T.text, cursor:"pointer" }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
              </button>
            }/>
            <div style={{ padding:"16px" }}>
              <div style={{ fontSize:13, color:T.textMuted, lineHeight:1.6, marginBottom:16 }}>
                Select a game you're attending.
              </div>

              <div style={{ background:T.surfaceHigh, borderRadius:T.radius, padding:"14px 16px", marginBottom:20, display:"flex", gap:10, alignItems:"flex-start" }}>
                <div style={{ color:T.accent, flexShrink:0, marginTop:1 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                </div>
                <div style={{ fontSize:13, color:T.textMuted, lineHeight:1.5 }}>
                  Games can be logged from <b style={{ color:T.text }}>{formatDurationMinutes(CHECKIN_PRE_MIN)} before kickoff</b> up to <b style={{ color:T.text }}>{formatDurationMinutes(CHECKIN_POST_MIN)} after fulltime</b>.
                </div>
              </div>

              {fixturesLoading && (
                <div style={{ textAlign:"center", padding:"40px 0", color:T.textMuted, fontSize:13 }}>Loading games…</div>
              )}

              {!fixturesLoading && fixturesError && (
                <div style={{ textAlign:"center", padding:"40px 0", color:T.textMuted, fontSize:13 }}>
                  Couldn't load games. <button onClick={()=>setRetryCount(count=>count+1)} style={{ background:"none", border:"none", padding:0, color:T.accent, fontWeight:700, cursor:"pointer" }}>Retry</button>
                </div>
              )}

              {!fixturesLoading && !fixturesError && upcoming.length === 0 && (
                <div style={{ textAlign:"center", padding:"40px 0", color:T.textDim, fontSize:13 }}>No upcoming games found.</div>
              )}

              {!fixturesLoading && !fixturesError && upcoming.map(g => {
                const windowState = fixtureWindowState(g, now);
                const windowOpenMs = g.kickoffUtc.getTime() - (CHECKIN_PRE_MIN * 60 * 1000);
                const canLog = windowState === "pre" || windowState === "live";
                const isAttended = checkedInFixtureIds.has(g.id);
                const viewerLabel = kickoffViewerLabel(g);
                return (
                  <div key={g.id} style={{ background:T.surface, border:`1px solid ${canLog?T.borderGreen:T.border}`, borderRadius:T.radius, padding:"16px", marginBottom:10 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
                      <div style={{ fontSize:14, fontWeight:700 }}><TeamMatchup home={g.homeRef} away={g.awayRef} /></div>
                      {windowState==="live" && <span style={{ background:"rgba(57,255,20,0.15)", color:T.accent, fontWeight:700, borderRadius:4, padding:"2px 8px", fontSize:10 }}>🔴 LIVE</span>}
                    </div>
                    <div style={{ fontSize:12, color:T.textMuted, marginBottom:4 }}>📍 {g.venue.name}</div>
                    <div style={{ fontSize:12, color:T.textMuted, marginBottom:viewerLabel?2:12 }}>
                      {originLabel(g)} · {matchDateLabel(g)} · {kickoffVenueLabel(g)}
                    </div>
                    {viewerLabel && <div style={{ fontSize:11, color:T.textDim, marginBottom:12 }}>(your time: {viewerLabel})</div>}
                    {!canLog && windowOpenMs > now.getTime() && (
                      <CountdownToOpen targetMs={windowOpenMs} />
                    )}
                    {canLog && (
                      isAttended ? (
                        <div style={{ textAlign:"center", padding:"10px 0", color:T.accent, fontSize:13, fontWeight:700 }}>✓ Game Logged</div>
                      ) : (
                        <>
                          {/* R15 — only when replay is on AND this member carries the stand-in flag */}
                          {Boolean(getAppClock()?.replay_mode) && Boolean(getAppClock()?.location_standin) && (
                            <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:12, color:T.textMuted, marginBottom:10, cursor:"pointer" }}>
                              <input
                                type="checkbox"
                                checked={Boolean(standinChecked[g.id])}
                                onChange={(e)=>setStandinChecked(prev=>({ ...prev, [g.id]: e.target.checked }))}
                              />
                              Test mode: check in as if I'm at {g.venue.name}
                            </label>
                          )}
                          <button disabled={checkInBusy} onClick={async()=>{
                            await liveCheckIn(g, { standin: Boolean(standinChecked[g.id]) });
                            setTab("home");
                          }} style={{ width:"100%", background:T.accent, border:"none", borderRadius:T.radiusSm, padding:"12px 0", color:"#000", fontSize:14, fontWeight:800, cursor:checkInBusy?"default":"pointer", opacity:checkInBusy?0.55:1 }}>
                            {checkInBusy ? "Checking In…" : "Check In & Log This Game"}
                          </button>
                        </>
                      )
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          );
        }
        return <LogGameTab />;
      })()}

      {/* ── BADGES TAB ── */}
      {tab==="badges" && (
        <BadgesScreen
          defs={BADGE_DEFS}
          earnedSeason={myBadges.season}
          earnedAllTime={myBadges.allTime}
          seasonYear={CURRENT_SEASON}
          svgCache={badgeSvgCache}
          onVisible={generateBadgeSvg}
          onLogGame={()=>setTab("logGame")}
          onOpenBadge={(b)=>setModal({type:"badgeHistory", data:b})}
        />
      )}

      {/* ── CARDS TAB ── */}
      {tab==="cards" && (() => {
        const uniqueOwned = new Set(ownedCards);
        const totalCopies = ownedCards.reduce((sum,id)=>sum+(cardCopies[id] || 1), 0);
        const totalValue = ownedCards.reduce((sum,id)=>{ const c=ALL_CARDS.find(x=>x.id===id); return c?sum+RARITY[c.rarity].pts:sum; },0);
        const rarityCount = {};
        Object.keys(RARITY).forEach(r=>{ rarityCount[r]=ownedCards.filter(id=>ALL_CARDS.find(c=>c.id===id&&c.rarity===r)).length; });
        return (
          <div>
            <PageHeader label="YOUR" title="Cards" subtitle={`${uniqueOwned.size} unique cards · ${totalCopies} total · ✦ ${totalValue} total value`} action={
              <button onClick={()=>setTab("logGame")} style={{ background:T.accent, border:"none", borderRadius:24, padding:"8px 18px", color:"#000", fontSize:13, fontWeight:800, cursor:"pointer" }}>+ Log Game</button>
            }/>
            <div style={{ padding:"0 16px" }}>

              {/* Available Packs */}
              <div style={{ background:"rgba(57,255,20,0.07)", border:`1px solid rgba(57,255,20,0.2)`, borderRadius:T.radius, padding:"16px", marginTop:16, marginBottom:16 }}>
                <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                  <div style={{ width:44, height:44, borderRadius:12, background:"rgba(65,171,128,0.2)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22 }}>📦</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, fontSize:15, color:T.text }}>Available Packs</div>
                    <div style={{ fontSize:12, color:T.textMuted, marginTop:2 }}>
                      {packGrants.length} pack{packGrants.length===1?"":"s"} ready to open
                    </div>
                  </div>
                </div>
                {packGrants.map(grant => (
                  <button key={grant.id} disabled={packBusy} onClick={()=>openGrant(grant)} style={{ marginTop:14, width:"100%", background:T.accent, border:"none", borderRadius:T.radiusSm, padding:"13px 0", color:"#000", fontSize:14, fontWeight:800, cursor:"pointer" }}>
                    Open {grant.kind} pack · {grant.cards} cards
                  </button>
                ))}
                {standardClaimLabel
                  ? <PackCountdown targetMs={nextStandardAt ? new Date(nextStandardAt).getTime() : 0} onElapsed={() => setNextStandardAt(null)} />
                  : <button disabled={packBusy} onClick={claimStandard} style={{ marginTop:10, width:"100%", background:T.accent, border:"none", borderRadius:T.radiusSm, padding:"13px 0", color:"#000", fontSize:14, fontWeight:800, cursor:"pointer", opacity:packBusy?0.5:1 }}>
                      Claim Standard Pack
                    </button>}
              </div>

              {/* Collection / Checklist / Forever toggle */}
              <div style={{ display:"flex", background:T.surface, borderRadius:T.radius, padding:4, marginBottom:16, border:`1px solid ${T.border}` }}>
                {[{id:"collection",label:"Collection"},{id:"checklist",label:"Checklist"},{id:"forever",label:"Forever"}].map(v=>(
                  <button key={v.id} onClick={()=>setCardView(v.id)} style={{ flex:1, padding:"10px 0", border:"none", cursor:"pointer", borderRadius:T.radiusSm, background:cardView===v.id?T.accent:"transparent", color:cardView===v.id?"#000":T.textMuted, fontSize:13, fontWeight:cardView===v.id?700:400, display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
                    {v.label}
                  </button>
                ))}
              </div>

              {/* COLLECTION VIEW */}
              {cardView==="collection" && (
                <div>
                  {/* Rarity summary pills */}
                  <div style={{ display:"flex", gap:8, marginBottom:16 }}>
                    {Object.entries(rarityCount).map(([r,count])=>(
                      <div key={r} onClick={()=>setCardFilter(cardFilter===r?"all":r)} style={{ flex:1, background:cardFilter===r?RARITY[r].color+"33":RARITY[r].color+"11", border:`2px solid ${cardFilter===r?RARITY[r].color:RARITY[r].color+"44"}`, borderRadius:T.radiusSm, padding:"8px 4px", textAlign:"center", cursor:"pointer", transition:"all 0.15s" }}>
                        <div style={{ fontSize:18, fontWeight:900, color:RARITY[r].color }}>{count}</div>
                        <div style={{ fontSize:10, color:RARITY[r].color, marginTop:1, opacity:0.75 }}>{RARITY[r].label}</div>
                      </div>
                    ))}
                  </div>
                  {/* Filter row */}
                  <div style={{ display:"flex", gap:6, marginBottom:12, overflowX:"auto", paddingBottom:4 }}>
                    {["all","common","silver","gold","platinum","ultimate"].map(f=>(
                      <button key={f} onClick={()=>setCardFilter(f)} style={{ background:cardFilter===f?T.accent:"transparent", border:`1px solid ${cardFilter===f?T.accent:T.border}`, borderRadius:20, padding:"5px 12px", cursor:"pointer", color:cardFilter===f?"#000":T.textMuted, fontSize:12, fontWeight:cardFilter===f?700:400, whiteSpace:"nowrap", flexShrink:0 }}>
                        {f==="all"?"All":RARITY[f]?.label||f}
                      </button>
                    ))}
                  </div>
                  <div style={{ fontSize:12, color:T.textMuted, marginBottom:12 }}>{uniqueOwned.size} unique cards · {totalCopies} total</div>
                  {/* Card grid — 2 columns like screenshot */}
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
                    {[...uniqueOwned].filter(id=>{
                      const c=ALL_CARDS.find(x=>x.id===id);
                      return c && (cardFilter==="all"||c.rarity===cardFilter);
                    }).sort((idA,idB)=>{
                      const cA=ALL_CARDS.find(c=>c.id===idA), cB=ALL_CARDS.find(c=>c.id===idB);
                      const rarityOrderIdx = r => RARITIES_ORDER.indexOf(r);
                      if(rarityOrderIdx(cA.rarity) !== rarityOrderIdx(cB.rarity)) return rarityOrderIdx(cA.rarity) - rarityOrderIdx(cB.rarity);
                      return cA.num - cB.num;
                    }).map(id=>{
                      const card=ALL_CARDS.find(c=>c.id===id);
                      const copies=cardCopies[id] || 1;
                      const lockedCount=lockedCopies[id] || 0;
                      const r=RARITY[card.rarity];
                      return (
                        <div key={id} onClick={()=>setModal({type:"cardDetail",data:card})} style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.radius, padding:"16px", cursor:"pointer", position:"relative" }}>
                          {copies>1 && <div style={{ position:"absolute", top:10, right:10, background:T.accent, borderRadius:10, padding:"1px 7px", fontSize:11, fontWeight:800, color:"#000" }}>×{copies}</div>}
                          <div style={{ width:52, height:52, borderRadius:12, background:T.surfaceHigh, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:10 }}>
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(12,19,12,0.35)" strokeWidth="1.5"><ellipse cx="12" cy="12" rx="10" ry="6" transform="rotate(-30 12 12)"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2z"/></svg>
                          </div>
                          <div style={{ fontWeight:700, fontSize:14, color:T.text, lineHeight:1.3 }}>{card.player}</div>
                          <div style={{ fontSize:11, color:T.textMuted, marginTop:3 }}>
                            #{card.num} · {card.isTeamCard ? "Team Card" : <TeamName team={card.team} size={11}>{card.team.split(" ").slice(-1)[0]}</TeamName>}
                          </div>
                          <div style={{ height:1, background:T.border, margin:"10px 0" }}/>
                          <div style={{ fontSize:12, color:r.color, fontWeight:600 }}>✦ {r.pts} pts</div>
                          <div style={{ fontSize:10, color:T.textDim, marginTop:2 }}>{r.label}</div>
                          {lockedCount>0 && <div style={{ fontSize:10, color:T.gold, marginTop:6, fontWeight:700 }}>🔒 {lockedCount} locked</div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* CHECKLIST VIEW */}
              {cardView==="checklist" && (
                <div>
                  <div style={{ fontSize:12, color:T.textMuted, marginBottom:12 }}>Tap a card in the table to view it. ✅ = owned, ☐ = missing.</div>
                  {/* Table header */}
                  <div style={{ display:"grid", gridTemplateColumns:"40px 1fr 28px 28px 28px 28px 28px", gap:0, padding:"8px 12px", background:"#0e110e", borderRadius:`${T.radiusSm}px ${T.radiusSm}px 0 0`, borderBottom:`1px solid rgba(57,255,20,0.15)` }}>
                    <div style={{ fontSize:11, color:T.textDim, fontWeight:700 }}>#</div>
                    <div style={{ fontSize:11, color:T.textDim, fontWeight:700 }}>Player</div>
                    {Object.entries(RARITY).map(([r])=>(
                      <div key={r} style={{ fontSize:10, color:RARITY[r].color, fontWeight:800, textAlign:"center", borderLeft:"1px solid rgba(12,19,12,0.12)", paddingLeft:2 }}>{r[0].toUpperCase()}</div>
                    ))}
                  </div>
                  <div style={{ position:"relative" }}>
                  {[...TEAM_NAMES].sort((a,b)=>{
                    const minA = Math.min(...CARD_DEFINITIONS.filter(d=>d.team===a).map(d=>d.num));
                    const minB = Math.min(...CARD_DEFINITIONS.filter(d=>d.team===b).map(d=>d.num));
                    return minA - minB;
                  }).map((team, teamIdx)=>{
                    const teamCards = CARD_DEFINITIONS.filter(d=>d.team===team).sort((a,b)=>a.num-b.num);
                    return [
                      <div key={`divider-${team}`} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px 6px", background:T.surfaceHigh, borderTop: teamIdx===0 ? "none" : `2px solid rgba(57,255,20,0.2)` }}>
                        <span style={{ fontSize:11, fontWeight:700, color:T.accent, letterSpacing:1 }}><TeamName team={team}>{team.toUpperCase()}</TeamName></span>
                      </div>,
                      ...teamCards.map((def, idx) => {
                        const isLastInTeam = idx === teamCards.length - 1;
                        return (
                          <div key={def.num} style={{ display:"grid", gridTemplateColumns:"40px 1fr 28px 28px 28px 28px 28px", gap:0, padding:"10px 12px", borderBottom: isLastInTeam ? "none" : `1px solid ${T.border}`, background:"transparent", alignItems:"center" }}>
                            <div style={{ fontSize:11, color:T.textDim }}>{def.num}</div>
                            <div>
                              <div style={{ fontSize:13, fontWeight:600, color:T.text }}>{def.isTeamCard ? def.name.toUpperCase() : def.name}</div>
                            </div>
                            {Object.keys(RARITY).map(rarity=>{
                              const cardId = ALL_CARDS.find(c=>c.num===def.num&&c.rarity===rarity)?.id;
                              const copies = cardId && ownedCards.includes(cardId) ? (cardCopies[cardId] || 1) : 0;
                              return (
                                <div key={rarity} style={{ textAlign:"center",  }}>
                                  {copies===0
                                    ? <div style={{ width:10, height:10, borderRadius:2, border:`1px solid ${T.border}`, margin:"0 auto" }}/>
                                    : copies>1
                                    ? <div style={{ fontSize:10, fontWeight:800, color:T.accent }}>×{copies}</div>
                                    : <div style={{ color:T.accent, fontSize:14 }}>✓</div>
                                  }
                                </div>
                              );
                            })}
                          </div>
                        );
                      })
                    ];
                  })}
                  </div>
                </div>
              )}

              {/* FOREVER COLLECTION VIEW */}
              {cardView==="forever" && (
                <div style={{ marginBottom:16 }}>
                  <ForeverSection
                    key={`${user?.id || ""}:${CURRENT_SEASON}:${epochTick}`}
                    userId={user?.id}
                    season={CURRENT_SEASON}
                    onSubmitted={refreshCollectionMirror}
                    onSuccess={(label, xp) => setModal({ type:"foreverSetSubmitted", data:{ label, xp } })}
                  />
                </div>
              )}

              {/* ── TRADING BOARD ── */}
              <div style={{ marginTop:8, marginBottom:16 }}>
                <TradingBoardScreen key={`${user?.id}:${epochTick}`} board={board} loading={tradeState.loading} error={tradeError || tradeState.error} notice={tradeNotice} myUserId={user?.id} ownedCards={ownedCards} cardCopies={cardCopies} lockedCopies={lockedCopies} onPostListing={onPostListing} onCancelListing={onCancelListing} onPostCounter={onPostCounter} onWithdrawCounter={onWithdrawCounter} onAcceptCounter={onAcceptCounter} onDeclineCounter={onDeclineCounter} onRefresh={()=>setTradeRefresh(t=>t+1)} />
              </div>
            </div>
          </div>
        );
      })()}

      {passwordNotice === "Password updated. You're signed in." && <div role="status" style={{ padding:16, color:T.accentDark }}>{passwordNotice}</div>}

      {/* ── MORE TAB ── */}
      {tab==="more" && (
        <div>
          <PageHeader label="" title="More" />
          <div style={{ padding:"16px" }}>
            <div style={{ fontSize:11, fontWeight:700, color:T.textDim, letterSpacing:1.5, marginBottom:10 }}>HOW XP WORKS</div>
            <div style={{ fontSize:13, color:T.textMuted, lineHeight:1.6, marginBottom:18 }}>
              Your XP total is built up from attending games. Here's exactly how it's calculated.
            </div>

            <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.radius, padding:"16px", marginBottom:14 }}>
              <div style={{ fontSize:13, fontWeight:700, color:T.text, marginBottom:10 }}>Players</div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0", borderBottom:`1px solid ${T.border}` }}>
                <div style={{ fontSize:13, color:T.textMuted, flex:1 }}>Seeing a player for the first time that season</div>
                <div style={{ fontSize:14, fontWeight:800, color:T.accent, flexShrink:0, marginLeft:12 }}>2 XP</div>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0" }}>
                <div style={{ fontSize:13, color:T.textMuted, flex:1 }}>Seeing a player you've already seen that season</div>
                <div style={{ fontSize:14, fontWeight:800, color:T.accent, flexShrink:0, marginLeft:12 }}>1 XP</div>
              </div>
            </div>

            <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.radius, padding:"16px", marginBottom:14 }}>
              <div style={{ fontSize:13, fontWeight:700, color:T.text, marginBottom:10 }}>Teams</div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0", borderBottom:`1px solid ${T.border}` }}>
                <div style={{ fontSize:13, color:T.textMuted, flex:1 }}>Seeing a team for the first time that season</div>
                <div style={{ fontSize:14, fontWeight:800, color:T.accent, flexShrink:0, marginLeft:12 }}>10 XP</div>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0" }}>
                <div style={{ fontSize:13, color:T.textMuted, flex:1 }}>Seeing a team you've already seen that season</div>
                <div style={{ fontSize:14, fontWeight:800, color:T.accent, flexShrink:0, marginLeft:12 }}>5 XP</div>
              </div>
            </div>

            <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.radius, padding:"16px", marginBottom:14 }}>
              <div style={{ fontSize:13, fontWeight:700, color:T.text, marginBottom:10 }}>Venues / Stadiums</div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0", borderBottom:`1px solid ${T.border}` }}>
                <div style={{ fontSize:13, color:T.textMuted, flex:1 }}>Visiting a venue for the first time that season</div>
                <div style={{ fontSize:14, fontWeight:800, color:T.accent, flexShrink:0, marginLeft:12 }}>10 XP</div>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0" }}>
                <div style={{ fontSize:13, color:T.textMuted, flex:1 }}>Visiting a venue you've already seen that season</div>
                <div style={{ fontSize:14, fontWeight:800, color:T.accent, flexShrink:0, marginLeft:12 }}>5 XP</div>
              </div>
            </div>

            <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.radius, padding:"16px", marginBottom:14 }}>
              <div style={{ fontSize:13, fontWeight:700, color:T.text, marginBottom:8 }}>Badges</div>
              <div style={{ fontSize:13, color:T.textMuted, lineHeight:1.5 }}>
                Each badge awards its own XP value when earned — see the Badges tab for the full list and how to unlock each one.
              </div>
            </div>

            <SupportedTeamCard
              teamName={me?.supportedTeam}
              confirmedSeason={me?.teamConfirmedSeason}
              seasonYear={CURRENT_SEASON}
              onPick={() => setModal({ type: "pickTeam", data: { reason: "more" } })}
            />

            <SetPasswordScreen title="Change password" showMark={false} onSubmit={handlePasswordChange} busy={passwordBusy} error={changeError} notice={changeNotice} />

            {user?.isAdmin && (
              <button onClick={()=>setTab("adminConfirm")} style={{ width:"100%", background:T.purple, border:"none", borderRadius:T.radiusSm, padding:"13px 0", color:"#fff", fontSize:14, fontWeight:700, cursor:"pointer", marginTop:8 }}>
                🛠️ Weekly Confirmation (Admin)
              </button>
            )}

            {user?.isAdmin && (
              <button onClick={()=>setTab("adminSquads")} style={{ width:"100%", background:T.purple, border:"none", borderRadius:T.radiusSm, padding:"13px 0", color:"#fff", fontSize:14, fontWeight:700, cursor:"pointer", marginTop:8 }}>
                🧩 Squads (Admin)
              </button>
            )}

            {user?.isAdmin && getAppClock()?.replay_mode && (
              <button onClick={()=>setTab("adminReplay")} style={{ width:"100%", background:T.purple, border:"none", borderRadius:T.radiusSm, padding:"13px 0", color:"#fff", fontSize:14, fontWeight:700, cursor:"pointer", marginTop:8 }}>
                ⏱️ Replay clock (Admin)
              </button>
            )}

            {user?.isAdmin && (
              <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.radius, padding:16, marginTop:14 }}>
                <div style={{ fontSize:13, fontWeight:700, color:T.text, marginBottom:10 }}>Member's team (admin)</div>
                <select
                  value={adminMemberId}
                  onChange={(e)=>setAdminMemberId(e.target.value)}
                  aria-label="Member"
                  style={{ width:"100%", background:T.surfaceHigh, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"11px 14px", color:T.text, fontSize:14, marginBottom:10, boxSizing:"border-box" }}
                >
                  {adminProfiles.map((p) => (
                    <option key={p.id} value={p.id}>{p.displayName} — {p.teamName ?? "No team"}</option>
                  ))}
                </select>
                <select
                  value={adminTeamName}
                  onChange={(e)=>setAdminTeamName(e.target.value)}
                  aria-label="Team"
                  style={{ width:"100%", background:T.surfaceHigh, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"11px 14px", color:T.text, fontSize:14, marginBottom:12, boxSizing:"border-box" }}
                >
                  <option value="__none__">No team</option>
                  {TEAM_NAMES.map((team) => (
                    <option key={team} value={team}>{team}</option>
                  ))}
                </select>
                <button type="button" onClick={handleAdminSetTeam} disabled={adminTeamBusy || !adminMemberId} style={{ width:"100%", background:T.accent, border:"none", borderRadius:T.radiusSm, padding:"13px 0", color:"#000", fontSize:14, fontWeight:800, cursor:adminTeamBusy?"default":"pointer", opacity:adminTeamBusy?0.6:1 }}>
                  {adminTeamBusy ? "Please wait…" : "Set team"}
                </button>
                {adminTeamNotice && <div role="status" style={{ color:T.accentDark, fontSize:12, marginTop:10 }}>{adminTeamNotice}</div>}
                {adminTeamError && <div role="alert" style={{ color:T.red, fontSize:12, marginTop:10 }}>{adminTeamError}</div>}
              </div>
            )}
            {user?.isAdmin && <RegistrationAdminPanel key={user.id} />}
            <a href={RLP_ATTRIBUTION.href} target="_blank" rel="noopener noreferrer" style={{ display: "block", textAlign: "center", fontSize: 12, color: T.textSubtle, textDecoration: "underline", padding: "24px 0 8px" }}>{RLP_ATTRIBUTION.text}</a>
          </div>
        </div>
      )}

      {/* ── LEADERBOARD TAB ── */}
      {tab==="board" && (
        <div>
          <PageHeader label="COMPETITION" title="Leaderboard" subtitle={`${leaderboard.seasonYear ?? CURRENT_SEASON} Season`} action={
            <button onClick={()=>setTab("logGame")} style={{ background:T.accent, border:"none", borderRadius:24, padding:"8px 18px", color:"#000", fontSize:13, fontWeight:800, cursor:"pointer" }}>+ Log Game</button>
          }/>
          <LeaderboardScreen
            rows={leaderboard.rows}
            me={leaderboard.me}
            period={leaderboardPeriod}
            onPeriodChange={setLeaderboardPeriod}
            teamFilter={leaderboardTeamFilter}
            onTeamChange={setLeaderboardTeamFilter}
            loading={leaderboard.loading}
            error={leaderboard.error}
            round={leaderboard.round}
          />
        </div>
      )}


      {/* ── DRILLDOWN OVERLAY ── */}
      {drilldown && (() => {
        const isAllTime = drilldown.allTime === true;
        // R4: the drill-down lists derive from the released check-ins in
        // `stats.games` (season list = seasonYear matches; all-time = all).
        const allStatsGames = (stats.games || []).filter(g => isAllTime ? true : g.seasonYear === stats.seasonYear);

        // Build derived lists from the server game facts.
        const teamMap = {};
        const stadiumMap = {};
        const playerMap = {};
        // R18: a sighting's side resolves to the side's TEAM UUID (home →
        // homeTeamId, away → awayTeamId); a missing/invalid side or a null side
        // team → "Unknown side" (never away). One count per player/fixture.
        // Labels: club short name = last word of the club name ("Tigers",
        // "Broncos"); states in full ("New South Wales", "Queensland").
        const STATE_NAMES = new Set(["New South Wales", "Queensland"]);
        function sideTeamId(g, p) {
          if (p.side === "home") return g.homeTeamId ?? null;
          if (p.side === "away") return g.awayTeamId ?? null;
          return null;
        }
        function sideTeamLabel(g, p) {
          const id = sideTeamId(g, p);
          if (id == null) return "Unknown side";
          const ref = p.side === "home" ? g.homeRef : p.side === "away" ? g.awayRef : null;
          if (!ref) return "Unknown side";
          if (STATE_NAMES.has(ref)) return ref;
          const words = ref.trim().split(/\s+/);
          return words[words.length - 1] || ref;
        }
        allStatsGames.forEach(g => {
          const venueName = g.venue?.name ?? g.venue ?? "";
          if (g.homeTeamId != null) { if (!teamMap[g.homeRef]) teamMap[g.homeRef] = []; teamMap[g.homeRef].push(g); }
          if (g.awayTeamId != null) { if (!teamMap[g.awayRef]) teamMap[g.awayRef] = []; teamMap[g.awayRef].push(g); }
          if (venueName) { if (!stadiumMap[venueName]) stadiumMap[venueName] = []; stadiumMap[venueName].push(g); }
          (g.players || []).forEach(p => {
            const key = p.playerId || p.name;
            if (!key) return;
            if (!playerMap[key]) playerMap[key] = { player: p.name, playerId: key, games: [], _seenFixtures: new Set() };
            // one count per player/fixture
            if (!playerMap[key]._seenFixtures.has(g.fixtureId)) {
              playerMap[key]._seenFixtures.add(g.fixtureId);
              // F11: keep the side team UUID and the display label separately so
              // the side counts group by UUID (one count per player/fixture),
              // not by label. Missing/invalid side or null side team → Unknown.
              playerMap[key].games.push({ ...g, _seenSideTeamId: sideTeamId(g, p), _seenForTeam: sideTeamLabel(g, p) });
            }
          });
        });

        const GameRow = ({g}) => {
          if(!g) return null;
          const venueName = g.venue?.name ?? g.venue ?? "";
          return (
          <div onClick={()=>setDrilldown({type:"gameDetail", gameId:g.fixtureId, allTime:drilldown.allTime})}
            style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px 0", borderBottom:`1px solid ${T.border}`, cursor:"pointer" }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, fontWeight:600 }}><TeamMatchup home={g.homeRef} away={g.awayRef} /></div>
              <div style={{ fontSize:11, color:T.textMuted, marginTop:2 }}>{venueName} · {g.round === 0 ? "State of Origin" : `Rd ${g.round}`} · {g.matchDate ? new Date(g.matchDate).toLocaleDateString("en-AU",{day:"numeric",month:"short"}) : ""}</div>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:8, flexShrink:0 }}>
              {g.homeScore!==null && g.homeScore!==undefined && <div style={{ fontSize:13, fontWeight:700, color:T.accent }}>{g.homeScore}–{g.awayScore}</div>}
              <div style={{ fontSize:16, color:T.textDim }}>›</div>
            </div>
          </div>
          );
        };

        let title = "";
        let content = null;

        const seasonLabel = isAllTime ? " (All Time)" : (stats.seasonYear != null ? ` (${stats.seasonYear} Season)` : "");
        if(drilldown.type === "gameDetail") {
          const g = (stats.games || []).find(x=>x.fixtureId===drilldown.gameId);
          if(!g) { title = "Game not found"; content = null; }
          else {
            const venueName = g.venue?.name ?? g.venue ?? "";
            title = `${g.homeRef} vs ${g.awayRef}`;
            const gamePlayers = g.players || [];
            const hasPlayers = gamePlayers.length > 0;
            const homePlayers = gamePlayers.filter(p => p.side === "home");
            const awayPlayers = gamePlayers.filter(p => p.side === "away");
            content = (
              <div>
                {/* Match summary */}
                <div style={{ background:T.surfaceHigh, borderRadius:T.radius, padding:"14px 16px", marginBottom:14 }}>
                  <div style={{ fontSize:12, color:T.textMuted, marginBottom:6 }}>{g.round === 0 ? "State of Origin" : `Round ${g.round}`} · {g.matchDate ? new Date(g.matchDate).toLocaleDateString("en-AU",{weekday:"long",day:"numeric",month:"long",year:"numeric"}) : ""}</div>
                  <div style={{ fontSize:12, color:T.textMuted, marginBottom:10 }}>📍 {venueName}</div>
                  {g.homeScore!==null && g.homeScore!==undefined && (
                    <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                      <div style={{ flex:1, fontSize:14, fontWeight:700 }}><TeamName team={g.homeRef} /></div>
                      <div style={{ fontSize:22, fontWeight:900, color:T.accent }}>{g.homeScore}–{g.awayScore}</div>
                      <div style={{ flex:1, fontSize:14, fontWeight:700, textAlign:"right" }}><TeamName team={g.awayRef} /></div>
                    </div>
                  )}
                </div>

                {/* Players section */}
                <div style={{ marginBottom:10 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:T.textDim, letterSpacing:1.5 }}>PLAYERS</div>
                </div>
                {!hasPlayers ? (
                  <div style={{ background:T.surfaceHigh, borderRadius:T.radiusSm, padding:"12px 14px", fontSize:12, color:T.textMuted }}>
                    No confirmed players recorded
                  </div>
                ) : (
                  [{ team:g.homeRef, players:homePlayers }, { team:g.awayRef, players:awayPlayers }].map(({team, players}) => {
                    return (
                      <div key={team} style={{ marginBottom:16 }}>
                        <div style={{ fontSize:12, fontWeight:700, color:T.accent, marginBottom:6, paddingBottom:4, borderBottom:`1px solid ${T.borderGreen}` }}><TeamName team={team} /></div>
                        {players.length === 0
                          ? <div style={{ fontSize:12, color:T.textDim }}>No player data</div>
                          : players.map((p, i) => (
                            <div key={p.playerId || p.name || i} style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 0", borderBottom:`1px solid ${T.border}` }}>
                              <div style={{ width:22, height:22, borderRadius:"50%", background:T.surfaceHigh, display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, color:T.textDim, flexShrink:0 }}>{i+1}</div>
                              <div style={{ flex:1 }}>
                                <div style={{ fontSize:13, fontWeight:600 }}>{p.name}</div>
                                {p.milestones && p.milestones.length > 0 && <div style={{ fontSize:10, color:T.gold, fontWeight:700, marginTop:1 }}>🏆 {p.milestones.join(", ")}</div>}
                              </div>
                              <div style={{ display:"flex", gap:4, flexShrink:0 }}>
                                {p.tries > 0 && <div style={{ background:"rgba(57,255,20,0.1)", border:`1px solid ${T.borderGreen}`, borderRadius:6, padding:"2px 8px", fontSize:11, fontWeight:700, color:T.accent }}>{p.tries === 1 ? "1 try" : `${p.tries} tries`}</div>}
                                {p.goals > 0 && <div style={{ background:T.surfaceHigh, border:`1px solid ${T.border}`, borderRadius:6, padding:"2px 8px", fontSize:11, fontWeight:700, color:T.text }}>{p.goals} {p.goals===1?"goal":"goals"}</div>}
                                {p.fieldGoals > 0 && <div style={{ background:T.surfaceHigh, border:`1px solid ${T.border}`, borderRadius:6, padding:"2px 8px", fontSize:11, fontWeight:700, color:T.text }}>{p.fieldGoals} FG</div>}
                              </div>
                            </div>
                          ))
                        }
                      </div>
                    );
                  })
                )}
              </div>
            );
          }
        }

        if(drilldown.type === "games") {
          title = `My Games${seasonLabel} — ${allStatsGames.length}`;
          content = allStatsGames.length === 0
            ? <div style={{ fontSize:13, color:T.textDim, padding:"20px 0" }}>No games logged yet.</div>
            : allStatsGames.slice().reverse().map(g => <GameRow key={g.fixtureId} g={g}/>);
        }

        if(drilldown.type === "teams") {
          if(drilldown.detail) {
            const team = drilldown.detail;
            const games = teamMap[team] || [];
            title = <TeamName team={team} size={36} prominent />;
            content = (<div>
              <div style={{ fontSize:12, color:T.textMuted, marginBottom:12 }}>{games.length} game{games.length!==1?"s":""} attended</div>
              {games.slice().reverse().map(g=><GameRow key={g.fixtureId} g={g}/>)}
            </div>);
          } else {
            title = `Teams Seen${seasonLabel} — ${Object.keys(teamMap).length}`;
            content = Object.keys(teamMap).sort().map(team => (
              <div key={team} onClick={()=>setDrilldown({type:"teams",detail:team,allTime:drilldown.allTime})} style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 0", borderBottom:`1px solid ${T.border}`, cursor:"pointer" }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:14, fontWeight:600 }}><TeamName team={team} /></div>
                  <div style={{ fontSize:11, color:T.textMuted }}>{teamMap[team].length} game{teamMap[team].length!==1?"s":""}</div>
                </div>
                <div style={{ fontSize:18, color:T.textDim }}>›</div>
              </div>
            ));
          }
        }

        if(drilldown.type === "stadiums") {
          if(drilldown.detail) {
            const venue = drilldown.detail;
            const games = stadiumMap[venue] || [];
            title = venue;
            content = (<div>
              <div style={{ fontSize:12, color:T.textMuted, marginBottom:12 }}>{games.length} game{games.length!==1?"s":""} attended here</div>
              {games.slice().reverse().map(g=><GameRow key={g.fixtureId} g={g}/>)}
            </div>);
          } else {
            title = `Venues Visited${seasonLabel} — ${Object.keys(stadiumMap).length}`;
            content = Object.keys(stadiumMap).sort().map(venue => (
              <div key={venue} onClick={()=>setDrilldown({type:"stadiums",detail:venue,allTime:drilldown.allTime})} style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 0", borderBottom:`1px solid ${T.border}`, cursor:"pointer" }}>
                <div style={{ fontSize:22 }}>🏟️</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:14, fontWeight:600 }}>{venue}</div>
                  <div style={{ fontSize:11, color:T.textMuted }}>{stadiumMap[venue].length} game{stadiumMap[venue].length!==1?"s":""}</div>
                </div>
                <div style={{ fontSize:18, color:T.textDim }}>›</div>
              </div>
            ));
          }
        }

        if(drilldown.type === "players") {
          if(drilldown.detail) {
            const key = drilldown.detail;
            const entry = playerMap[key];
            if(!entry) return null;
            title = entry.player;
            // R18/F11: group the player's appearances by the side's team UUID
            // (one count per player/fixture), keeping UUID and label separately.
            // Missing/invalid side or null side team → one "Unknown side" group.
            // Sort by count desc, then label asc, then UUID asc.
            const sideCounts = new Map();
            for (const g of entry.games) {
              const uuid = g._seenSideTeamId ?? "__unknown__";
              if (!sideCounts.has(uuid)) sideCounts.set(uuid, { uuid: g._seenSideTeamId ?? null, label: g._seenForTeam, count: 0 });
              sideCounts.get(uuid).count++;
            }
            const sideGroups = [...sideCounts.values()].sort((a, b) => b.count - a.count || String(a.label).localeCompare(String(b.label)) || String(a.uuid ?? "").localeCompare(String(b.uuid ?? "")));
            content = (<div>
              <div style={{ fontSize:12, color:T.textMuted, marginBottom:4 }}>{sideGroups.map((grp, index) => <span key={grp.uuid ?? "__unknown__"}>{index > 0 && " · "}{grp.count} {grp.label}</span>)}</div>
              <div style={{ fontSize:12, color:T.textMuted, marginBottom:12 }}>Seen live in {entry.games.length} game{entry.games.length!==1?"s":""}</div>
              {entry.games.slice().reverse().map(g=>(
                <div key={g.fixtureId}>
                  <div style={{ fontSize:10, color:T.textDim, marginTop:8, marginBottom:2 }}>Played for <TeamName team={g._seenForTeam} size={10} /></div>
                  <GameRow g={g}/>
                </div>
              ))}
            </div>);
          } else {
            const allPlayers = Object.values(playerMap).sort((a,b)=>b.games.length-a.games.length||a.player.localeCompare(b.player));
            title = `Players Seen${seasonLabel} — ${allPlayers.length}`;
            content = (
              <div>
                {allPlayers.map(entry => {
                  const key = entry.playerId || entry.player;
                  return (
                    <div key={key} onClick={()=>setDrilldown({type:"players", detail:key, allTime:drilldown.allTime})} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 0", borderBottom:`1px solid ${T.border}`, cursor:"pointer" }}>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:13, fontWeight:600 }}>{entry.player}</div>
                      </div>
                      <div style={{ fontSize:11, color:T.accent, fontWeight:700, flexShrink:0 }}>{entry.games.length}×</div>
                      <div style={{ fontSize:18, color:T.textDim }}>›</div>
                    </div>
                  );
                })}
              </div>
            );
          }
        }

        return (
          <div style={{ position:"fixed", inset:0, zIndex:200, background:"rgba(0,0,0,0.6)", display:"flex", alignItems:"flex-end", justifyContent:"center" }} onClick={()=>setDrilldown(null)}>
            <div style={{ background:T.surface, borderRadius:`${T.radiusLg}px ${T.radiusLg}px 0 0`, width:"100%", maxWidth:600, maxHeight:"85vh", display:"flex", flexDirection:"column" }} onClick={e=>e.stopPropagation()}>
              {/* Header */}
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"18px 20px 14px", borderBottom:`1px solid ${T.border}`, flexShrink:0 }}>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  {drilldown.detail && (
                    <button onClick={()=>setDrilldown({type:drilldown.type, allTime:drilldown.allTime})} style={{ background:"transparent", border:"none", color:T.purpleLight, fontSize:14, cursor:"pointer", padding:"0 4px 0 0" }}>‹ Back</button>
                  )}
                  <div style={{ fontWeight:800, fontSize:16 }}>{title}</div>
                </div>
                <button onClick={()=>setDrilldown(null)} style={{ background:"transparent", border:"none", color:T.textMuted, fontSize:22, cursor:"pointer", lineHeight:1 }}>×</button>
              </div>
              {/* Scrollable content */}
              <div style={{ overflowY:"auto", padding:"4px 20px 24px", flex:1 }}>
                {content}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── BOTTOM NAV ── */}
      <div style={{ position:"fixed", bottom:0, left:"50%", transform:"translateX(-50%)", width:"100%", maxWidth:600, background:T.bg, borderTop:`1px solid ${T.border}`, display:"flex", zIndex:100, paddingBottom:"env(safe-area-inset-bottom,0px)" }}>
        {NAV.map(n=>(
          <button key={n.id} onClick={()=>setTab(n.id)} style={{ flex:1, background:"transparent", border:"none", cursor:"pointer", padding:"12px 0 10px", display:"flex", flexDirection:"column", alignItems:"center", gap:4, color:tab===n.id?T.accent:"rgba(12,19,12,0.4)", transition:"color 0.15s" }}>
            <div style={{ opacity: tab===n.id?1:0.5 }}>{n.icon}</div>
            <span style={{ fontSize:10, fontWeight:tab===n.id?700:400, letterSpacing:0.3 }}>{n.label}</span>
          </button>
        ))}
      </div>

      {/* ── MODALS ── */}
      {modal && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.8)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:300, padding:20 }} onClick={()=>{ if(modal.type!=="pickTeam") setModal(null); }}>
          <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.radiusLg, padding:24, maxWidth:400, width:"100%", maxHeight:"80vh", overflowY:"auto" }} onClick={e=>e.stopPropagation()}>

            {modal.type==="checkinResult" && (
              <div style={{ textAlign:"center" }}>
                <div style={{ fontWeight:800, fontSize:18, marginBottom:4 }}>Check-In Confirmed!</div>
                <div style={{ fontSize:13, color:T.textMuted, marginBottom:4 }}><TeamMatchup home={modal.data.fixture.homeRef?.name||modal.data.fixture.homeRef||modal.data.fixture.home} away={modal.data.fixture.awayRef?.name||modal.data.fixture.awayRef||modal.data.fixture.away} size={36} prominent /></div>
                <div style={{ fontSize:12, color:T.textMuted, marginBottom:12 }}>📍 {modal.data.fixture.venue?.name||modal.data.fixture.venue}</div>
                <div style={{ background:T.surfaceHigh, borderRadius:T.radiusSm, padding:"10px 14px", marginBottom:12, textAlign:"left" }}>
                  <div style={{ fontSize:14, color:T.text, fontWeight:800, marginBottom:8 }}>+{modal.data.awards.reduce((sum, award)=>sum+award.amount, 0)} XP total</div>
                  {modal.data.awards.map((award, index) => {
                    const labels = { team_first:"First check-in for this team", team_repeat:"Team check-in", venue_first:"First check-in at this venue", venue_repeat:"Venue check-in" };
                    return <div key={`${award.source_type}-${award.subject_id}-${index}`} style={{ display:"flex", justifyContent:"space-between", gap:12, fontSize:12, color:T.text, marginBottom:4 }}><span>{labels[award.source_type]}</span><b>+{award.amount} XP</b></div>;
                  })}
                </div>
                <div style={{ background:T.goldDim, border:`1px solid rgba(245,183,49,0.3)`, borderRadius:T.radiusSm, padding:"10px 14px", marginBottom:12 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:T.gold }}>Booster pack granted — {modal.data.boosterCards} cards</div>
                  <div style={{ fontSize:11, color:T.textMuted, marginTop:4 }}>Open it from Available Packs on your Home screen.</div>
                </div>
                <div style={{ fontSize:11, color:T.textMuted }}>XP is pending until the game is confirmed.</div>
                <button onClick={()=>setModal(null)} style={{ marginTop:12, width:"100%", background:T.accent, border:"none", borderRadius:T.radiusSm, padding:"12px 0", color:"#000", fontSize:14, fontWeight:700, cursor:"pointer" }}>Awesome!</button>
              </div>
            )}

            {modal.type==="checkinError" && (
              <div style={{ textAlign:"center" }}>
                <div style={{ fontWeight:800, fontSize:18, marginBottom:8 }}>{modal.data.title}</div>
                <div style={{ fontSize:13, color:T.textMuted, lineHeight:1.5, marginBottom:16 }}>{modal.data.message}</div>
                <button onClick={()=>setModal(null)} style={{ width:"100%", background:T.accent, border:"none", borderRadius:T.radiusSm, padding:"12px 0", color:"#000", fontSize:14, fontWeight:700, cursor:"pointer" }}>Got It</button>
              </div>
            )}

            {modal.type==="packReveal" && (
              <div style={{ textAlign:"center" }}>
                <div style={{ fontSize:40, marginBottom:8 }}>🎁</div>
                <div style={{ fontWeight:800, fontSize:18, marginBottom:16 }}>Pack Opened!</div>
                <div style={{ display:"flex", gap:8, justifyContent:"center", flexWrap:"wrap" }}>
                  {modal.data.draws.map((draw,i) => {
                    // TODO(round3-merge): CARD_RARITIES from @nrl/shared/cards/composite replaces this identical local rarity palette lookup.
                    const rarity = RARITY[draw.rarity] || RARITY.common;
                    const team = CARD_DEFINITIONS.find(card => card.num === draw.legacy_num)?.team || draw.team_id;
                    const duplicateLabel = packDuplicateLabel({
                      duplicate: draw.duplicate,
                      copies_after: draw.copies_after,
                    });
                    return (
                      <div key={`${draw.definition_id}-${i}`} style={{ width:110, minHeight:142, borderRadius:T.radiusSm, padding:"12px 8px", background:rarity.gradient, border:`2px solid ${rarity.color}`, boxShadow:`0 0 12px ${rarity.glow}`, color:"#fff", display:"flex", flexDirection:"column", justifyContent:"center" }}>
                        <div style={{ fontSize:11, fontWeight:800, lineHeight:1.3 }}>{draw.name}</div>
                        <div style={{ fontSize:9, opacity:0.75, marginTop:6 }}><TeamName team={team} /></div>
                        <div style={{ fontSize:9, color:rarity.color, fontWeight:700, marginTop:6 }}>{rarity.label}</div>
                        {duplicateLabel && <div style={{ alignSelf:"center", background:rarity.color, borderRadius:10, padding:"3px 7px", fontSize:10, lineHeight:1.2, color:"#111", fontWeight:800, marginTop:7 }}>{duplicateLabel}</div>}
                      </div>
                    );
                  })}
                </div>
                <button onClick={()=>setModal(null)} style={{ marginTop:16, width:"100%", background:T.purple, border:"none", borderRadius:T.radiusSm, padding:"12px 0", color:"#fff", fontSize:14, fontWeight:700, cursor:"pointer" }}>Awesome!</button>
              </div>
            )}



            {modal.type==="cardDetail" && (
              <div style={{ textAlign:"center" }}>
                <TradingCard card={modal.data} size="lg"/>
                <div style={{ marginTop:16, fontSize:13, color:T.textMuted }}><TeamName team={modal.data.team} /></div>
                <div style={{ fontSize:12, color:RARITY[modal.data.rarity].color, fontWeight:700, marginTop:4 }}>{RARITY[modal.data.rarity].emoji} {RARITY[modal.data.rarity].label} · {RARITY[modal.data.rarity].pts} pts</div>
                <div style={{ marginTop:8, fontSize:12, color:T.textMuted }}>Card #{modal.data.num} – {CARD_DEFINITIONS.filter(d=>d.team===modal.data.team).findIndex(d=>d.num===modal.data.num)+1} of 18 for {modal.data.team}</div>
                <button onClick={()=>setModal(null)} style={{ marginTop:16, width:"100%", background:T.surfaceHigh, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"11px 0", color:T.text, fontSize:14, cursor:"pointer" }}>Close</button>
              </div>
            )}

            {modal.type==="foreverSetSubmitted" && (
              <div style={{ textAlign:"center" }}>
                <div style={{ fontSize:36, marginBottom:10 }}>🗃️</div>
                <div style={{ fontWeight:800, fontSize:18, marginBottom:6 }}>{modal.data.label} Submitted!</div>
                <div style={{ fontSize:13, color:T.textMuted, marginBottom:16 }}>
                  Those cards are now locked in your Forever Collection and can't be traded. Pending until the next weekly confirmation.
                </div>
                <div style={{ background:"rgba(184,134,11,0.1)", border:"1px solid rgba(184,134,11,0.3)", borderRadius:T.radiusSm, padding:"14px", marginBottom:16 }}>
                  <div style={{ fontSize:24, fontWeight:900, color:T.gold }}>+{modal.data.xp} XP</div>
                </div>
                <button onClick={()=>setModal(null)} style={{ width:"100%", background:T.accent, border:"none", borderRadius:T.radiusSm, padding:"12px 0", color:"#000", fontSize:14, fontWeight:700, cursor:"pointer" }}>Awesome!</button>
              </div>
            )}

            {modal.type==="locationChecking" && (
              <div style={{ textAlign:"center", padding:"10px 0" }}>
                <div style={{ width:32, height:32, border:`3px solid ${T.accent}`, borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.8s linear infinite", margin:"0 auto 16px" }}/>
                <div style={{ fontWeight:700, fontSize:15, marginBottom:6 }}>Checking your location...</div>
                <div style={{ fontSize:12, color:T.textMuted }}>Make sure location access is enabled for this app.</div>
              </div>
            )}

            {modal.type==="locationFailed" && (() => {
              const reason = modal.data?.reason;
              const messages = {
                "too-far": `You don't appear to be at the venue (you're approximately ${modal.data?.distance}m away). You need to be within ${CHECKIN_RADIUS_METERS}m of the stadium to check in.`,
                "denied": "Location access was denied. Please enable location permissions for this app in your browser/device settings, then try again.",
                "unavailable": "Couldn't determine your location right now. Check your device's location/GPS settings and try again.",
                "unsupported": "Your browser doesn't support location services, so check-in isn't available right now.",
              };
              return (
                <div style={{ textAlign:"center" }}>
                  <div style={{ fontSize:32, marginBottom:10 }}>📍</div>
                  <div style={{ fontWeight:800, fontSize:16, marginBottom:8 }}>Can't Check In</div>
                  <div style={{ fontSize:13, color:T.textMuted, lineHeight:1.5, marginBottom:18 }}>{messages[reason] || "Something went wrong checking your location."}</div>
                  {reason==="debug" && (
                    <div style={{ background:T.surfaceHigh, borderRadius:T.radiusSm, padding:"12px", marginBottom:18, textAlign:"left", fontSize:11, color:T.textMuted, fontFamily:"monospace" }}>
                      <div>code: {String(modal.data?.rawCode)}</div>
                      <div>message: {String(modal.data?.rawMessage)}</div>
                      <div>secureContext: {String(modal.data?.isSecure)}</div>
                      <div>hasGeolocation: {String(modal.data?.hasGeo)}</div>
                    </div>
                  )}
                  <button onClick={()=>setModal(null)} style={{ width:"100%", background:T.surfaceHigh, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"12px 0", color:T.text, fontSize:14, fontWeight:700, cursor:"pointer" }}>Close</button>
                </div>
              );
            })()}

            {modal.type==="pickTeam" && (() => {
              const isNewSeason = modal.data?.reason === "new-season";
              return (
                <TeamPickerModal
                  isNewSeason={isNewSeason}
                  onConfirm={confirmSupportedTeam}
                  pending={teamPickPending}
                  error={teamPickError}
                />
              );
            })()}

            {modal.type==="badgeHistory" && (
              <BadgeHistoryModal badge={modal.data} events={myBadges.events} historyUnavailable={myBadges.historyUnavailable} onClose={()=>setModal(null)} />
            )}

            {modal.type==="rankDetail" && (() => {
              const gamesAttended = stats.allTime.games || 0;
              const current = getRankForGames(gamesAttended);
              const next = getNextRank(current.rank);
              const gamesUntilNext = next ? next.games - gamesAttended : null;
              return (
                <div style={{ textAlign:"center" }}>
                  <div style={{ display:"flex", justifyContent:"center", marginBottom:14 }}>
                    <BeltIcon rankTier={current} size={64}/>
                  </div>
                  <div style={{ fontWeight:800, fontSize:18, marginBottom:2 }}>{current.belt} Belt{current.stripes>0?` · ${current.stripes} Stripe${current.stripes!==1?"s":""}`:""}</div>
                  <div style={{ fontSize:12, color:T.textMuted, marginBottom:16 }}>Rank {current.rank} of {RANK_TIERS.length} · {gamesAttended} all-time games attended</div>
                  {next ? (
                    <div style={{ background:T.surfaceHigh, borderRadius:T.radiusSm, padding:"12px 14px", marginBottom:16 }}>
                      <div style={{ fontSize:12, color:T.textMuted, marginBottom:6 }}>
                        {gamesUntilNext} more game{gamesUntilNext!==1?"s":""} to reach <b style={{ color:T.text }}>{next.belt} Belt{next.stripes>0?` · ${next.stripes} Stripe${next.stripes!==1?"s":""}`:""}</b>
                      </div>
                      <div style={{ background:T.border, borderRadius:4, height:6, overflow:"hidden" }}>
                        <div style={{ height:"100%", background:T.accent, width:`${Math.min(100,(gamesAttended-current.games)/(next.games-current.games)*100)}%`, borderRadius:4 }}/>
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize:13, color:T.gold, fontWeight:700, marginBottom:16 }}>🏆 Maximum rank achieved!</div>
                  )}
                  <button onClick={()=>setModal(null)} style={{ width:"100%", background:T.accent, border:"none", borderRadius:T.radiusSm, padding:"12px 0", color:"#000", fontSize:14, fontWeight:700, cursor:"pointer" }}>Close</button>
                </div>
              );
            })()}

            {modal.type==="info" && (
              <div style={{ textAlign:"center" }}>
                <div style={{ fontSize:40, marginBottom:12 }}>ℹ️</div>
                <div style={{ fontSize:14, color:T.text, marginBottom:20 }}>{modal.data}</div>
                <button onClick={()=>setModal(null)} style={{ width:"100%", background:T.purple, border:"none", borderRadius:T.radiusSm, padding:"12px 0", color:"#fff", fontSize:14, fontWeight:700, cursor:"pointer" }}>Got It</button>
              </div>
            )}

            {modal.type==="pointsBreakdown" && (
              <XpBreakdownModal
                seasonYear={myXp.seasonYear ?? CURRENT_SEASON}
                releasedTotal={myXp.releasedTotal}
                pendingTotal={myXp.pendingTotal}
                sections={deriveXpSections(myXp.rows).map(s => ({ ...s, color: T[s.color] }))}
                onClose={()=>setModal(null)}
              />
            )}
          </div>
        </div>
      )}
    </div>
    </>
  );
}

// ── Round 8: leaderboard-loading hook (server read model) ───────────────────
// `leaderboard` ({ rows, me, seasonYear, round, error, loading }) is loaded
// via fetchLeaderboard when the signed-in user is known, refreshed whenever
// the board tab opens or the period/team filter changes, reset on user
// change, and stale responses for a previous user are ignored. Same shape as
// useMyBadges. Display-only; the client never writes reward state (SEC-3).

// ── Round 8 rem2: supported-team picker (extracted) ─────────────────────────
// The pickTeam modal body, extracted to module scope so a failed save can
// KEEP the picker open with an inline T.red error line (above the buttons) and
// a retry. While `pending` every button is disabled (opacity 0.6) and clicks
// are ignored. Same markup and copy as the inline body it replaces.
export function TeamPickerModal({ isNewSeason, onConfirm, pending, error }) {
  return (
    <div>
      <div style={{ textAlign:"center", marginBottom:16 }}>
        <div style={{ fontSize:32, marginBottom:8 }}>🏉</div>
        <div style={{ fontWeight:800, fontSize:18, marginBottom:6 }}>{isNewSeason ? `Confirm your team for ${CURRENT_SEASON}` : "Which team do you support?"}</div>
        <div style={{ fontSize:13, color:T.textMuted, lineHeight:1.5 }}>
          {isNewSeason
            ? "A new season is starting soon. Pick the team you'll support this season — you won't be able to change it once the season kicks off."
            : "Pick the team you support. You won't be able to change it for the rest of the season, but you can select \"No team\" if you'd rather not choose."}
        </div>
      </div>
      {error && (
        <div style={{ fontSize:12, color:T.red, marginBottom:12 }}>{error}</div>
      )}
      <div style={{ maxHeight:320, overflowY:"auto", marginBottom:16 }}>
        {TEAM_NAMES.map(team => (
          <button key={team} onClick={()=>{ if(!pending) onConfirm(team); }} style={{ width:"100%", textAlign:"left", background:T.surfaceHigh, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"12px 14px", marginBottom:8, color:T.text, fontSize:14, fontWeight:600, cursor:pending?"default":"pointer", opacity:pending?0.6:1 }}>
            <TeamName team={team} size={32} prominent />
          </button>
        ))}
      </div>
      <button onClick={()=>{ if(!pending) onConfirm(null); }} style={{ width:"100%", background:"transparent", border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"11px 0", color:T.textMuted, fontSize:13, cursor:pending?"default":"pointer", opacity:pending?0.6:1 }}>
        No team / prefer not to choose
      </button>
    </div>
  );
}

// ── Round 9: supported-team card on the More tab (pick-if-none) ──────────────
// A member with no team yet sees a "Pick your team" button that re-opens the
// existing TeamPickerModal flow (set_supported_team; a "None" pick also goes
// through it). A member with a team sees the team name via <TeamName> and the
// season-lock copy (RUL-11: members never change a set team). The card sits
// ABOVE the Change password card and keeps the More style (UX-1).
export function SupportedTeamCard({ teamName, confirmedSeason, seasonYear, onPick }) {
  const hasTeam = Boolean(teamName);
  return (
    <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.radius, padding:16, marginBottom:14 }}>
      <div style={{ fontSize:13, fontWeight:700, color:T.text, marginBottom: hasTeam ? 10 : 8 }}>Supported team</div>
      {hasTeam ? (
        <>
          <div style={{ marginBottom:10 }}>
            <TeamName team={teamName} size={20} prominent />
          </div>
          <div style={{ fontSize:13, color:T.textMuted, lineHeight:1.5 }}>
            Locked for the {seasonYear} season.
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize:13, color:T.textMuted, lineHeight:1.5, marginBottom:12 }}>
            You haven't picked a team yet.
          </div>
          <button type="button" onClick={onPick} style={{ width:"100%", background:T.accent, border:"none", borderRadius:T.radiusSm, padding:"13px 0", color:"#000", fontSize:14, fontWeight:800, cursor:"pointer" }}>
            Pick your team
          </button>
        </>
      )}
    </div>
  );
}

function useLeaderboard(user, tab, period, teamFilter, epochTick) {
  const [data, setData] = useState(() => ({ rows: [], me: null, seasonYear: null, round: null, error: false, loading: false }));
  const currentUserId = useRef(user?.id);
  const requestId = useRef(0);
  // ONE request lifecycle (single effect keyed on user/tab/period/team). A late
  // initial response can no longer overwrite the active filter's rows: each run
  // bumps a requestId guard and only the result whose id still matches — and
  // whose user has not changed mid-flight — is applied.
  useEffect(() => {
    const prevUserId = currentUserId.current;
    const nextUserId = user?.id;
    if (prevUserId !== nextUserId) {
      currentUserId.current = nextUserId;
      setData({ rows: [], me: null, seasonYear: null, round: null, error: false, loading: false });
    }
    // Invalidate any in-flight request from a previous run (user change, filter
    // change, or leaving the board tab) before deciding whether to fetch.
    const readGeneration = getClockGeneration();
    const myId = ++requestId.current;
    if ((tab !== "board" && !epochTick) || !nextUserId) return;
    setData(prev => ({ ...prev, loading: true }));
    (async () => {
      const res = await fetchLeaderboard({ period, teamName: teamFilter === "all" ? null : teamFilter });
      if (readGeneration === getClockGeneration() && myId === requestId.current && currentUserId.current === nextUserId) {
        setData({ ...res, loading: false });
      }
    })();
    // F-r8-5: mark this request stale on cleanup (unmount or dependency change)
    // so a component that has unmounted — or moved to another period/team —
    // never calls setData with a late response.
    return () => { requestId.current += 1; };
  }, [user?.id, tab, period, teamFilter, epochTick]);
  return data;
}

export { useLeaderboard };

// ── Round 8: Leaderboard tab (extracted screen) ──────────────────────────────
// Renders the Season | Weekly pill toggle + the existing team select, the
// ranked rows from get_leaderboard (same row markup, "(you)" highlight, rank +
// games-logged line, XP figure), and the caller's own row appended after an
// "…" divider when it falls outside the list. Empty/error/weekly-empty copies
// degrade per-resource. Keeps the prototype's look and feel (UX-1: T tokens,
// row markup, copy) — additions are the toggle, rank number and own row.
export function LeaderboardScreen({ rows, me, period, onPeriodChange, teamFilter, onTeamChange, loading, error, round }) {
  const list = Array.isArray(rows) ? rows : [];
  const myRow = me || null;
  const meInRows = myRow && list.some(r => r && r.userId === myRow.userId);
  const renderRow = (u, isFirst, key) => {
    const isMe = !!(u && u.isMe);
    return (
      <div key={key} style={{ background:isFirst?"rgba(245,183,49,0.08)":isMe?T.greenDim:T.surface, border:`1px solid ${isFirst?"rgba(245,183,49,0.3)":isMe?T.borderGreen:T.border}`, borderRadius:T.radius, padding:"16px", marginBottom:10, display:"flex", alignItems:"center", gap:12 }}>
        <div style={{ width:36, height:36, borderRadius:"50%", background:isFirst?"rgba(245,183,49,0.2)":T.surfaceHigh, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>
          {isFirst?"🏆":(u&&u.avatar)||"🏉"}
        </div>
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:700, fontSize:14, color:isMe?T.accent:T.text }}>{u&&u.displayName}{isMe&&" (you)"}</div>
          <div style={{ fontSize:12, color:T.textMuted, marginTop:2 }}>#{u&&u.rank} · {u&&u.games} games logged{u&&u.supportedTeamName && <> · <TeamName team={u.supportedTeamName} /></>}</div>
        </div>
        <div style={{ textAlign:"right" }}>
          <div style={{ fontWeight:900, fontSize:20, color:isFirst?T.gold:isMe?T.accent:T.text }}>{((u&&u.xp)||0).toLocaleString()}</div>
          <div style={{ fontSize:10, color:T.textDim, letterSpacing:1 }}>XP</div>
        </div>
      </div>
    );
  };
  return (
    <div style={{ padding:"16px" }}>
      <div style={{ display:"flex", gap:6, marginBottom:14 }}>
        <button onClick={()=>onPeriodChange("season")} style={{ background:period==="season"?T.accent:"transparent", border:`1px solid ${period==="season"?T.accent:T.border}`, borderRadius:20, padding:"5px 12px", color:period==="season"?"#000":T.textMuted, fontSize:12, fontWeight:600, cursor:"pointer" }}>{"Season"}</button>
        <button onClick={()=>onPeriodChange("weekly")} style={{ background:period==="weekly"?T.accent:"transparent", border:`1px solid ${period==="weekly"?T.accent:T.border}`, borderRadius:20, padding:"5px 12px", color:period==="weekly"?"#000":T.textMuted, fontSize:12, fontWeight:600, cursor:"pointer" }}>{"Weekly"}</button>
      </div>
      <select value={teamFilter} onChange={e=>onTeamChange(e.target.value)} style={{ width:"100%", background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"10px 12px", color:T.text, fontSize:13, fontWeight:600, marginBottom:14, appearance:"none" }}>
        <option value="all">All teams</option>
        {TEAM_NAMES.map(team => <option key={team} value={team}>{team}</option>)}
      </select>
      {period === "season" && round == null && (
        <div style={{ fontSize:12, color:T.textMuted, marginBottom:10 }}>XP appears here after the first Weekly Confirmation release.</div>
      )}
      {loading ? (
        <div style={{ textAlign:"center", padding:"30px 0", color:T.textMuted, fontSize:13 }}>Loading…</div>
      ) : error ? (
        <div style={{ textAlign:"center", padding:"30px 0", color:T.textDim, fontSize:13 }}>Leaderboard unavailable right now.</div>
      ) : period === "weekly" && round == null ? (
        <div style={{ textAlign:"center", padding:"30px 0", color:T.textDim, fontSize:13 }}>No round has been released yet — check back after the next Weekly Confirmation.</div>
      ) : list.length === 0 ? (
        <div style={{ textAlign:"center", padding:"30px 0", color:T.textDim, fontSize:13 }}>{teamFilter !== "all" ? "No users support this team yet." : "No one on the board yet."}</div>
      ) : (
        list.map((u, i) => renderRow(u, i === 0, u && u.userId))
      )}
      {!loading && !error && myRow && !meInRows && (
        <>
          <div style={{ textAlign:"center", color:T.textDim, fontSize:13, padding:"6px 0" }}>…</div>
          {renderRow(myRow, false, "me")}
        </>
      )}
    </div>
  );
}

// ── Round 9: Home XP card + breakdown modal (server truth) ───────────────────
// `XpCard` and `XpBreakdownModal` are the extracted Home XP card and its
// breakdown modal, fed by `useMyXp` (fetchMyXp → get_my_xp). The big number is
// the member's released ledger total for the season — the same number the
// leaderboard ranks (R1); "+N XP pending" is the pending sum, kept separate.
// Sections come from deriveXpSections (released rows only; R2). The card and
// modal keep the prototype's markup and copy (UX-1). Display-only; the client
// never writes reward state (SEC-3).
export function XpCard({ releasedTotal, pendingTotal, pendingGames, onOpen }) {
  return (
    <div onClick={onOpen} style={{ background:T.accentDim, border:`1px solid ${T.borderGreen}`, borderRadius:T.radius, padding:"20px", cursor:"pointer", marginBottom:16 }}>
      <div style={{ fontSize:11, fontWeight:700, color:T.accent, letterSpacing:2, marginBottom:4 }}>YOUR XP</div>
      <div style={{ fontSize:52, fontWeight:900, color:T.accent, lineHeight:1 }}>{releasedTotal.toLocaleString()}</div>
      <div style={{ fontSize:11, color:T.accent, marginTop:8, opacity:0.7 }}>Tap to see breakdown →</div>
      {pendingTotal > 0 && (
        <div style={{ fontSize:11, color:T.accent, marginTop:6, opacity:0.6 }}>+{pendingTotal.toLocaleString()} XP pending</div>
      )}
      {pendingGames > 0 && (
        <div style={{ fontSize:11, color:T.gold, marginTop:8, fontWeight:600 }}>
          {pendingGames === 1
            ? "1 game pending confirmation - XP will update once reviewed"
            : `${pendingGames} games pending confirmation - XP will update once reviewed`}
        </div>
      )}
    </div>
  );
}

export function XpBreakdownModal({ seasonYear, releasedTotal, pendingTotal, sections, onClose }) {
  const total = releasedTotal || 0;
  return (
    <div>
      <div style={{ textAlign:"center", marginBottom:16 }}>
        <div style={{ fontSize:13, color:T.textMuted, marginBottom:2 }}>{seasonYear} Season XP</div>
        <div style={{ fontSize:40, fontWeight:900, color:T.purpleLight }}>{total.toLocaleString()}</div>
      </div>

      {/* Bar chart overview */}
      <div style={{ marginBottom:20 }}>
        {sections.map(s => (
          <div key={s.label} style={{ marginBottom:8 }}>
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:3 }}>
              <span style={{ color:T.textMuted }}>{s.icon} {s.label}</span>
              <span style={{ fontWeight:700, color:s.color }}>{s.pts} XP</span>
            </div>
            <div style={{ background:T.surfaceHigh, borderRadius:4, height:6, overflow:"hidden" }}>
              <div style={{ height:"100%", background:s.color, width:`${total>0?(s.pts/total)*100:0}%`, borderRadius:4, transition:"width 0.5s" }}/>
            </div>
          </div>
        ))}
      </div>

      {/* Detailed rows per section */}
      {sections.map(s => s.rows.length > 0 && (
        <div key={s.label} style={{ marginBottom:14 }}>
          <div style={{ fontSize:11, fontWeight:700, color:T.textDim, letterSpacing:1, marginBottom:6 }}>{s.label.toUpperCase()}</div>
          {s.rows.slice(0,8).map((r,i) => (
            <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"6px 10px", borderRadius:T.radiusSm, background: r.isNew?"rgba(34,212,124,0.06)":T.surfaceHigh, marginBottom:3 }}>
              <span style={{ fontSize:12, color:T.text, flex:1 }}>{r.label}</span>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                {r.isNew && <span style={{ fontSize:9, color:T.green, fontWeight:700, background:"rgba(34,212,124,0.15)", borderRadius:4, padding:"1px 5px" }}>FIRST TIME ×2</span>}
                <span style={{ fontSize:12, fontWeight:700, color:s.color }}>+{r.pts}</span>
              </div>
            </div>
          ))}
          {s.rows.length > 8 && <div style={{ fontSize:11, color:T.textDim, padding:"4px 10px" }}>+{s.rows.length-8} more...</div>}
        </div>
      ))}

      {pendingTotal > 0 && (
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"6px 10px", borderRadius:T.radiusSm, background:T.surfaceHigh, marginBottom:3 }}>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:12, color:T.text }}>Pending — verified check-ins</div>
            <div style={{ fontSize:10, color:T.textMuted, marginTop:2 }}>Releases when games are confirmed</div>
          </div>
          <span style={{ fontSize:12, fontWeight:700, color:T.accent }}>+{pendingTotal.toLocaleString()} XP</span>
        </div>
      )}
      {releasedTotal > 0 && (
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"6px 10px", borderRadius:T.radiusSm, background:T.surfaceHigh, marginBottom:3 }}>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:12, color:T.text }}>Released — confirmed rounds</div>
            <div style={{ fontSize:10, color:T.textMuted, marginTop:2 }}>Counts towards your total</div>
          </div>
          <span style={{ fontSize:12, fontWeight:700, color:T.accent }}>{releasedTotal.toLocaleString()} XP released</span>
        </div>
      )}

      {total === 0 && (
        <div style={{ textAlign:"center", padding:"20px 0", color:T.textDim, fontSize:13 }}>Log your first game to start earning points!</div>
      )}

      <button onClick={onClose} style={{ width:"100%", background:T.purple, border:"none", borderRadius:T.radiusSm, padding:"12px 0", color:"#fff", fontSize:14, fontWeight:700, cursor:"pointer", marginTop:4 }}>Close</button>
    </div>
  );
}

// `useMyXp` (same shape as useMyBadges) loads the signed-in user's released +
// pending ledger for the current season via fetchMyXp → get_my_xp. Fetched on
// sign-in and whenever the Home or Board tab opens (the big number mirrors the
// leaderboard), reset on user change, stale responses ignored, and refetched
// after the check-in flow's refreshTick. Display-only (SEC-3).
function useMyXp(user, tab, refreshTick) {
  const [myXp, setMyXp] = useState(() => ({ seasonYear: null, releasedTotal: 0, pendingTotal: 0, rows: [], error: false }));
  const currentUserId = useRef(user?.id);
  currentUserId.current = user?.id;
  useEffect(() => {
    setMyXp({ seasonYear: null, releasedTotal: 0, pendingTotal: 0, rows: [], error: false });
    if (!user?.id) return;
    const fetchedUserId = user.id;
    const readGeneration = getClockGeneration();
    let active = true;
    (async () => {
      const res = await fetchMyXp(CURRENT_SEASON);
      if (active && readGeneration === getClockGeneration() && currentUserId.current === fetchedUserId) setMyXp(res);
    })();
    return () => { active = false; };
  }, [user?.id]);
  useEffect(() => {
    if (!user?.id) return;
    const fetchedUserId = user.id;
    const readGeneration = getClockGeneration();
    let active = true;
    (async () => {
      const res = await fetchMyXp(CURRENT_SEASON);
      if (active && readGeneration === getClockGeneration() && currentUserId.current === fetchedUserId) setMyXp(res);
    })();
    return () => { active = false; };
  }, [tab, user?.id, refreshTick]);
  return myXp;
}

// `useMyStats` (R4) loads the signed-in member's Home attendance stats from
// `get_my_stats` (via fetchMyStats). The state is keyed by the signed-in user
// id and the app-clock generation: it is cleared to empty on sign-out, on an
// identity change and on a generation change (offset or epoch — both increment
// getClockGeneration()); a response whose request generation or user has been
// superseded is discarded; a failed fetch keeps the last values only within the
// same identity and generation. All three fetch sites share one request serial
// so an older response can never overwrite a newer one (review must-fix 4).
// Three fetch sites cover the ruled triggers: identity/epoch reset (Home
// initial + epoch), Home tab entry + the 120-second refresh tick, and every
// onAppClock change (offset or epoch) plus the post-check-in refresh. Until the
// first response arrives the tiles show 0 and the pending line is hidden.
// Display-only (SEC-3).
function useMyStats(user, tab, refreshTick, clockTick, epochTick, checkInRefreshTick) {
  const [stats, setStats] = useState(() => EMPTY_MY_STATS());
  const currentUserId = useRef(user?.id);
  currentUserId.current = user?.id;
  // Shared request serial across all three fetch triggers so an older response
  // can never overwrite a newer one (review must-fix 4).
  const requestSerial = useRef(0);
  // The app-clock generation the retained stats belong to. A new generation
  // (offset or epoch change) clears the retained values even when its fetch
  // fails (review must-fix 3).
  const statsGeneration = useRef(null);

  // Stamps the generation (clearing if it changed), allocates the next request
  // serial, and returns a guard that accepts only the latest response for the
  // same user and generation. Each effect calls fetchMyStats directly so the
  // three ruled triggers remain structurally distinct.
  function beginStatsRequest() {
    const fetchedUserId = currentUserId.current;
    const readGeneration = getClockGeneration();
    if (statsGeneration.current !== readGeneration) {
      statsGeneration.current = readGeneration;
      setStats(EMPTY_MY_STATS());
    }
    const serial = ++requestSerial.current;
    return () => serial === requestSerial.current
      && currentUserId.current === fetchedUserId
      && readGeneration === getClockGeneration();
  }

  // Identity / epoch reset: blank on sign-out / identity change / epoch change,
  // then fetch for the new identity/generation.
  useEffect(() => {
    if (!user?.id) {
      setStats(EMPTY_MY_STATS());
      statsGeneration.current = null;
      return;
    }
    const isLatest = beginStatsRequest();
    (async () => {
      const res = await fetchMyStats();
      if (isLatest() && res.ok) setStats(res.stats);
    })();
  }, [user?.id, epochTick]);

  // Home tab entry + the existing 120-second refresh tick.
  useEffect(() => {
    if (!user?.id) return;
    const isLatest = beginStatsRequest();
    (async () => {
      const res = await fetchMyStats();
      if (isLatest() && res.ok) setStats(res.stats);
    })();
  }, [tab, user?.id, refreshTick]);

  // Every onAppClock change (offset or epoch — clockTick) and post-check-in.
  useEffect(() => {
    if (!user?.id) return;
    const isLatest = beginStatsRequest();
    (async () => {
      const res = await fetchMyStats();
      if (isLatest() && res.ok) setStats(res.stats);
    })();
  }, [user?.id, clockTick, checkInRefreshTick]);

  return stats;
}

// ── Round 7: badge-loading hook (server truth) ──────────────────────────────
// `myBadges` (EMPTY_BADGES default) is loaded via fetchMyBadges(CURRENT_SEASON)
// when the signed-in user is known and refreshed whenever the Badges tab opens
// (cheap; RLS-scoped). Display-only; the client never writes reward state
// (SEC-3). Extracted to module scope so App stays focused on composition.
function useMyBadges(user, tab, epochTick) {
  const [myBadges, setMyBadges] = useState(EMPTY_BADGES());
  const currentUserId = useRef(user?.id);
  currentUserId.current = user?.id;
  useEffect(() => {
    setMyBadges(EMPTY_BADGES());
    if (!user?.id) return;
    const fetchedUserId = user.id;
    const readGeneration = getClockGeneration();
    let active = true;
    (async () => {
      const res = await fetchMyBadges(CURRENT_SEASON);
      if (active && readGeneration === getClockGeneration() && currentUserId.current === fetchedUserId) setMyBadges(res);
    })();
    return () => { active = false; };
  }, [user?.id, epochTick]);
  useEffect(() => {
    if (tab !== "badges" || !user?.id) return;
    const fetchedUserId = user.id;
    const readGeneration = getClockGeneration();
    let active = true;
    (async () => {
      const res = await fetchMyBadges(CURRENT_SEASON);
      if (active && readGeneration === getClockGeneration() && currentUserId.current === fetchedUserId) setMyBadges(res);
    })();
    return () => { active = false; };
  }, [tab, user?.id]);
  return myBadges;
}

// ── Round 7: badge history modal (server events) ────────────────────────────
// Reads `myBadges.events` filtered to the badge (`badgeEvents`), sorted
// newest-first; each event shows the player name (when present) and
// homeRef v awayRef · State of Origin|Rd N · date from the embedded fixture
// (TeamMatchup when both refs resolve to teams, else plain text). Exploration
// badges keep today's "earned once per season based on your cumulative total"
// copy; "EARNED N TIMES" counts the events. T + TeamMatchup are module scope.
function BadgeHistoryModal({ badge, events, historyUnavailable, onClose }) {
  const isExploration = badge.cat === "exploration";
  const badgeEvents = (events||[]).filter(e => e.badgeId === badge.id).slice().sort((a,b) => (b.at < a.at ? -1 : b.at > a.at ? 1 : 0));
  return (
    <div>
      <div style={{ textAlign:"center", marginBottom:16 }}>
        <div style={{ fontSize:32, marginBottom:8 }}>{badge.icon}</div>
        <div style={{ fontWeight:800, fontSize:17 }}>{badge.name}</div>
        <div style={{ fontSize:12, color:T.textMuted, marginTop:4 }}>{badge.desc}</div>
      </div>
      {isExploration ? (
        <div style={{ background:T.surfaceHigh, borderRadius:T.radiusSm, padding:"14px", textAlign:"center", marginBottom:16 }}>
          <div style={{ fontSize:13, color:T.textMuted }}>This badge is earned once per season based on your cumulative total — it doesn't have individual earning events to list.</div>
        </div>
      ) : historyUnavailable ? (
        <div style={{ fontSize:13, color:T.textDim, textAlign:"center", padding:"10px 0" }}>History is unavailable right now — the badge is still yours.</div>
      ) : (
        <div style={{ marginBottom:16 }}>
          <div style={{ fontSize:11, fontWeight:700, color:T.textDim, letterSpacing:1.5, marginBottom:10 }}>
            EARNED {badgeEvents.length} TIME{badgeEvents.length!==1?"S":""}
          </div>
          {badgeEvents.length === 0 && (
            <div style={{ fontSize:13, color:T.textDim, textAlign:"center", padding:"10px 0" }}>No detailed history recorded yet.</div>
          )}
          {badgeEvents.map((entry, i) => {
            const fx = entry.fixture;
            const hasRefs = fx && fx.homeRef && fx.awayRef;
            const roundLabel = fx ? (fx.isOrigin ? "State of Origin" : (fx.round ? `Rd ${fx.round}` : "")) : "";
            const dateLabel = fx && fx.matchDate ? new Date(fx.matchDate).toLocaleDateString("en-AU",{day:"numeric",month:"short",year:"numeric"}) : "";
            return (
              <div key={i} style={{ background:T.surfaceHigh, borderRadius:T.radiusSm, padding:"10px 12px", marginBottom:8 }}>
                {entry.playerName && <div style={{ fontSize:13, fontWeight:700, color:T.text, marginBottom:2 }}>{entry.playerName}</div>}
                {fx ? (
                  <div style={{ fontSize:12, color:T.textMuted }}>
                    {hasRefs ? <TeamMatchup home={fx.homeRef} away={fx.awayRef} /> : `${fx.homeRef||""} v ${fx.awayRef||""}`}
                    {roundLabel ? ` · ${roundLabel}` : ""}{dateLabel ? ` · ${dateLabel}` : ""}
                  </div>
                ) : (
                  <div style={{ fontSize:12, color:T.textMuted }}>Game details unavailable</div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <button onClick={onClose} style={{ width:"100%", background:T.accent, border:"none", borderRadius:T.radiusSm, padding:"12px 0", color:"#000", fontSize:14, fontWeight:700, cursor:"pointer" }}>Close</button>
    </div>
  );
}

// Round 10 remediation (M1/M6) — Forever is pulled out into its own
// self-contained, testable component, matching AdminConfirmScreen/
// AdminSquadsScreen: it owns every read this feature needs (definitions +
// submissions, the card-team catalog, and its own collection-availability
// read — COLLECTION failures get the same error+Retry surface as the
// Forever/catalog reads, closing the gap the closure review found at the
// old App.jsx:3171/:3503), the submit busy/actor-guard lifecycle, and reset
// on account/season change (via the `key` its caller renders it with, the
// same pattern as TradingBoardScreen). `onSubmitted` lets the caller keep
// its own collection mirror (used by stats/Trading) in sync after a lock;
// `onSuccess` lets it show the shared success modal.
export function ForeverSection({ userId, season, onSubmitted, onSuccess }) {
  const [definitions, setDefinitions] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [cardTeamIds, setCardTeamIds] = useState(null);
  const [availability, setAvailability] = useState({});
  const [loading, setLoading] = useState(false);
  const [readError, setReadError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [retryTick, setRetryTick] = useState(0);
  const submitInFlight = useRef(false);
  useEffect(() => onAppClock(() => setRetryTick(t => t + 1)), []);
  // The caller keys this component on user id + season (like TradingBoardScreen's
  // own `key={user?.id}`), so an account/season change unmounts this instance
  // outright rather than mutating its props in place — a genuinely fresh
  // component owns the new account's reads from scratch. A submission started
  // just before that remount is still an in-flight promise, though; it is not
  // cancelled, so its continuation must check `mounted` before touching state
  // or invoking onSuccess/onSubmitted — otherwise a locked-in-the-old-account
  // result could show its success modal (or refresh the collection mirror)
  // for whichever account is active by the time it resolves (M6).
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  async function loadAll() {
    const [forever, teams, collection] = await Promise.all([getMyForever(season), fetchCardTeams(), fetchMyCollection()]);
    if (!forever || !teams || collection.ids === null) return null;
    const teamMap = {};
    teams.forEach(row => { teamMap[row.legacyNum] = row.teamId; });
    const copies = collection.copies || {};
    const locked = collection.lockedCopies || {};
    const avail = Object.fromEntries(Object.keys(copies).map(id => [id, Math.max(0, (copies[id] || 0) - (locked[id] || 0))]));
    return { definitions: forever.definitions, submissions: forever.submissions, cardTeamIds: teamMap, availability: avail };
  }
  function applyLoad(res) {
    if (!res) { setReadError(true); return; }
    setReadError(false);
    setDefinitions(res.definitions);
    setSubmissions(res.submissions);
    setCardTeamIds(res.cardTeamIds);
    setAvailability(res.availability);
  }

  useEffect(() => {
    if (!userId) return;
    const readGeneration = getClockGeneration();
    let active = true;
    setLoading(true); setReadError(false);
    loadAll().then(res => {
      if (!active || readGeneration !== getClockGeneration()) return;
      setLoading(false);
      applyLoad(res);
    });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, season, retryTick]);

  function retry() { setRetryTick(t => t + 1); }

  const foreverCardDefs = useMemo(() => (cardTeamIds ? CARD_DEFINITIONS.map(d => ({ num: d.num, team: cardTeamIds[d.num] })) : []), [cardTeamIds]);
  const teamNames = useMemo(() => {
    const map = {};
    if (!cardTeamIds) return map;
    CARD_DEFINITIONS.forEach(d => { const teamId = cardTeamIds[d.num]; if (teamId && !map[teamId]) map[teamId] = d.team; });
    return map;
  }, [cardTeamIds]);
  const progress = foreverCardDefs.length ? foreverProgress(availability, foreverCardDefs, definitions) : {};

  async function handleSubmit(setId, selection, requestId) {
    const operationGeneration = getClockGeneration();
    if (submitInFlight.current) return { ok: false, code: "in_flight" };
    submitInFlight.current = true;
    setBusy(true);
    setSubmitError(null);
    const def = definitions.find(d => d.id === setId);
    const result = await submitForeverSet(setId, selection, requestId);
    submitInFlight.current = false;
    // M6: a result belongs to the account/season that submitted it — once
    // this instance has been replaced (account or season change unmounted
    // it), neither the success modal nor an error should surface, and its
    // own local state must not update either.
    if (!mounted.current || operationGeneration !== getClockGeneration()) return result;
    if (result.ok) {
      onSuccess?.(def?.label || result.setId, result.xp);
      const res = await loadAll();
      if (!mounted.current || operationGeneration !== getClockGeneration()) return result;
      if (mounted.current) applyLoad(res);
      onSubmitted?.();
      if (mounted.current) setBusy(false);
      return result;
    }
    setSubmitError(foreverErrorCopy(result.code));
    if (result.code === "set_incomplete") {
      const res = await loadAll();
      if (!mounted.current || operationGeneration !== getClockGeneration()) return result;
      if (mounted.current) applyLoad(res);
    }
    if (mounted.current) setBusy(false);
    return result;
  }

  return (
    <ForeverScreen
      definitions={definitions}
      submissions={submissions}
      progress={progress}
      teamNames={teamNames}
      loading={loading}
      error={readError}
      onRetry={retry}
      busy={busy}
      submitError={submitError}
      onSubmit={handleSubmit}
      onClearError={() => setSubmitError(null)}
    />
  );
}

// Round 10 (R26) — Forever sets on server truth. `definitions`/`submissions`
// come from get_my_forever; `progress` is @nrl/shared's foreverProgress over
// the collection mirror's availability. This component owns only the pick-a-
// team/pick-a-number chooser UI; submit_forever_set decides everything else.
export function ForeverScreen({ definitions = [], submissions = [], progress = {}, teamNames = {}, loading, error, onRetry, busy, submitError, onSubmit, onClearError }) {
  const [selecting, setSelecting] = useState(null); // { setId, kind, teams?, legacyNum?, requestId } | null
  // Round 10 remediation (M1) — a synchronous guard against a rapid double
  // click firing two submit calls before the `busy` prop re-renders in.
  const submitInFlight = useRef(false);

  function openChooser(def) {
    const p = progress[def.id];
    if (!p?.completable) return;
    onClearError?.();
    // One request id per logical confirmed selection (M1) — minted once when
    // the chooser opens for a deliberate new submission, then held across any
    // retry of this same selection (the chooser stays open on failure).
    const requestId = newForeverRequestId();
    if (def.id === "common") { setSelecting({ setId: def.id, kind:"common", requestId, uncertain:false }); return; }
    if (def.id === "fullnum") { setSelecting({ setId: def.id, kind:"fullnum", legacyNum: p.suggestedSelection?.legacy_num, requestId, uncertain:false }); return; }
    setSelecting({ setId: def.id, kind:"teams", teams: p.suggestedSelection?.teams || [], requestId, uncertain:false });
  }
  function closeChooser() {
    if (busy) return; // stay blocked until success handling (incl. refresh) completes
    setSelecting(null);
    onClearError?.();
  }
  // Round 10 remediation (M1) — the request id is a key for the CONFIRMED
  // payload, not the chooser session: once a submission for it comes back
  // uncertain (we don't know whether the server received it), the selection
  // must freeze. Editing it here and resubmitting under the same id would let
  // the server's replay branch answer for a payload it never actually saw.
  // The only way out is to resolve the outstanding attempt — retry it
  // unchanged (same id, same payload) — which is still the only submit path
  // available while `uncertain` is true.
  function toggleTeam(def, teamId) {
    setSelecting(prev => {
      if (!prev || prev.uncertain || busy) return prev;
      const need = def.teams_required || 0;
      if (prev.teams.includes(teamId)) return { ...prev, teams: prev.teams.filter(t => t !== teamId) };
      if (prev.teams.length >= need) return prev;
      return { ...prev, teams: [...prev.teams, teamId] };
    });
  }
  function setFullNumber(legacyNum) {
    setSelecting(prev => (!prev || prev.uncertain || busy) ? prev : { ...prev, legacyNum });
  }
  function selectionPayload() {
    if (!selecting) return null;
    if (selecting.kind === "common") return {};
    if (selecting.kind === "fullnum") return { legacy_num: selecting.legacyNum };
    return { teams: selecting.teams };
  }
  async function confirmSubmit() {
    if (!selecting || busy || submitInFlight.current) return;
    const requestId = selecting.requestId;
    submitInFlight.current = true;
    try {
      const result = await onSubmit(selecting.setId, selectionPayload(), requestId);
      if (result?.ok) { setSelecting(null); return; }
      // Only network_error is genuinely uncertain (the request may or may not
      // have reached the server); every other failure code is the server
      // explicitly answering "no", so the selection is safe to edit again.
      setSelecting(prev => (prev && prev.requestId === requestId) ? { ...prev, uncertain: result?.code === "network_error" } : prev);
    } finally {
      submitInFlight.current = false;
    }
  }

  const selectingDef = selecting ? definitions.find(d => d.id === selecting.setId) : null;
  const ready = !selecting ? false
    : selecting.kind === "teams" ? selecting.teams.length === (selectingDef?.teams_required || 0)
    : selecting.kind === "fullnum" ? Boolean(selecting.legacyNum)
    : true;

  return (
    <div>
      <div style={{ fontSize:13, color:T.textMuted, lineHeight:1.6, marginBottom:16 }}>
        Complete a set below and submit it to your Forever Collection to earn bonus XP. Submitted cards are locked permanently and can no longer be traded — but spare duplicates stay free to trade.
      </div>

      {loading && <div style={{ color:T.textMuted, marginBottom:12 }}>Loading…</div>}
      {error && !loading && (
        <div style={{ fontSize:12, color:T.red, background:T.redDim, borderRadius:T.radiusSm, padding:"10px 14px", marginBottom:12, display:"flex", justifyContent:"space-between", alignItems:"center", gap:10 }}>
          <span>Couldn't load your Forever sets — try again.</span>
          <button onClick={onRetry} style={{ background:"none", border:`1px solid ${T.red}`, borderRadius:T.radiusSm, padding:"6px 12px", color:T.red, fontSize:12, fontWeight:700, cursor:"pointer" }}>Retry</button>
        </div>
      )}

      {definitions.map(def => {
        const p = progress[def.id] || { have:0, total:1, pct:0, completable:false };
        const isReady = Boolean(p.completable);
        const timesSubmitted = submissions.filter(s => s.setId === def.id).length;
        const pctDisplay = (p.pct * 100).toFixed(1);
        return (
          <div key={def.id} style={{ background:T.surface, border:`1px solid ${isReady?T.borderGreen:T.border}`, borderRadius:T.radius, padding:"16px", marginBottom:10 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
              <div style={{ fontSize:14, fontWeight:700, color:T.text }}>{def.label}</div>
              <div style={{ fontSize:13, fontWeight:800, color:T.gold, flexShrink:0, marginLeft:10 }}>+{def.xp} XP</div>
            </div>
            <div style={{ fontSize:12, color:T.textMuted, lineHeight:1.5, marginBottom:10 }}>{def.description}</div>
            <div style={{ marginBottom:10 }}>
              <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:T.textMuted, marginBottom:4 }}>
                <span>{p.have} of {p.total} cards</span>
                <span style={{ fontWeight:700, color:isReady?T.accent:T.textMuted }}>{pctDisplay}% complete</span>
              </div>
              <div style={{ background:T.surfaceHigh, borderRadius:4, height:6, overflow:"hidden" }}>
                <div style={{ height:"100%", background:isReady?T.accent:T.gold, width:`${Math.min(p.pct*100,100)}%`, borderRadius:4, transition:"width 0.4s" }}/>
              </div>
            </div>
            {timesSubmitted > 0 && (
              <div style={{ fontSize:11, color:T.textDim, marginBottom:10 }}>Submitted {timesSubmitted} time{timesSubmitted!==1?"s":""} so far</div>
            )}
            {isReady ? (
              <button onClick={()=>openChooser(def)} style={{ width:"100%", background:T.accent, border:"none", borderRadius:T.radiusSm, padding:"11px 0", color:"#000", fontSize:13, fontWeight:800, cursor:"pointer" }}>
                Submit to Forever Collection
              </button>
            ) : (
              <div style={{ textAlign:"center", padding:"9px 0", color:T.textDim, fontSize:12, background:T.surfaceHigh, borderRadius:T.radiusSm }}>
                Not yet complete
              </div>
            )}
          </div>
        );
      })}

      {submissions.length > 0 && (
        <div style={{ marginTop:18 }}>
          <div style={{ fontSize:11, fontWeight:700, color:T.textDim, letterSpacing:1.5, marginBottom:10 }}>YOUR SUBMISSIONS — {submissions.length}</div>
          {submissions.map(s => (
            <div key={s.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:12, color:T.text, padding:"8px 0", borderBottom:`1px solid ${T.border}` }}>
              <span>{s.label}</span>
              <span style={{ display:"flex", alignItems:"center", gap:10 }}>
                <span style={{ color:T.gold, fontWeight:700 }}>+{s.xp} XP</span>
                <span style={{ color:s.status==="released"?T.accent:T.textMuted, fontWeight:700 }}>{s.status==="released" ? "Paid" : "Pending"}</span>
              </span>
            </div>
          ))}
        </div>
      )}

      {selecting && selectingDef && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.8)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:300, padding:20 }} onClick={closeChooser}>
          <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.radiusLg, padding:24, maxWidth:400, width:"100%", maxHeight:"80vh", overflowY:"auto" }} onClick={e=>e.stopPropagation()}>
            <div style={{ fontWeight:800, fontSize:16, marginBottom:12 }}>{selectingDef.label}</div>

            {selecting.kind === "common" && (
              <div style={{ fontSize:13, color:T.textMuted, marginBottom:12 }}>All 306 common cards.</div>
            )}

            {selecting.kind === "teams" && (
              <div style={{ marginBottom:12 }}>
                <div style={{ fontSize:12, color:T.textMuted, marginBottom:8 }}>Choose {selectingDef.teams_required} complete team{selectingDef.teams_required === 1 ? "" : "s"}</div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                  {(progress[selectingDef.id]?.completeTeams || []).map(teamId => {
                    const picked = selecting.teams.includes(teamId);
                    return (
                      <button key={teamId} aria-pressed={picked} disabled={selecting.uncertain || busy} onClick={()=>toggleTeam(selectingDef, teamId)} style={{ background:picked?T.accent:"transparent", border:`1px solid ${picked?T.accent:T.border}`, borderRadius:20, padding:"6px 12px", color:picked?"#000":T.text, fontSize:12, fontWeight:600, cursor:(selecting.uncertain || busy)?"default":"pointer", opacity:(selecting.uncertain || busy)?0.6:1 }}>
                        {teamNames[teamId] || teamId}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {selecting.kind === "fullnum" && (
              <div style={{ marginBottom:12 }}>
                <div style={{ fontSize:12, color:T.textMuted, marginBottom:8 }}>Choose a card number</div>
                <select aria-label="Card number" value={selecting.legacyNum} disabled={selecting.uncertain || busy} onChange={e=>setFullNumber(Number(e.target.value))} style={{ width:"100%", padding:10, border:`1px solid ${T.border}`, borderRadius:T.radiusSm }}>
                  {(progress[selectingDef.id]?.completableNumbers || []).map(n => (
                    <option key={n} value={n}>#{n}</option>
                  ))}
                </select>
              </div>
            )}

            <div style={{ fontSize:12, color:T.red, background:T.redDim, borderRadius:T.radiusSm, padding:"10px 14px", marginBottom:12 }}>
              These cards will be locked for good and can never be traded. The XP is paid at the next weekly confirmation.
            </div>

            {submitError && <div style={{ fontSize:12, color:T.red, marginBottom:12 }}>{submitError}</div>}

            <div style={{ display:"flex", gap:8 }}>
              <button onClick={closeChooser} disabled={busy} style={{ flex:1, background:T.surfaceHigh, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"11px 0", color:T.text, fontSize:13, cursor:"pointer" }}>Cancel</button>
              <button disabled={!ready || busy} onClick={confirmSubmit} style={{ flex:1, background:ready?T.accent:T.surfaceHigh, border:"none", borderRadius:T.radiusSm, padding:"11px 0", color:ready?"#000":T.textDim, fontSize:13, fontWeight:700, cursor:ready?"pointer":"default" }}>Confirm &amp; Lock</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function TradingBoardScreen({ board, loading, error, notice, myUserId, ownedCards = [], cardCopies = {}, lockedCopies = {}, onPostListing, onCancelListing, onPostCounter, onWithdrawCounter, onAcceptCounter, onDeclineCounter, onRefresh }) {
  const [tradeView, setTradeView] = useState("browse");
  const [items, setItems] = useState([]);
  const [counterTarget, setCounterTarget] = useState(null);
  const [search, setSearch] = useState("");
  const [rarity, setRarity] = useState("all");
  const [team, setTeam] = useState("all");
  const [busy, setBusy] = useState(false);
  const chart = Object.fromEntries(Object.entries(RARITY).map(([key,value])=>[key,board?.valueChart?.[key] ?? value.pts]));
  const available = id => Math.max(0,(cardCopies[id] || 0)-(lockedCopies?.[id] || 0));
  const availableIds = [...new Set(ownedCards)].filter(id=>available(id)>=1);
  const selectedIds = compositeIdsFromItems(items);
  const copyCount = selectedIds.length;
  const quantity = id => selectedIds.filter(value=>value===id).length;
  const target = board?.listings?.find(listing=>listing.id===counterTarget);
  const mine = row => row.isMine ?? (Boolean(myUserId) && row.userId===myUserId);
  const summary = entries => (entries || []).map(item=>`${item.name || "Card"} × ${item.copies}`).join(", ");
  function changeQuantity(id, delta) {
    const next = quantity(id)+delta;
    if(next<1 || next>available(id)) return;
    const ids = selectedIds.filter(value=>value!==id).concat(Array(next).fill(id));
    const proposed = itemsFromCompositeIds(ids);
    if(tradeView==="counter" && offerValue(proposed,chart)>target?.valuePts) return;
    setItems(proposed);
  }
  function toggleCard(id) {
    const proposed = itemsFromCompositeIds(selectedIds.includes(id)?selectedIds.filter(value=>value!==id):[...selectedIds,id]);
    if(tradeView==="counter" && offerValue(proposed,chart)>target?.valuePts) return;
    setItems(proposed);
  }
  async function submit(action) {
    if(busy) return;
    setBusy(true);
    try {
      const result = await action();
      if(result?.ok) { setItems([]); setCounterTarget(null); setTradeView("browse"); }
    } finally { setBusy(false); }
  }
  return <div>
                {/* Header + toggle */}
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
                  <div style={{ fontWeight:800, fontSize:16 }}>Trading Board</div>
                  <div style={{ display:"flex", gap:6 }}>
                    <button onClick={()=>{setTradeView("browse");setCounterTarget(null);}} style={{ background:tradeView==="browse"?T.accent:"transparent", border:`1px solid ${tradeView==="browse"?T.accent:T.border}`, borderRadius:20, padding:"5px 12px", color:tradeView==="browse"?"#000":T.textMuted, fontSize:12, fontWeight:600, cursor:"pointer" }}>Browse</button>
                    <button onClick={()=>{setItems([]);setTradeView("post");}} style={{ background:tradeView==="post"?T.accent:"transparent", border:`1px solid ${tradeView==="post"?T.accent:T.border}`, borderRadius:20, padding:"5px 12px", color:tradeView==="post"?"#000":T.textMuted, fontSize:12, fontWeight:600, cursor:"pointer" }}>+ List</button>
                  </div>
                </div>

                {error && <div style={{ fontSize:12, color:T.red }}>{tradeErrorCopy(error)}</div>}
                {notice && <div style={{ fontSize:12, color:T.accent }}>{notice}</div>}
                {loading && <div style={{ color:T.textMuted }}>Loading…</div>}
                <button onClick={onRefresh} style={{ color:T.textMuted, background:"transparent", border:0, marginBottom:10 }}>Refresh</button>
                {/* POST LISTING */}
                {tradeView==="post" && (
                  <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.radius, padding:16 }}>
                    <div style={{ fontSize:13, fontWeight:700, marginBottom:4 }}>Select cards to offer</div>
                    <div style={{ fontSize:11, color:T.textMuted, marginBottom:12 }}>Others will counter with cards of equal value. Value = {offerValue(items, chart)} pts</div>
                    {/* Rarity key */}
                    <div style={{ display:"flex", gap:6, marginBottom:12, flexWrap:"wrap" }}>
                      {Object.entries(RARITY).map(([r,rv])=>(
                        <span key={r} style={{ fontSize:10, color:rv.color }}>{rv.emoji} {rv.label}={chart[r]}pts</span>
                      ))}
                    </div>
                    {/* Card picker */}
                    <div style={{ maxHeight:220, overflowY:"auto", marginBottom:12 }}>
                      {availableIds.map(id=>{
                        const c=ALL_CARDS.find(x=>x.id===id);
                        if(!c) return null;
                        const selected = selectedIds.includes(id);
                        const r = RARITY[c.rarity];
                        return (
                          <div key={id} onClick={()=>toggleCard(id)}
                            style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 12px", marginBottom:4, borderRadius:T.radiusSm, background:selected?"rgba(57,255,20,0.1)":T.surfaceHigh, border:`1px solid ${selected?T.accent:T.border}`, cursor:"pointer" }}>
                            <div style={{ width:18, height:18, borderRadius:4, border:`2px solid ${selected?T.accent:T.border}`, background:selected?T.accent:"transparent", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
                              {selected && <span style={{ fontSize:12, color:"#000", fontWeight:900 }}>✓</span>}
                            </div>
                            <div style={{ flex:1 }}>
                              <span style={{ fontSize:13, fontWeight:600 }}>{c.player}</span>
                              <span style={{ fontSize:11, color:T.textMuted }}> · <TeamName team={c.team} size={11}>{c.team.split(" ").slice(-1)[0]}</TeamName></span>
                            </div>
                            <span style={{ fontSize:11, color:r.color, fontWeight:700 }}>{r.emoji} {chart[c.rarity]}pts</span>
                            {selected && available(id)>1 && <span onClick={event=>event.stopPropagation()} style={{ display:"flex", alignItems:"center", gap:6 }}>
                              <button aria-label={`Fewer ${c.player}`} disabled={quantity(id)<=1} onClick={()=>changeQuantity(id,-1)}>−</button>
                              <span>{quantity(id)}</span>
                              <button aria-label={`More ${c.player}`} disabled={quantity(id)>=available(id) || (tradeView==="counter" && offerValue(items,chart)+chart[c.rarity]>target?.valuePts)} onClick={()=>changeQuantity(id,1)}>+</button>
                            </span>}
                          </div>
                        );
                      })}
                      {ownedCards.length===0 && <div style={{ fontSize:12, color:T.textDim, padding:"12px 0" }}>You have no cards to offer yet.</div>}
                    </div>
                    {copyCount>0 && (
                      <div style={{ background:"rgba(57,255,20,0.08)", border:`1px solid ${T.borderGreen}`, borderRadius:T.radiusSm, padding:"8px 12px", marginBottom:12, fontSize:12 }}>
                        Offering {copyCount} card{copyCount!==1?"s":""} · <span style={{ color:T.accent, fontWeight:700 }}>{offerValue(items, chart)} pts total</span>
                      </div>
                    )}
                    <div style={{ display:"flex", gap:8 }}>
                      <button onClick={()=>{setTradeView("browse");setItems([]);}} style={{ flex:1, background:"transparent", border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"11px 0", color:T.textMuted, fontSize:13, cursor:"pointer" }}>Cancel</button>
                      <button onClick={()=>submit(()=>onPostListing(items))} disabled={busy || copyCount===0} style={{ flex:2, background:copyCount>0?T.accent:T.surfaceHigh, border:"none", borderRadius:T.radiusSm, padding:"11px 0", color:copyCount>0?"#000":T.textDim, fontSize:13, fontWeight:700, cursor:copyCount>0?"pointer":"default" }}>Post Listing</button>
                    </div>
                  </div>
                )}

                {/* COUNTER OFFER */}
                {tradeView==="counter" && counterTarget && (() => {
                  const trade = (board?.listings||[]).find(t=>t.id===counterTarget);
                  if(!trade) return null;
                  const targetVal = trade.valuePts;
                  const myVal = offerValue(items, chart);
                  const matched = myVal === targetVal;
                  return (
                    <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.radius, padding:16 }}>
                      <div style={{ fontSize:13, fontWeight:700, marginBottom:4 }}>Make a Counter-Offer</div>
                      <div style={{ fontSize:11, color:T.textMuted, marginBottom:4 }}>
                        {trade.displayName} is offering:&nbsp;
                        {summary(trade.items)}
                      </div>
                      <div style={{ fontSize:11, color:T.accent, marginBottom:12 }}>Required value: {targetVal} pts · Your selection: <span style={{ color:matched?T.accent:T.red }}>{myVal} pts</span></div>
                      <div style={{ maxHeight:220, overflowY:"auto", marginBottom:12 }}>
                        {availableIds.map(id=>{
                          const c=ALL_CARDS.find(x=>x.id===id);
                          if(!c) return null;
                          const selected = selectedIds.includes(id);
                          const r = RARITY[c.rarity];
                          const wouldExceed = !selected && (myVal + chart[c.rarity]) > targetVal;
                          return (
                            <div key={id} onClick={()=>!wouldExceed&&toggleCard(id)}
                              style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 12px", marginBottom:4, borderRadius:T.radiusSm, background:selected?"rgba(57,255,20,0.1)":T.surfaceHigh, border:`1px solid ${selected?T.accent:T.border}`, cursor:wouldExceed?"not-allowed":"pointer", opacity:wouldExceed?0.4:1 }}>
                              <div style={{ width:18, height:18, borderRadius:4, border:`2px solid ${selected?T.accent:T.border}`, background:selected?T.accent:"transparent", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
                                {selected && <span style={{ fontSize:12, color:"#000", fontWeight:900 }}>✓</span>}
                              </div>
                              <div style={{ flex:1 }}>
                                <span style={{ fontSize:13, fontWeight:600 }}>{c.player}</span>
                                <span style={{ fontSize:11, color:T.textMuted }}> · <TeamName team={c.team} size={11}>{c.team.split(" ").slice(-1)[0]}</TeamName></span>
                              </div>
                              <span style={{ fontSize:11, color:r.color, fontWeight:700 }}>{r.emoji} {chart[c.rarity]}pts</span>
                            {selected && available(id)>1 && <span onClick={event=>event.stopPropagation()} style={{ display:"flex", alignItems:"center", gap:6 }}>
                              <button aria-label={`Fewer ${c.player}`} disabled={quantity(id)<=1} onClick={()=>changeQuantity(id,-1)}>−</button>
                              <span>{quantity(id)}</span>
                              <button aria-label={`More ${c.player}`} disabled={quantity(id)>=available(id) || (tradeView==="counter" && offerValue(items,chart)+chart[c.rarity]>target?.valuePts)} onClick={()=>changeQuantity(id,1)}>+</button>
                            </span>}
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ display:"flex", gap:8 }}>
                        <button onClick={()=>{setTradeView("browse");setItems([]);setCounterTarget(null);}} style={{ flex:1, background:"transparent", border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:"11px 0", color:T.textMuted, fontSize:13, cursor:"pointer" }}>Cancel</button>
                        <button onClick={()=>submit(()=>onPostCounter(counterTarget, items))} disabled={busy || !matched || copyCount===0} style={{ flex:2, background:matched?T.accent:T.surfaceHigh, border:"none", borderRadius:T.radiusSm, padding:"11px 0", color:matched?"#000":T.textDim, fontSize:13, fontWeight:700, cursor:matched?"pointer":"default" }}>Send Counter-Offer</button>
                      </div>
                    </div>
                  );
                })()}

                {tradeView==="browse" && <div style={{ marginBottom:12 }}>
                  <input placeholder="Search listed cards" value={search} onChange={e=>setSearch(e.target.value)} style={{ width:"100%", padding:10, border:`1px solid ${T.border}`, borderRadius:T.radiusSm }} />
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap", margin:"8px 0" }}>
                    {[['all','All'],...Object.entries(RARITY).map(([key,value])=>[key,value.label])].map(([key,label])=><button key={key} onClick={()=>setRarity(key)} aria-pressed={rarity===key} style={{ background:rarity===key?T.accent:T.surface, border:`1px solid ${T.border}`, borderRadius:20, padding:"5px 10px" }}>{label}</button>)}
                  </div>
                  <select aria-label="Listed card team" value={team} onChange={e=>setTeam(e.target.value)}><option value="all">All teams</option>{TEAM_NAMES.map(name=><option key={name} value={name}>{name}</option>)}</select>
                </div>}
                {/* BROWSE LISTINGS */}
                {tradeView==="browse" && (() => {
                  const listings = (board?.listings||[]).filter(t=>t.status==="open");
                  const openTrades = listings.filter(t=>(t.items||[]).some(item =>
                    (!search || `${item.name || ""} ${item.team || ""}`.toLowerCase().includes(search.toLowerCase())) &&
                    (rarity === "all" || item.rarity === rarity) && (team === "all" || item.team === team)));
                  if(openTrades.length===0) return (
                    <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.radius, padding:"24px 16px", textAlign:"center" }}>
                      <div style={{ fontSize:13, color:T.textDim }}>{listings.length ? "No listings match your filters." : "No active listings yet."}</div>
                      {listings.length===0 && <div style={{ fontSize:12, color:T.textDim, marginTop:4 }}>Be the first to post one!</div>}
                    </div>
                  );
                  return openTrades.map(trade => {
                    const isOwn = mine(trade);
                    const myCounters = (trade.counters||[]).filter(c=>mine(c));
                    const hasCountered = myCounters.some(c=>c.status==="pending");
                    const pendingCounters = (trade.counters||[]).filter(c=>c.status==="pending");
                    return (
                      <div key={trade.id} style={{ background:T.surface, border:`1px solid ${isOwn?T.borderGreen:T.border}`, borderRadius:T.radius, padding:16, marginBottom:10 }}>
                        <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:10 }}>
                          <div>
                            <div style={{ fontSize:12, color:T.textMuted, marginBottom:4 }}>{isOwn?"Your listing":"From "+trade.displayName}</div>
                            <div style={{ fontSize:13, fontWeight:700 }}>Offering:</div>
                            {(trade.items||[]).map(c=>{ const id=`${c.rarity}-${c.legacyNum}`; const r=c?RARITY[c.rarity]:null; return c?(
                              <div key={id} style={{ fontSize:12, color:T.text, marginTop:3, display:"flex", alignItems:"center", gap:6 }}>
                                <span style={{ color:r.color }}>{r.emoji}</span> {c.name} × {c.copies} <span style={{ color:T.textMuted }}>· <TeamName team={c.team} size={11}>{c.team.split(" ").slice(-1)[0]}</TeamName></span>
                                <span style={{ color:r.color, fontWeight:700 }}>{chart[c.rarity]}pts</span>
                              </div>
                            ):null; })}
                            <div style={{ fontSize:11, color:T.accent, marginTop:6, fontWeight:700 }}>Total value: {trade.valuePts} pts</div>
                          </div>
                          {isOwn && <button onClick={()=>onCancelListing(trade.id)} style={{ background:"transparent", border:`1px solid ${T.border}`, borderRadius:8, padding:"4px 8px", color:T.red, fontSize:11, cursor:"pointer" }}>Remove</button>}
                        </div>

                        {/* Counter offers visible to listing owner */}
                        {isOwn && pendingCounters.length>0 && (
                          <div style={{ borderTop:`1px solid ${T.border}`, paddingTop:10, marginTop:6 }}>
                            <div style={{ fontSize:11, fontWeight:700, color:T.textMuted, marginBottom:8 }}>COUNTER-OFFERS ({pendingCounters.length})</div>
                            {(trade.counters||[]).map((co,i)=>{
                              if(co.status!=="pending") return null;
                              return (
                                <div key={i} style={{ background:T.surfaceHigh, borderRadius:T.radiusSm, padding:"10px 12px", marginBottom:6 }}>
                                  <div style={{ fontSize:12, color:T.textMuted, marginBottom:4 }}>{co.displayName} offers:</div>
                                  {(co.items||[]).map(c=>{ const id=`${c.rarity}-${c.legacyNum}`; const r=c?RARITY[c.rarity]:null; return c?(
                                    <div key={id} style={{ fontSize:12, display:"flex", alignItems:"center", gap:6, marginBottom:2 }}>
                                      <span style={{ color:r.color }}>{r.emoji}</span> {c.name} × {c.copies} <span style={{ color:T.textMuted }}>· <TeamName team={c.team} size={11}>{c.team.split(" ").slice(-1)[0]}</TeamName></span>
                                    </div>
                                  ):null; })}
                                  <div style={{ display:"flex", gap:6, marginTop:8 }}>
                                    <button onClick={()=>onAcceptCounter(co.id)} style={{ flex:1, background:T.accent, border:"none", borderRadius:6, padding:"7px 0", color:"#000", fontSize:12, fontWeight:700, cursor:"pointer" }}>Accept</button>
                                    <button onClick={()=>onDeclineCounter(co.id)} style={{ flex:1, background:"transparent", border:`1px solid ${T.border}`, borderRadius:6, padding:"7px 0", color:T.textMuted, fontSize:12, cursor:"pointer" }}>Decline</button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Counter button for others */}
                        {!isOwn && (
                          <div style={{ borderTop:`1px solid ${T.border}`, paddingTop:10, marginTop:6 }}>
                            {hasCountered
                              ? <div style={{ fontSize:12, color:T.textMuted, textAlign:"center" }}>✓ Counter-offer sent — awaiting response <button onClick={()=>onWithdrawCounter(myCounters.find(c=>c.status==="pending").id)} style={{ background:"transparent", border:0, color:T.accent, fontSize:11, cursor:"pointer" }}>Withdraw</button></div>
                              : <button onClick={()=>{setCounterTarget(trade.id);setItems([]);setTradeView("counter");}} style={{ width:"100%", background:"transparent", border:`1px solid ${T.accent}`, borderRadius:T.radiusSm, padding:"9px 0", color:T.accent, fontSize:13, fontWeight:700, cursor:"pointer" }}>Make Counter-Offer</button>
                            }
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
    {tradeView==="browse" && <section style={{ marginTop:16 }}>
      <div style={{ fontWeight:800, fontSize:16, marginBottom:10 }}>Recent trades</div>
      {(board?.myRecent || []).map(trade=>{
        const accepted = trade.counters?.find(counter=>counter.id===trade.acceptedCounterId);
        const involved = mine(trade) || (accepted && mine(accepted));
        return <div key={trade.id} style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.radiusSm, padding:12, marginBottom:8, fontSize:12 }}>
          {trade.status==="completed" ? involved ? <><div>Trade complete — you received {summary(mine(trade)?accepted?.items:trade.items)}</div><div>You gave {summary(mine(trade)?trade.items:accepted?.items)}</div></> : <div>Trade complete — your counter-offer was declined.</div> : <><div>Listing cancelled</div><div style={{ color:T.textMuted }}>{trade.cancelReason==="offer_unavailable"?tradeErrorCopy("offer_unavailable"):"The listing was cancelled."}</div></>}
        </div>;
      })}
    </section>}
  </div>;
}

function useTradeBoard(user, tab, refreshTick, epochTick) {
  const [state, setState] = useState({ board:null, loading:false, error:null });
  const currentUser = useRef(user?.id);
  currentUser.current = user?.id;
  useEffect(()=>{ setState({ board:null, loading:false, error:null }); },[user?.id]);
  useEffect(()=>{
    if (!user?.id || (tab !== "cards" && !epochTick)) return;
    const readGeneration = getClockGeneration();
    let active = true;
    const actor = user.id;
    setState(previous=>({ ...previous, loading:true, error:null }));
    fetchTradeBoard().then(result=>{
      if (!active || readGeneration !== getClockGeneration() || currentUser.current !== actor) return;
      setState(previous=>({ userId:actor, board:result.ok?result:previous.board, loading:false, error:result.ok?null:result.code }));
    });
    return ()=>{ active=false; };
  },[user?.id, tab, refreshTick, epochTick]);
  return state.userId && state.userId !== user?.id ? { board:null, loading:false, error:null } : state;
}
